import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Edit2, Eye, FileText, Image, Settings, Zap, Globe, CheckCircle, MessageCircle, Mail, Clock, Shield, Award, Code, Headphones, ShoppingCart, HelpCircle, Plus, X } from 'lucide-react';
import MediaSelector from '../MediaSelector';
import * as Icons from 'lucide-react';
import { FRONTEND_PAGES, PAGE_SLUG_MAP } from './pageConstants';

const resolveIcon = (name, fallback = 'FileText') => {
  if (!name && fallback && Icons[fallback]) return Icons[fallback];
  if (name && Icons[name]) return Icons[name];
  if (fallback && Icons[fallback]) return Icons[fallback];
  return FileText;
};

function AdminPages({ standalone = true }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Tümü');
  const [editingPage, setEditingPage] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [pagesFromDB, setPagesFromDB] = useState([]);
  const [loadingPages, setLoadingPages] = useState(true);
  
  // SEO Editing State
  const [editingSeoPage, setEditingSeoPage] = useState(null);
  const [seoData, setSeoData] = useState(null);
  const [loadingSeo, setLoadingSeo] = useState(false);
  const [savingSeo, setSavingSeo] = useState(false);
  
  // Media Library Modal
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [selectedImageField, setSelectedImageField] = useState(null);

  const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';

  // Veritabanından sayfaları çek
  useEffect(() => {
    const fetchPages = async () => {
      try {
        const { adminApiFetch } = await import('../../../utils/adminApi');
        const res = await adminApiFetch(`${apiBase}/backend/admin_api.php?action=getAllPages`);
        const data = await res.json();
        
        let allPages = [];
        let dbPages = [];
        
        if (data.status === 'success') {
          // Veritabanındaki sayfaları formatla (artık backend path bilgisini de döndürüyor)
          dbPages = data.data.map(dbPage => ({
            path: dbPage.path || (dbPage.page_slug === 'anasayfa' ? '/' : '/' + dbPage.page_slug),
            name: dbPage.page_name || dbPage.page_slug,
            category: dbPage.page_category || 'Genel',
            component: dbPage.component || dbPage.component_name || null,
            slug: dbPage.page_slug,
            isActive: dbPage.is_active === 1,
            isDynamic: false
          }));
          
          // Veritabanı sayfalarını ekle
          allPages = [...dbPages];
        }
        
        // Dinamik sayfaları çek (sadece listing sayfaları, tekil içerikler değil)
        // Tekil içerikler: blog_post, education_post, project, service, project_category, project_subcategory
        // Listing sayfaları: blog_listing, education_listing, projects_listing, services_listing, faq_listing
        let dynamicPagePaths = new Set();
        const allowedDynamicTypes = [
          'blog_listing',
          'education_listing',
          'projects_listing',
          'projects_catalog',
          'services_listing',
          'faq_listing',
          'ecommerce_designs_listing',
          'website_templates_listing'
        ];
        
        try {
          const dynamicRes = await adminApiFetch(`${apiBase}/backend/admin_api.php?action=getDynamicPagesForSeo`);
          const dynamicData = await dynamicRes.json();
          
          if (dynamicData.status === 'success' && dynamicData.data) {
            // Sadece listing sayfalarını filtrele (tekil içerikleri hariç tut)
            const listingPages = dynamicData.data.filter(page => 
              allowedDynamicTypes.includes(page.type)
            );
            
            const dynamicPages = listingPages.map(page => ({
              path: page.path,
              name: page.name,
              category: page.category || 'Dinamik',
              component: null,
              slug: page.slug || page.path.replace(/^\//, '').replace(/\//g, '-'),
              isActive: true,
              isDynamic: true,
              type: page.type,
              id: page.id
            }));
            
            // Dinamik sayfa path'lerini set'e ekle
            dynamicPages.forEach(page => {
              dynamicPagePaths.add(page.path);
            });
            
            // Dinamik sayfaları eklerken, aynı path'e sahip sayfaları güncelle veya ekle
            dynamicPages.forEach(dynamicPage => {
              const existingIndex = allPages.findIndex(p => p.path === dynamicPage.path);
              if (existingIndex >= 0) {
                // Aynı path'e sahip sayfa varsa, dinamik sayfa ile güncelle
                // ÖNEMLİ: isDynamic: true olarak ayarla
                allPages[existingIndex] = {
                  ...allPages[existingIndex],
                  ...dynamicPage,
                  isDynamic: true, // Dinamik sayfa olarak işaretle
                  // Component bilgisini koru (varsa)
                  component: allPages[existingIndex].component || dynamicPage.component
                };
              } else {
                // Yeni dinamik sayfa ekle
                allPages.push(dynamicPage);
              }
            });
          }
        } catch (dynamicError) {
          // Dinamik sayfalar yüklenirken hata oluştu
        }
        
        // FRONTEND_PAGES'deki ama veritabanında olmayan VE dinamik olmayan sayfaları ekle (fallback)
        // Not: Artık tüm sayfalar veritabanında olmalı, bu sadece güvenlik için
        if (allPages.length === 0) {
          FRONTEND_PAGES.forEach(fp => {
            const slug = PAGE_SLUG_MAP[fp.path] || fp.path.replace('/', '').replace(/\//g, '-');
            if (!dynamicPagePaths.has(fp.path)) {
              allPages.push({
                ...fp,
                slug: slug,
                isActive: true,
                isDynamic: false
              });
            }
          });
        }
        
        setPagesFromDB(allPages);
      } catch (error) {
        // Hata durumunda FRONTEND_PAGES'i fallback olarak kullan
        setPagesFromDB(FRONTEND_PAGES.map(fp => ({
          ...fp,
          slug: PAGE_SLUG_MAP[fp.path] || fp.path.replace('/', '').replace(/\//g, '-'),
          isActive: true,
          isDynamic: false
        })));
      } finally {
        setLoadingPages(false);
      }
    };
    
    fetchPages();
  }, [apiBase]);

  // Kategorileri al
  const categories = useMemo(() => {
    const cats = ['Tümü', ...new Set(pagesFromDB.map(p => p.category))];
    return cats;
  }, [pagesFromDB]);

  // Filtrelenmiş sayfalar
  const filteredPages = useMemo(() => {
    return pagesFromDB.filter(page => {
      const matchesSearch = page.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                           page.path.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           (page.slug && page.slug.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = categoryFilter === 'Tümü' || page.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, categoryFilter, pagesFromDB]);

  const handleEdit = async (page) => {
    // Dinamik sayfa path'leri listesi (backend'den gelen dinamik sayfalar)
    const dynamicPaths = [
      '/blog', '/egitim', '/sss', '/sikca-sorulan-sorular',
      '/projeler', '/projeler-katalog',
      '/web-sitesi-tasarimlari', '/hazir-web-sitesi-tasarimlari',
      '/hizmetler'
    ];
    
    // Dinamik sayfa ise SEO düzenleme modalını aç
    // isDynamic kontrolü veya path kontrolü yap
    if (page.isDynamic || dynamicPaths.includes(page.path) || page.type) {
      setEditingSeoPage(page);
      setLoadingSeo(true);
      setSeoData(null);
      
      try {
        const { adminApiFetch } = await import('../../../utils/adminApi');
        const res = await adminApiFetch(`${apiBase}/backend/admin_api.php?action=getPageSeo&path=${encodeURIComponent(page.path)}`);
        const data = await res.json();
        
        if (data.status === 'success' && data.data) {
          setSeoData(data.data);
        } else {
          // SEO verisi yoksa boş yapı oluştur
          setSeoData({
            path: page.path,
            title: null,
            description: null,
            keywords: null,
            og_image: null,
            noindex: 0
          });
        }
      } catch (error) {
        setSeoData({
          path: page.path,
          title: null,
          description: null,
          keywords: null,
          og_image: null,
          noindex: 0
        });
      }
      
      setLoadingSeo(false);
      return;
    }
    
    // Normal sayfa içeriği düzenleme
    setEditingPage(page);
    setLoadingContent(true);
    setPageContent(null);
    
    // Slug'ı sayfa objesinden al (artık veritabanından geliyor)
    const slug = page.slug || page.path.replace(/^\//, '').replace(/\//g, '-') || PAGE_SLUG_MAP[page.path];
    
    // İçerik çekilebilir sayfalar için API'den içerik al
    if (slug) {
      try {
        const { adminApiFetch } = await import('../../../utils/adminApi');
        const res = await adminApiFetch(`${apiBase}/backend/admin_api.php?action=getPageContent&slug=${slug}`);
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          setLoadingContent(false);
          return;
        }
        
        if (data.status === 'success' && data.data) {
          // additional_sections ve success_stories backend'de JSON olarak gelebilir; string ise parse et
          let content = data.data;
          if (content.additional_sections && typeof content.additional_sections === 'string') {
            try {
              content.additional_sections = JSON.parse(content.additional_sections);
            } catch (e) {
              content.additional_sections = {};
            }
          }
          if (content.success_stories && typeof content.success_stories === 'string') {
            try {
              const parsed = JSON.parse(content.success_stories);
              content.success_stories = Array.isArray(parsed) ? parsed : [];
            } catch (e) {
              content.success_stories = [];
            }
          }
          if (content.testimonials && typeof content.testimonials === 'string') {
            try {
              const parsed = JSON.parse(content.testimonials);
              content.testimonials = Array.isArray(parsed) ? parsed : [];
            } catch (e) {
              content.testimonials = [];
            }
          }
          // Anasayfa için varsayılanlar: success_stories yoksa boş dizi, additional_sections alt bölümleri en az boş obje
          if (slug === 'anasayfa') {
            if (!Array.isArray(content.success_stories)) content.success_stories = [];
            const as = content.additional_sections || {};
            content.additional_sections = {
              projects: as.projects ?? {},
              services: as.services ?? {},
              gogi_ai: as.gogi_ai ?? {},
              education: as.education ?? {},
              blog: as.blog ?? {},
              faq: as.faq ?? {},
              ...as
            };
          }
          setPageContent(content);
        }
      } catch (error) {
        // İçerik yükleme hatası
      }
    }
    
    setLoadingContent(false);
  };

  const handleSaveSeo = async () => {
    if (!seoData || !editingSeoPage) return;
    
    setSavingSeo(true);
    try {
      const { adminApiFetch } = await import('../../../utils/adminApi');
      const res = await adminApiFetch(`${apiBase}/backend/admin_api.php?action=updatePageSeo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(seoData)
      });
      const data = await res.json();
      
      if (data.status === 'success') {
        setSuccessMsg('SEO ayarları başarıyla güncellendi!');
        setTimeout(() => setSuccessMsg(''), 3000);
        setEditingSeoPage(null);
        setSeoData(null);
      } else {
        alert('Kayıt hatası: ' + data.message);
      }
    } catch (error) {
      alert('SEO ayarları kaydedilirken hata oluştu');
    }
    setSavingSeo(false);
  };


  const handlePreview = (page) => {
    window.open(page.path, '_blank');
  };

  const runMigration = async (target) => {
    setMigrationRunning(true);
    try {
      const url = `${apiBase}/backend/admin_api.php?action=runPageContentsMigration${target ? `&target=${encodeURIComponent(target)}` : ''}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      
      if (data.status === 'success') {
        setSuccessMsg('Migration başarıyla tamamlandı! Şimdi sayfa içeriklerini düzenleyebilirsiniz.');
        setTimeout(() => setSuccessMsg(''), 3000);
        
        // Eğer modal açıksa içeriği yeniden yükle
        if (editingPage && PAGE_SLUG_MAP[editingPage.path]) {
          handleEdit(editingPage);
        }
      } else {
        alert('Migration hatası: ' + data.message);
      }
    } catch (error) {
      alert('Migration çalıştırılırken hata oluştu');
    }
    setMigrationRunning(false);
  };

  const handleSavePageContent = async () => {
    if (!pageContent) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${apiBase}/backend/admin_api.php?action=updatePageContent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pageContent)
      });
      const data = await res.json();
      
      if (data.status === 'success') {
        setSuccessMsg('Sayfa içeriği başarıyla güncellendi!');
        setTimeout(() => setSuccessMsg(''), 3000);
        setEditingPage(null);
        setPageContent(null);
      } else {
        alert('Kayıt hatası: ' + data.message);
      }
    } catch (error) {
      alert('İçerik kaydedilirken hata oluştu');
    }
    setSaving(false);
  };

  const openMediaLibrary = async (fieldName) => {
    setSelectedImageField(fieldName);
    setShowMediaLibrary(true);
    setLoadingMedia(true);
    
    try {
      const res = await fetch(`${apiBase}/backend/admin_api.php?action=getMedia`);
      const data = await res.json();
      
      if (data.data) {
        setMediaFiles(data.data);
      }
    } catch (error) {
      // Medya yükleme hatası
    }
    
    setLoadingMedia(false);
  };

  const selectMedia = (mediaUrl) => {
    // SEO modal için özel durum
    if (selectedImageField === 'og_image_seo' && seoData) {
      setSeoData({ ...seoData, og_image: mediaUrl });
      setShowMediaLibrary(false);
      setSelectedImageField(null);
      return;
    }
    
    if (selectedImageField && pageContent) {
      // Hero görseli için özel durum: hem hero_image_url hem de additional_sections.hero.image_url'e set et
      if (selectedImageField === 'hero_image_url') {
        setPageContent((prev) => {
          if (!prev) return prev;
          const currentAdditional = prev.additional_sections || {};
          const currentHero = currentAdditional.hero || {};
          return {
            ...prev,
            hero_image_url: mediaUrl,
            additional_sections: {
              ...currentAdditional,
              hero: {
                ...currentHero,
                image_url: mediaUrl
              }
            }
          };
        });
      } else {
        const path =
          selectedImageField.includes('.')
            ? selectedImageField.split('.')
            : [selectedImageField];

        setPageContent((prev) => {
          if (!prev) return prev;

          if (path.length === 1) {
            return {
              ...prev,
              [path[0]]: mediaUrl
            };
          }

          const newContent = { ...prev };
          let current = newContent;

          for (let i = 0; i < path.length - 1; i++) {
            const key = path[i];
            const existing = current[key];

            if (Array.isArray(existing)) {
              current[key] = [...existing];
            } else {
              current[key] = { ...(existing || {}) };
            }

            current = current[key];
          }

          current[path[path.length - 1]] = mediaUrl;
          return newContent;
        });
      }
    }
    setShowMediaLibrary(false);
    setSelectedImageField(null);
  };

  const generateSitemap = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${apiBase}/backend/api.php?action=generateSitemap`, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify({}) 
      });
      const data = await res.json();
      if (data && data.success) {
        let msg = `Sitemap oluşturuldu: ${data.file}`;
        if (data.synced_pages > 0) {
          msg += `\n${data.synced_pages} sayfa veritabanına eklendi: ${data.synced_page_names.join(', ')}`;
        }
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(''), 5000);
      }
    } catch (error) {
      setErrorMsg('Sitemap oluşturulurken hata oluştu: ' + error.message);
      setTimeout(() => setErrorMsg(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const generateLLMSTxt = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${apiBase}/backend/api.php?action=generateLLMSTxt`, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify({}) 
      });
      const data = await res.json();
      if (data && data.success) {
        setSuccessMsg(`LLMS.txt oluşturuldu: ${data.file}`);
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        alert('Hata: ' + (data.message || 'LLMS.txt oluşturulamadı'));
      }
    } catch (error) {
      alert('LLMS.txt oluşturulurken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const containerClass = standalone ? 'w-full p-2 sm:p-4 lg:p-6 overflow-x-hidden max-w-full' : 'space-y-4 sm:space-y-6 overflow-x-hidden max-w-full';

  return (
    <div className={containerClass}>
      {standalone && (
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Sayfa Yönetimi</h1>
          <p className="text-sm sm:text-base text-gray-600">Tüm frontend sayfalarınızı görüntüleyin ve düzenleyin</p>
        </div>
      )}

      {/* Success Message */}
      {successMsg && (
        <div className="mb-3 sm:mb-4 p-3 sm:p-4 rounded-lg bg-green-50 text-green-700 border border-green-200 flex items-center gap-2 text-sm">
          <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div className="whitespace-pre-line text-xs sm:text-sm">{successMsg}</div>
        </div>
      )}

      {/* Error Message */}
      {errorMsg && (
        <div className="mb-3 sm:mb-4 p-3 sm:p-4 rounded-lg bg-red-50 text-red-700 border border-red-200 flex items-center gap-2 text-sm">
          <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span className="text-xs sm:text-sm">{errorMsg}</span>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 mb-4 sm:mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {/* Search */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Sayfa Ara</label>
            <Input 
              placeholder="Sayfa adı veya URL..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full text-sm"
            />
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Kategori</label>
            <select 
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>Toplam: <strong>{pagesFromDB.length}</strong> sayfa</span>
          </div>
          <div className="flex items-center gap-2">
            <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>Gösterilen: <strong>{filteredPages.length}</strong> sayfa</span>
          </div>
        </div>
      </div>

      {/* Pages Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mx-0">
        {/* Mobile View - Card Layout */}
        <div className="block sm:hidden">
          {loadingPages ? (
            <div className="p-4 sm:p-6 text-center text-gray-500">
              <div className="flex flex-col items-center gap-2">
                <FileText className="w-12 h-12 text-gray-300 animate-pulse" />
                <p className="text-sm">Sayfalar yükleniyor...</p>
              </div>
            </div>
          ) : filteredPages.length === 0 ? (
            <div className="p-4 sm:p-6 text-center text-gray-500">
              <div className="flex flex-col items-center gap-2">
                <FileText className="w-12 h-12 text-gray-300" />
                <p className="text-sm">Sayfa bulunamadı</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredPages.map((page) => (
                <div key={page.path} className="p-3 sm:p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="font-medium text-sm text-gray-900 truncate">{page.name}</span>
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0 ml-2">
                      {page.category}
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-gray-500">URL:</span>
                      <code className="block text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded mt-1 break-all">
                        {page.path}
                      </code>
                    </div>
                    {page.component && (
                      <div>
                        <span className="text-xs text-gray-500">Component:</span>
                        <span className="block text-xs text-gray-600 mt-1">{page.component}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePreview(page)}
                      className="flex-1 text-xs h-8"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      Önizle
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        handleEdit(page);
                      }}
                      className="flex-1 text-xs h-8 bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <Edit2 className="w-3 h-3 mr-1" />
                      Düzenle
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Desktop View - Table Layout */}
        <div className="hidden sm:block">
          <div className="overflow-x-auto">
            <table className="w-full min-w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sayfa Adı
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    URL
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kategori
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Component
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loadingPages ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <FileText className="w-12 h-12 text-gray-300 animate-pulse" />
                        <p>Sayfalar yükleniyor...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredPages.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <FileText className="w-12 h-12 text-gray-300" />
                        <p>Sayfa bulunamadı</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredPages.map((page) => (
                    <tr key={page.path} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 sm:px-6 py-4 w-[40%] align-top">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="font-medium text-sm text-gray-900 max-w-[140px] sm:max-w-[200px] whitespace-normal break-words">
                            {page.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 w-[30%] align-top">
                        <code
                          className="text-[11px] sm:text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded max-w-[120px] sm:max-w-[180px] inline-block whitespace-normal break-words"
                          title={page.path}
                        >
                          {page.path}
                        </code>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {page.category}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 hidden lg:table-cell">
                        {(() => {
                          const raw = page.component;
                          const fallback =
                            page.path && page.path !== '/'
                              ? page.path
                                  .replace(/^\//, '')
                                  .split(/[/?#]/)[0]
                                  .split('-')
                                  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                                  .join(' ')
                              : 'Genel Sayfa';
                          const label = raw && raw.trim().length > 0 ? raw : fallback;
                          return (
                            <span className="text-xs sm:text-sm text-gray-600 truncate max-w-xs block" title={label}>
                              {label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePreview(page)}
                            className="flex items-center gap-1"
                          >
                            <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span className="hidden md:inline">Önizle</span>
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              handleEdit(page);
                            }}
                            className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            <Edit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span className="hidden md:inline">Düzenle</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Sitemap & LLMS.txt Area */}
      <div className="mt-4 sm:mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        {/* Sitemap */}
        <div className="border rounded-lg p-3 sm:p-4 bg-white">
          <div className="flex flex-col gap-2 sm:gap-3">
            <div>
              <h2 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
                <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
                Sitemap
              </h2>
              <p className="text-xs text-gray-500 mt-1">Site haritasını oluşturun ve arama motorlarına gönderin.</p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="text-xs sm:text-sm text-purple-700 hover:underline break-all">/sitemap.xml</a>
              <Button variant="secondary" onClick={generateSitemap} disabled={saving} size="sm" className="text-xs h-8 w-full sm:w-auto">
                Sitemap Oluştur
              </Button>
            </div>
          </div>
        </div>

        {/* LLMS.txt */}
        <div className="border rounded-lg p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-blue-50">
          <div className="flex flex-col gap-2 sm:gap-3">
            <div>
              <h2 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2 flex-wrap">
                🤖 LLMS.txt
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">AI Friendly</span>
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Large Language Models için site içeriğinizi yapılandırılmış formatta oluşturun.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <a href="/llms.txt" target="_blank" rel="noreferrer" className="text-xs sm:text-sm text-purple-700 hover:underline break-all">/llms.txt</a>
              <Button variant="secondary" onClick={generateLLMSTxt} disabled={saving} size="sm" className="bg-purple-600 text-white hover:bg-purple-700 text-xs h-8 w-full sm:w-auto">
                LLMS.txt Oluştur
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between z-10 rounded-t-lg">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{editingPage.name} - İçerik Düzenleme</h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  <code className="bg-gray-100 px-2 py-1 rounded text-purple-600 text-xs break-all">{editingPage.path}</code>
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => { setEditingPage(null); setPageContent(null); }} className="flex-shrink-0">
                Kapat
              </Button>
            </div>
            <div className="p-4 sm:p-6 w-full overflow-x-hidden">
              {loadingContent ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : editingPage.path === '/vps-sunucu' && !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm overflow-x-hidden">
                  <p className="font-medium mb-2">⚠️ Veritabanında İçerik Yok</p>
                  <p className="mb-3 sm:mb-4 break-words">
                    VPS Sunucu sayfası içeriklerini düzenleyebilmek için migration çalıştırmanız gerekiyor. Bu işlem sayfa
                    verilerini veritabanına aktarır.
                  </p>
                  <Button
                    onClick={() => runMigration(PAGE_SLUG_MAP[editingPage.path] || null)}
                    disabled={migrationRunning}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                  </Button>
                </div>
              ) : editingPage.path === '/vps-sunucu' && pageContent ? (
                (() => {
                  const additional = pageContent.additional_sections || {};
                  const heroSection = additional.hero || {};
                  const introSection = additional.intro_section || {};
                  const featuresSection = additional.features_section || {};
                  const featureCards = Array.isArray(featuresSection.cards) ? featuresSection.cards : [];
                  const vpsStandardSection = additional.standard_section || {};
                  const vpsStandardCards = Array.isArray(vpsStandardSection.cards) ? vpsStandardSection.cards : [];
                  const vpsValueProductsSection = additional.value_products_section || {};
                  const vpsValueProductCards = Array.isArray(vpsValueProductsSection.cards) ? vpsValueProductsSection.cards : [];
                  const packagesSection = additional.packages_section || {};
                  const controlPanelSection = additional.control_panel_section || {};
                  const controlPanelCards = Array.isArray(controlPanelSection.cards) ? controlPanelSection.cards : [];
                  const operatingSystemsSection = additional.operating_systems_section || {};
                  const operatingSystemsCards = Array.isArray(operatingSystemsSection.cards) ? operatingSystemsSection.cards : [];
                  const windowsSection = additional.windows_section || {};
                  const windowsHighlights = Array.isArray(windowsSection.highlights) ? windowsSection.highlights : [];
                  const comparisonSection = additional.comparison_section || {};
                  const supportSection = additional.support_section || {};
                  const supportCards = Array.isArray(supportSection.cards) ? supportSection.cards : [];
                  const supportHighlight = supportSection.highlight || {};
                  return (
                    <div className="space-y-4 sm:space-y-6 overflow-x-hidden w-full">
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden w-full">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Image className="h-5 w-5" />
                          Hero Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                            <Input
                              value={pageContent.hero_badge_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                              placeholder="Örn: VPS Sunucu"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hero Başlığı</label>
                            <Input
                              value={pageContent.hero_title || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                              placeholder="Örn: Tam Kontrol ve Yüksek Performans"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Açıklaması</label>
                          <Textarea
                            rows={3}
                            value={pageContent.hero_description || ''}
                            onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                            placeholder="Kısa açıklama metni"
                          />
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hero Görseli</label>
                            <Input
                              value={heroSection.image_url || pageContent.hero_image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  hero_image_url: e.target.value,
                                  additional_sections: {
                                    ...additional,
                                    hero: {
                                      ...heroSection,
                                      image_url: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hero Vurgusu</label>
                            <Input
                              value={heroSection.highlight_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero: {
                                      ...heroSection,
                                      highlight_text: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Tam root erişimi"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">
                          Hero alanındaki değişiklikler kaydedildiğinde otomatik olarak veritabanına aktarılır.
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Paketler Bölümü</h3>
                            <p className="text-sm text-gray-500">
                              VPS paket listesinin üst kısmında görünen başlık ve açıklamayı buradan düzenleyebilirsiniz.
                            </p>
                          </div>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={packagesSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    packages_section: {
                                      ...packagesSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: VPS Sunucu Paketleri"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={packagesSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    packages_section: {
                                      ...packagesSection,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: İşinize uygun ölçeklenebilir VPS çözümleri"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Standart Özellikler</h3>
                            <p className="text-sm text-gray-500">Standart özellik kartlarını ve görselini buradan yönetin.</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...vpsStandardCards, { icon: 'Shield', title: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  standard_section: {
                                    ...vpsStandardSection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={vpsStandardSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    standard_section: {
                                      ...vpsStandardSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Standart Özellikler"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Textarea
                              rows={3}
                              value={vpsStandardSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    standard_section: {
                                      ...vpsStandardSection,
                                      subtitle: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Etkin ve güvenli VPS sunucu yönetimine uygun standartlar"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Görsel URL</label>
                          <Input
                            value={vpsStandardSection.image_url || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  standard_section: {
                                    ...vpsStandardSection,
                                    image_url: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="https://..."
                          />
                        </div>
                        <div className="space-y-3">
                          {vpsStandardCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...vpsStandardCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          standard_section: {
                                            ...vpsStandardSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Shield"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...vpsStandardCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          standard_section: {
                                            ...vpsStandardSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: cPanel / Plesk"
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = vpsStandardCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        standard_section: {
                                          ...vpsStandardSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {vpsStandardCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz standart özellik kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Kontrol Paneli Bölümü</h3>
                            <p className="text-sm text-gray-500">
                              VPS kontrol panelini anlatan başlık, alt metin ve kart içeriklerini buradan yönetebilirsiniz.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...controlPanelCards, { icon: '', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  control_panel_section: {
                                    ...controlPanelSection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={controlPanelSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    control_panel_section: {
                                      ...controlPanelSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Kolay Yönetim Deneyimi"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                        <Textarea
                              rows={3}
                              value={controlPanelSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    control_panel_section: {
                                      ...controlPanelSection,
                                      subtitle: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Tek tıkla sunucu yönetimi, kaynak takibi ve güvenlik kontrolleri."
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {controlPanelCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...controlPanelCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          control_panel_section: {
                                            ...controlPanelSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Settings"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...controlPanelCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          control_panel_section: {
                                            ...controlPanelSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Gelişmiş İzleme"
                                  />
                                </div>
                                <div className="md:col-span-1">
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={2}
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = [...controlPanelCards];
                                      cards[index] = { ...cards[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          control_panel_section: {
                                            ...controlPanelSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Trafik, kaynak ve güvenlik olaylarını anlık takip edin."
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = controlPanelCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        control_panel_section: {
                                          ...controlPanelSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                          </Button>
                              </div>
                            </div>
                          ))}
                          {controlPanelCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz kontrol panel kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">İşletim Sistemleri</h3>
                            <p className="text-sm text-gray-500">
                              Desteklediğiniz işletim sistemi kartlarını ekleyin. Gradient rengi Tailwind formatında olmalı
                              (örn: <code>from-orange-500 to-red-500</code>).
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [
                                ...operatingSystemsCards,
                                { id: '', name: '', icon: 'Server', color: 'from-orange-500 to-red-500', versions: [] }
                              ];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  operating_systems_section: {
                                    ...operatingSystemsSection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            İşletim Sistemi Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={operatingSystemsSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    operating_systems_section: {
                                      ...operatingSystemsSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Popüler İşletim Sistemleri"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                        <Textarea
                              rows={3}
                              value={operatingSystemsSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    operating_systems_section: {
                                      ...operatingSystemsSection,
                                      subtitle: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Linux ve Windows seçenekleriyle tam uyumluluk."
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {operatingSystemsCards.map((card, index) => {
                            const versionsText = Array.isArray(card.versions) ? card.versions.join('\n') : '';
                            return (
                              <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
                                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-600">İsim</label>
                                    <Input
                                      value={card.name || ''}
                                      onChange={(e) => {
                                        const cards = [...operatingSystemsCards];
                                        cards[index] = { ...cards[index], name: e.target.value };
                                        setPageContent({
                                          ...pageContent,
                                          additional_sections: {
                                            ...additional,
                                            operating_systems_section: {
                                              ...operatingSystemsSection,
                                              cards
                                            }
                                          }
                                        });
                                      }}
                                      placeholder="Örn: Ubuntu"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                    <Input
                                      value={card.icon || ''}
                                      onChange={(e) => {
                                        const cards = [...operatingSystemsCards];
                                        cards[index] = { ...cards[index], icon: e.target.value };
                                        setPageContent({
                                          ...pageContent,
                                          additional_sections: {
                                            ...additional,
                                            operating_systems_section: {
                                              ...operatingSystemsSection,
                                              cards
                                            }
                                          }
                                        });
                                      }}
                                      placeholder="Örn: Server"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-600">Gradient</label>
                                    <Input
                                      value={card.color || ''}
                                      onChange={(e) => {
                                        const cards = [...operatingSystemsCards];
                                        cards[index] = { ...cards[index], color: e.target.value };
                                        setPageContent({
                                          ...pageContent,
                                          additional_sections: {
                                            ...additional,
                                            operating_systems_section: {
                                              ...operatingSystemsSection,
                                              cards
                                            }
                                          }
                                        });
                                      }}
                                      placeholder="Örn: from-orange-500 to-red-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-600">Versiyonlar</label>
                                    <Textarea
                                      rows={4}
                                      value={versionsText}
                                      onChange={(e) => {
                                        const entries = e.target.value.split('\n').map((item) => item.trim()).filter(Boolean);
                                        const cards = [...operatingSystemsCards];
                                        cards[index] = { ...cards[index], versions: entries };
                                        setPageContent({
                                          ...pageContent,
                                          additional_sections: {
                                            ...additional,
                                            operating_systems_section: {
                                              ...operatingSystemsSection,
                                              cards
                                            }
                                          }
                                        });
                                      }}
                                      placeholder="Her satıra bir sürüm yazın"
                                    />
                                  </div>
                                </div>
                                <div className="flex justify-end">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                    onClick={() => {
                                      const cards = operatingSystemsCards.filter((_, idx) => idx !== index);
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          operating_systems_section: {
                                            ...operatingSystemsSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  >
                                    Kartı Sil
                          </Button>
                                </div>
                              </div>
                            );
                          })}
                          {operatingSystemsCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz işletim sistemi kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Windows Bölümü</h3>
                            <p className="text-sm text-gray-500">
                              VPS sayfasındaki Windows anlatımını güncelleyin. Açıklama alanında satır atlayarak yeni paragraf
                              oluşturabilirsiniz.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const highlights = [...windowsHighlights, { icon: 'CheckCircle', text: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  windows_section: {
                                    ...windowsSection,
                                    highlights
                                  }
                                }
                              });
                            }}
                          >
                            Vurgu Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={windowsSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    windows_section: {
                                      ...windowsSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Windows Sunucu Deneyimi"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Görsel URL</label>
                            <Input
                              value={windowsSection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    windows_section: {
                                      ...windowsSection,
                                      image_url: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={4}
                            value={windowsSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  windows_section: {
                                    ...windowsSection,
                                    description: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="Her paragrafı yeni satırda yazın."
                          />
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Vurgu Başlığı</label>
                            <Input
                              value={windowsSection.highlight_title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    windows_section: {
                                      ...windowsSection,
                                      highlight_title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Neden Windows?"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Vurgu Açıklaması</label>
                            <Textarea
                              rows={3}
                              value={windowsSection.highlight_description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    windows_section: {
                                      ...windowsSection,
                                      highlight_description: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Kısa açıklama metni"
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {windowsHighlights.map((highlight, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={highlight.icon || ''}
                                    onChange={(e) => {
                                      const highlights = [...windowsHighlights];
                                      highlights[index] = { ...highlights[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          windows_section: {
                                            ...windowsSection,
                                            highlights
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: CheckCircle"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Metin</label>
                                  <Input
                                    value={highlight.text || highlight.title || ''}
                                    onChange={(e) => {
                                      const highlights = [...windowsHighlights];
                                      highlights[index] = { ...highlights[index], text: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          windows_section: {
                                            ...windowsSection,
                                            highlights
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Aktif Directory uyumu"
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const highlights = windowsHighlights.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        windows_section: {
                                          ...windowsSection,
                                          highlights
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Vurguyu Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {windowsHighlights.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz vurgu maddesi eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">VPS ile VDS Arasındaki Farklar</h3>
                            <p className="text-sm text-gray-500">
                              Karşılaştırma bölümündeki başlığı, açıklama metnini (paragrafa bölünecek) ve görsel URL’sini girin.
                            </p>
                          </div>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={comparisonSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    comparison_section: {
                                      ...comparisonSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: VPS ile VDS Arasındaki Farklar"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Görsel URL</label>
                            <Input
                              value={comparisonSection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    comparison_section: {
                                      ...comparisonSection,
                                      image_url: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={4}
                            value={comparisonSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  comparison_section: {
                                    ...comparisonSection,
                                    description: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="Her paragrafı yeni satırda yazın."
                          />
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">7/24 Uzman VPS Desteği</h3>
                            <p className="text-sm text-gray-500">
                              Destek bölümünün başlığını, açıklamasını, kartlarını ve öne çıkan blok bilgilerini düzenleyin.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...supportCards, { icon: 'Headphones', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  support_section: {
                                    ...supportSection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            Destek Kartı Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={supportSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    support_section: {
                                      ...supportSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: 7/24 Uzman VPS Desteği"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={supportSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    support_section: {
                                      ...supportSection,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Deneyimli mühendislerimiz ile her an yanınızdayız."
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {supportCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...supportCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          support_section: {
                                            ...supportSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: HelpCircle"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...supportCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          support_section: {
                                            ...supportSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Bilet Sistemi"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={2}
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = [...supportCards];
                                      cards[index] = { ...cards[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          support_section: {
                                            ...supportSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Anında cevaplayan uzman ekip"
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = supportCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        support_section: {
                                          ...supportSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {supportCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz destek kartı eklenmedi.</p>
                          )}
                        </div>
                        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 space-y-3">
                          <h4 className="text-sm font-semibold text-orange-800">Öne Çıkan Blok</h4>
                          <div className="grid gap-3 md:grid-cols-2">
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">İkon</label>
                              <Input
                                value={supportHighlight.icon || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      support_section: {
                                        ...supportSection,
                                        highlight: {
                                          ...supportHighlight,
                                          icon: e.target.value
                                        }
                                      }
                                    }
                                  })
                                }
                                placeholder="Örn: Headphones"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                              <Input
                                value={supportHighlight.title || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      support_section: {
                                        ...supportSection,
                                        highlight: {
                                          ...supportHighlight,
                                          title: e.target.value
                                        }
                                      }
                                    }
                                  })
                                }
                                placeholder="Örn: VIP Destek Hattı"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                              <Textarea
                                rows={3}
                                value={supportHighlight.description || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      support_section: {
                                        ...supportSection,
                                        highlight: {
                                          ...supportHighlight,
                                          description: e.target.value
                                        }
                                      }
                                    }
                                  })
                                }
                                placeholder="Örn: Telefon, canlı sohbet ve WhatsApp ile saniyeler içinde cevap."
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Buton Metni</label>
                              <Input
                                value={supportHighlight.button_text || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      support_section: {
                                        ...supportSection,
                                        highlight: {
                                          ...supportHighlight,
                                          button_text: e.target.value
                                        }
                                      }
                                    }
                                  })
                                }
                                placeholder="Örn: Bize Ulaşın"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Buton Linki</label>
                              <Input
                                value={supportHighlight.button_link || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      support_section: {
                                        ...supportSection,
                                        highlight: {
                                          ...supportHighlight,
                                          button_link: e.target.value
                                        }
                                      }
                                    }
                                  })
                                }
                                placeholder="https:// veya dahili yol"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Standart Özellikler</h3>
                            <p className="text-sm text-gray-500">Standart özellik kartlarını ve görsellerini buradan yönetin.</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...vpsStandardCards, { icon: 'Shield', title: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  standard_section: {
                                    ...vpsStandardSection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={vpsStandardSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    standard_section: {
                                      ...vpsStandardSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Standart Özellikler"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Textarea
                              rows={3}
                              value={vpsStandardSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    standard_section: {
                                      ...vpsStandardSection,
                                      subtitle: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Etkin ve güvenli VPS sunucu yönetimine uygun standartlar"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Görsel URL</label>
                          <Input
                            value={vpsStandardSection.image_url || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  standard_section: {
                                    ...vpsStandardSection,
                                    image_url: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="https://..."
                          />
                        </div>
                        <div className="space-y-3">
                          {vpsStandardCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...vpsStandardCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          standard_section: {
                                            ...vpsStandardSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Shield"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...vpsStandardCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          standard_section: {
                                            ...vpsStandardSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: cPanel / Plesk"
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = vpsStandardCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        standard_section: {
                                          ...vpsStandardSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {vpsStandardCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz standart özellik kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPage(null);
                            setPageContent(null);
                          }}
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handleSavePageContent}
                          disabled={saving}
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </Button>
                      </div>
                    </div>
                  );
                })()
              ) : editingPage.path === '/vds-sunucu' && !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm overflow-x-hidden">
                  <p className="font-medium mb-2">⚠️ Veritabanında İçerik Yok</p>
                  <p className="mb-3 sm:mb-4 break-words">
                    VDS Sunucu sayfası içeriklerini düzenleyebilmek için migration çalıştırmanız gerekiyor. Bu işlem sayfa
                    verilerini veritabanına aktarır.
                  </p>
                  <Button
                    onClick={() => runMigration(PAGE_SLUG_MAP[editingPage.path] || null)}
                    disabled={migrationRunning}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                  </Button>
                </div>
              ) : editingPage.path === '/vds-sunucu' && pageContent ? (
                (() => {
                  const additional = pageContent.additional_sections || {};
                  const heroSection = additional.hero || {};
                  const introSection = additional.intro_section || {};
                  const featuresSection = additional.features_section || {};
                  const featureCards = Array.isArray(featuresSection.cards) ? featuresSection.cards : [];
                  const vdsValueProductsSection = additional.value_products_section || {};
                  const vdsValueProductCards = Array.isArray(vdsValueProductsSection.cards) ? vdsValueProductsSection.cards : [];
                  const vdsStandardSection = additional.standard_section || {};
                  const vdsStandardCards = Array.isArray(vdsStandardSection.cards) ? vdsStandardSection.cards : [];

                  return (
                    <div className="space-y-4 sm:space-y-6 overflow-x-hidden w-full">
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden w-full">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Image className="h-5 w-5" />
                          Hero Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={pageContent.hero_title || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                              placeholder="Örn: VDS Sunucu ile Üstün Performans"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge / Vurgu Metni</label>
                            <Input
                              value={pageContent.hero_badge_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                              placeholder="Örn: %99,9 Uptime Garantisi"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Açıklaması</label>
                          <Textarea
                            rows={3}
                            value={pageContent.hero_description || ''}
                            onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                            placeholder="Sanallaştırma ve dedike kaynaklarla yüksek performanslı çözümler..."
                          />
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hero Görseli</label>
                            <Input
                              value={heroSection.image_url || pageContent.hero_image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  hero_image_url: e.target.value,
                                  additional_sections: {
                                    ...additional,
                                    hero: {
                                      ...heroSection,
                                      image_url: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">İkincil Vurgu</label>
                            <Input
                              value={heroSection.highlight_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero: {
                                      ...heroSection,
                                      highlight_text: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Dedike kaynaklarla tam kontrol"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">
                          Bu sayfanın diğer bölümleri için form çalışması devam ediyor. Şimdilik yalnızca hero alanını güncelleyebilirsiniz.
                        </p>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-bold text-gray-900">VDS Server Nedir?</h3>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={introSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    intro_section: {
                                      ...introSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: VDS Server Nedir?"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Görsel URL</label>
                            <Input
                              value={introSection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    intro_section: {
                                      ...introSection,
                                      image_url: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={4}
                            value={introSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  intro_section: {
                                    ...introSection,
                                    description: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="VDS (Virtual Dedicated Server), ... "
                          />
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Benzersiz VDS Server Özellikleri</h3>
                            <p className="text-sm text-gray-500">Bu bölümdeki başlık, alt metin ve kartları güncelleyebilirsiniz.</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...featureCards, { icon: 'Shield', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  features_section: {
                                    ...featuresSection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={featuresSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    features_section: {
                                      ...featuresSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Benzersiz VDS Server Özellikleri"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Textarea
                              rows={3}
                              value={featuresSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    features_section: {
                                      ...featuresSection,
                                      subtitle: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Yüksek performans ve tam kontrol sunan VDS avantajları"
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {featureCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...featureCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          features_section: {
                                            ...featuresSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Shield"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...featureCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          features_section: {
                                            ...featuresSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Dedike Sunucu Kaynağı"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={2}
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = [...featureCards];
                                      cards[index] = { ...cards[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          features_section: {
                                            ...featuresSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Donanım kaynaklarının dedike ayrıldığı yapı ..."
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = featureCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        features_section: {
                                          ...featuresSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {featureCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz özellik kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-6 space-y-4">
                      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">Standart Özellikler</h3>
                          <p className="text-sm text-gray-500">Bu bölümdeki görseli ve standart kartları güncelleyebilirsiniz.</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const cards = [...vdsStandardCards, { icon: 'Users', title: '' }];
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...additional,
                                standard_section: {
                                  ...vdsStandardSection,
                                  cards
                                }
                              }
                            });
                          }}
                        >
                          Kart Ekle
                        </Button>
                      </div>
                      <div className="grid gap-4 lg:grid-cols-2 w-full">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Başlık</label>
                          <Input
                            value={vdsStandardSection.title || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  standard_section: {
                                    ...vdsStandardSection,
                                    title: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="Örn: Standart Özellikler"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                          <Textarea
                            rows={3}
                            value={vdsStandardSection.subtitle || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  standard_section: {
                                    ...vdsStandardSection,
                                    subtitle: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="Örn: Etkin ve güvenli VDS sunucu yönetimine uygun standartlar"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Görsel URL</label>
                        <Input
                          value={vdsStandardSection.image_url || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...additional,
                                standard_section: {
                                  ...vdsStandardSection,
                                  image_url: e.target.value
                                }
                              }
                            })
                          }
                          placeholder="https://..."
                        />
                      </div>
                      <div className="space-y-3">
                        {vdsStandardCards.map((card, index) => (
                          <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                            <div className="grid gap-3 md:grid-cols-2">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                <Input
                                  value={card.icon || ''}
                                  onChange={(e) => {
                                    const cards = [...vdsStandardCards];
                                    cards[index] = { ...cards[index], icon: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        standard_section: {
                                          ...vdsStandardSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                  placeholder="Örn: Users"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                <Input
                                  value={card.title || ''}
                                  onChange={(e) => {
                                    const cards = [...vdsStandardCards];
                                    cards[index] = { ...cards[index], title: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        standard_section: {
                                          ...vdsStandardSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                  placeholder="Örn: cPanel / Plesk"
                                />
                              </div>
                            </div>
                            <div className="flex justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => {
                                  const cards = vdsStandardCards.filter((_, idx) => idx !== index);
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      standard_section: {
                                        ...vdsStandardSection,
                                        cards
                                      }
                                    }
                                  });
                                }}
                              >
                                Kartı Sil
                              </Button>
                            </div>
                          </div>
                        ))}
                        {vdsStandardCards.length === 0 && (
                          <p className="text-xs text-gray-500">Henüz standart özellik kartı eklenmedi.</p>
                        )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">VDS Sunucunuza Değer Katacak Ürünler</h3>
                          <p className="text-sm text-gray-500">VDS’e değer katan tamamlayıcı hizmet kartlarını buradan yönetin.</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const cards = [...vdsValueProductCards, { title: '', description: '', image_url: '', link: '' }];
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...additional,
                                value_products_section: {
                                  ...vdsValueProductsSection,
                                  cards
                                }
                              }
                            });
                          }}
                        >
                          Kart Ekle
                        </Button>
                      </div>
                      <div className="grid gap-4 lg:grid-cols-2 w-full">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Başlık</label>
                          <Input
                            value={vdsValueProductsSection.title || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  value_products_section: {
                                    ...vdsValueProductsSection,
                                    title: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="Örn: VDS Sunucunuza Değer Katacak Ürünler"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                          <Textarea
                            rows={3}
                            value={vdsValueProductsSection.subtitle || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  value_products_section: {
                                    ...vdsValueProductsSection,
                                    subtitle: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="Örn: İşinize değer katan tamamlayıcı hizmetler"
                          />
                        </div>
                      </div>
                      <div className="space-y-3">
                        {vdsValueProductCards.map((card, index) => (
                          <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                            <div className="grid gap-3 md:grid-cols-2">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Kart Başlığı</label>
                                <Input
                                  value={card.title || ''}
                                  onChange={(e) => {
                                    const cards = [...vdsValueProductCards];
                                    cards[index] = { ...cards[index], title: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        value_products_section: {
                                          ...vdsValueProductsSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                  placeholder="Örn: SSL Sertifikası"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Görsel URL</label>
                                <Input
                                  value={card.image_url || ''}
                                  onChange={(e) => {
                                    const cards = [...vdsValueProductCards];
                                    cards[index] = { ...cards[index], image_url: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        value_products_section: {
                                          ...vdsValueProductsSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                  placeholder="https://..."
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                              <Textarea
                                rows={2}
                                value={card.description || ''}
                                onChange={(e) => {
                                  const cards = [...vdsValueProductCards];
                                  cards[index] = { ...cards[index], description: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      value_products_section: {
                                        ...vdsValueProductsSection,
                                        cards
                                      }
                                    }
                                  });
                                }}
                                placeholder="Örn: Google’da yükselin, güvenliğinizi garanti altına alın."
                              />
                            </div>
                            <div className="grid gap-3 md:grid-cols-2">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Link</label>
                                <Input
                                  value={card.link || ''}
                                  onChange={(e) => {
                                    const cards = [...vdsValueProductCards];
                                    cards[index] = { ...cards[index], link: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        value_products_section: {
                                          ...vdsValueProductsSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                  placeholder="/ssl/ssl-sertifikasi"
                                />
                              </div>
                              <div className="flex items-end justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = vdsValueProductCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        value_products_section: {
                                          ...vdsValueProductsSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                        {vdsValueProductCards.length === 0 && (
                          <p className="text-xs text-gray-500">Henüz tamamlayıcı ürün kartı eklenmedi.</p>
                        )}
                      </div>
                    </div>

                      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPage(null);
                            setPageContent(null);
                          }}
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handleSavePageContent}
                          disabled={saving}
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </Button>
                      </div>
                    </div>
                  );
                })()
              ) : editingPage.path === '/' && !pageContent ? (
                <div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800 text-sm mb-4">
                    <p className="font-medium mb-2">⚠️ Veritabanı Migration Gerekli</p>
                    <p className="mb-4">Anasayfa içeriklerini düzenleyebilmek için önce migration çalıştırmanız gerekiyor. Bu işlem page_contents tablosunu oluşturacak ve anasayfa verilerini veritabanına aktaracak.</p>
                    <Button 
                      onClick={() => runMigration(PAGE_SLUG_MAP[editingPage.path] || null)} 
                      disabled={migrationRunning}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                    >
                      {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                    </Button>
                  </div>
                </div>
              ) : editingPage.path === '/' && pageContent ? (
                <div className="space-y-6">
                  {/* Hero Section */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-3 sm:p-4 lg:p-6 border border-purple-200 overflow-x-hidden">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Image className="w-5 h-5" />
                      Hero Section
                    </h3>
                    
                    <div className="space-y-4">
                      {/* Badge */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Badge Metni</label>
                        <Input 
                          value={pageContent.hero_badge_text || ''} 
                          onChange={(e) => setPageContent({...pageContent, hero_badge_text: e.target.value})}
                        />
                      </div>
                      
                      {/* Title - Parse HTML and show only text */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hero Başlık</label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">1. Satır (Renkli)</label>
                            <Input 
                              placeholder="Dijital Dünya" 
                              value={(() => {
                                // Parse: <span...>Dijital Dünya</span>
                                const match = pageContent.hero_title?.match(/>([^<]+)</);
                                return match ? match[1] : '';
                              })()}
                              onChange={(e) => {
                                const line2Match = pageContent.hero_title?.match(/<br\/><span[^>]*>([^<]+)<\/span>/);
                                const line2 = line2Match ? line2Match[1] : 'Uzmanlarınız';
                                setPageContent({
                                  ...pageContent, 
                                  hero_title: `<span class="bg-gradient-to-r from-[#7A5AFF] via-[#9d7dff] to-[#7A5AFF] bg-clip-text text-transparent">${e.target.value}</span><br/><span class="text-white">${line2}</span>`
                                });
                              }}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">2. Satır (Beyaz)</label>
                            <Input 
                              placeholder="Uzmanlarınız" 
                              value={(() => {
                                // Parse: <br/><span...>Uzmanlarınız</span>
                                const match = pageContent.hero_title?.match(/<br\/><span[^>]*>([^<]+)<\/span>/);
                                return match ? match[1] : '';
                              })()}
                              onChange={(e) => {
                                const line1Match = pageContent.hero_title?.match(/>([^<]+)</);
                                const line1 = line1Match ? line1Match[1] : 'Dijital Dünya';
                                setPageContent({
                                  ...pageContent, 
                                  hero_title: `<span class="bg-gradient-to-r from-[#7A5AFF] via-[#9d7dff] to-[#7A5AFF] bg-clip-text text-transparent">${line1}</span><br/><span class="text-white">${e.target.value}</span>`
                                });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Description - Parse HTML and show only text */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hero Açıklama</label>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Ana Metin</label>
                            <Textarea 
                              placeholder="E-ticaret, web tasarım, mobil uygulama ve yapay zeka çözümleriyle işinizi büyütün." 
                              value={(() => {
                                // Parse: Text before <span>
                                const text = pageContent.hero_description || '';
                                const match = text.match(/^([^<]+)</);
                                return match ? match[1].trim() : text;
                              })()}
                              onChange={(e) => {
                                const brandMatch = pageContent.hero_description?.match(/<span[^>]*>([^<]+)<\/span>/);
                                const brand = brandMatch ? brandMatch[1] : 'Website Al';
                                const endMatch = pageContent.hero_description?.match(/<\/span>([^$]+)$/);
                                const endText = endMatch ? endMatch[1].trim() : 'ile hedeflerinize daha hızlı ulaşın.';
                                setPageContent({
                                  ...pageContent, 
                                  hero_description: `${e.target.value} <span class="text-[#7A5AFF] font-semibold">${brand}</span> ${endText}`
                                });
                              }}
                              rows={2}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Marka Adı (Mor vurgu)</label>
                              <Input 
                                placeholder="Website Al" 
                                value={(() => {
                                  // Parse: <span...>Website Al</span>
                                  const match = pageContent.hero_description?.match(/<span[^>]*>([^<]+)<\/span>/);
                                  return match ? match[1] : '';
                                })()}
                                onChange={(e) => {
                                  const mainMatch = pageContent.hero_description?.match(/^([^<]+)</);
                                  const mainText = mainMatch ? mainMatch[1].trim() : 'E-ticaret, web tasarım, mobil uygulama ve yapay zeka çözümleriyle işinizi büyütün.';
                                  const endMatch = pageContent.hero_description?.match(/<\/span>([^$]+)$/);
                                  const endText = endMatch ? endMatch[1].trim() : 'ile hedeflerinize daha hızlı ulaşın.';
                                  setPageContent({
                                    ...pageContent, 
                                    hero_description: `${mainText} <span class="text-[#7A5AFF] font-semibold">${e.target.value}</span> ${endText}`
                                  });
                                }}
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Son Cümle</label>
                              <Input 
                                placeholder="ile hedeflerinize daha hızlı ulaşın." 
                                value={(() => {
                                  // Parse: Text after </span>
                                  const match = pageContent.hero_description?.match(/<\/span>([^$]+)$/);
                                  return match ? match[1].trim() : '';
                                })()}
                                onChange={(e) => {
                                  const mainMatch = pageContent.hero_description?.match(/^([^<]+)</);
                                  const mainText = mainMatch ? mainMatch[1].trim() : 'E-ticaret, web tasarım, mobil uygulama ve yapay zeka çözümleriyle işinizi büyütün.';
                                  const brandMatch = pageContent.hero_description?.match(/<span[^>]*>([^<]+)<\/span>/);
                                  const brand = brandMatch ? brandMatch[1] : 'Dolugo Partner';
                                  setPageContent({
                                    ...pageContent, 
                                    hero_description: `${mainText} <span class="text-[#7A5AFF] font-semibold">${brand}</span> ${e.target.value}`
                                  });
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Image */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hero Resim</label>
                        <div className="flex gap-2">
                          <Input 
                            value={pageContent.hero_image_url || ''} 
                            onChange={(e) => setPageContent({...pageContent, hero_image_url: e.target.value})}
                            placeholder="/website-al-logo.png"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => openMediaLibrary('hero_image_url')}
                            className="flex items-center gap-2"
                          >
                            <Image className="w-4 h-4" />
                            Medya Seç
                          </Button>
                        </div>
                        {pageContent.hero_image_url && (
                          <div className="mt-2">
                            <img 
                              src={pageContent.hero_image_url} 
                              alt="Preview" 
                              className="w-32 h-32 object-cover rounded border border-gray-300"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Buttons */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Buton 1</label>
                          <Input 
                            placeholder="Buton Metni" 
                            value={pageContent.hero_button1_text || ''} 
                            onChange={(e) => setPageContent({...pageContent, hero_button1_text: e.target.value})}
                          />
                          <Input 
                            placeholder="Link (ör: #consultation)" 
                            value={pageContent.hero_button1_link || ''} 
                            onChange={(e) => setPageContent({...pageContent, hero_button1_link: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Buton 2</label>
                          <Input 
                            placeholder="Buton Metni" 
                            value={pageContent.hero_button2_text || ''} 
                            onChange={(e) => setPageContent({...pageContent, hero_button2_text: e.target.value})}
                          />
                          <Input 
                            placeholder="Link (ör: #project-share)" 
                            value={pageContent.hero_button2_link || ''} 
                            onChange={(e) => setPageContent({...pageContent, hero_button2_link: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Features Section */}
                  {Array.isArray(pageContent.features_section) && (
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4 lg:p-6 border border-gray-200 overflow-x-hidden">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Özellikler (Features)</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {pageContent.features_section.map((feature, index) => (
                          <div key={index} className="bg-white p-4 rounded border border-gray-200">
                            <Input 
                              placeholder="Başlık" 
                              value={feature.title || ''} 
                              onChange={(e) => {
                                const newFeatures = [...pageContent.features_section];
                                newFeatures[index] = {...feature, title: e.target.value};
                                setPageContent({...pageContent, features_section: newFeatures});
                              }}
                              className="mb-2"
                            />
                            <Textarea 
                              placeholder="Açıklama" 
                              value={feature.description || ''} 
                              onChange={(e) => {
                                const newFeatures = [...pageContent.features_section];
                                newFeatures[index] = {...feature, description: e.target.value};
                                setPageContent({...pageContent, features_section: newFeatures});
                              }}
                              rows={2}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Success Stories - Anasayfa */}
                  {editingPage.path === '/' && (
                    <div className="bg-green-50 rounded-lg p-3 sm:p-4 lg:p-6 border border-green-200 overflow-x-hidden">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Başarı Hikayeleri</h3>
                      
                      <div className="space-y-4 mb-4">
                        <Input 
                          placeholder="Section Başlığı" 
                          value={pageContent.success_stories_title || ''} 
                          onChange={(e) => setPageContent({...pageContent, success_stories_title: e.target.value})}
                        />
                        <Textarea 
                          placeholder="Section Açıklaması" 
                          value={pageContent.success_stories_description || ''} 
                          onChange={(e) => setPageContent({...pageContent, success_stories_description: e.target.value})}
                          rows={2}
                        />
                      </div>
                      
                      {(Array.isArray(pageContent.success_stories) ? pageContent.success_stories : []).map((story, index) => (
                        <div key={index} className="bg-white p-4 rounded border border-gray-200 mb-3">
                          <p className="text-sm font-medium text-gray-700 mb-2">Hikaye {index + 1}</p>
                          <div className="grid grid-cols-2 gap-2">
                            <Input 
                              placeholder="Başlık" 
                              value={story.title || ''} 
                              onChange={(e) => {
                                const prev = Array.isArray(pageContent.success_stories) ? pageContent.success_stories : [];
                                const newStories = [...prev];
                                newStories[index] = {...story, title: e.target.value};
                                setPageContent({...pageContent, success_stories: newStories});
                              }}
                            />
                            <Input 
                              placeholder="Şirket" 
                              value={story.company || ''} 
                              onChange={(e) => {
                                const prev = Array.isArray(pageContent.success_stories) ? pageContent.success_stories : [];
                                const newStories = [...prev];
                                newStories[index] = {...story, company: e.target.value};
                                setPageContent({...pageContent, success_stories: newStories});
                              }}
                            />
                            <Input 
                              placeholder="Sonuç (ör: %400 Satış Artışı)" 
                              value={story.result || ''} 
                              onChange={(e) => {
                                const prev = Array.isArray(pageContent.success_stories) ? pageContent.success_stories : [];
                                const newStories = [...prev];
                                newStories[index] = {...story, result: e.target.value};
                                setPageContent({...pageContent, success_stories: newStories});
                              }}
                            />
                            <Input 
                              placeholder="Hizmet" 
                              value={story.service || ''} 
                              onChange={(e) => {
                                const prev = Array.isArray(pageContent.success_stories) ? pageContent.success_stories : [];
                                const newStories = [...prev];
                                newStories[index] = {...story, service: e.target.value};
                                setPageContent({...pageContent, success_stories: newStories});
                              }}
                            />
                            <Input
                              type="number"
                              min="1"
                              max="5"
                              placeholder="Puan (1-5) - yıldız"
                              value={story.rating || ''}
                              onChange={(e) => {
                                const prev = Array.isArray(pageContent.success_stories) ? pageContent.success_stories : [];
                                const newStories = [...prev];
                                const value = e.target.value ? parseInt(e.target.value, 10) : undefined;
                                newStories[index] = {...story, rating: value};
                                setPageContent({...pageContent, success_stories: newStories});
                              }}
                            />
                          </div>
                          <Textarea 
                            placeholder="Açıklama" 
                            value={story.description || ''} 
                            onChange={(e) => {
                              const prev = Array.isArray(pageContent.success_stories) ? pageContent.success_stories : [];
                              const newStories = [...prev];
                              newStories[index] = {...story, description: e.target.value};
                              setPageContent({...pageContent, success_stories: newStories});
                            }}
                            rows={2}
                            className="mt-2"
                          />
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => setPageContent({
                          ...pageContent,
                          success_stories: [
                            ...(Array.isArray(pageContent.success_stories) ? pageContent.success_stories : []),
                            { title: '', company: '', result: '', description: '', service: '' }
                          ]
                        })}
                      >
                        + Başarı hikayesi ekle
                      </Button>
                    </div>
                  )}
                  
                  {/* Testimonials (Müşteri Yorumları) - Anasayfa */}
                  {editingPage.path === '/' && (
                    <div className="bg-yellow-50 rounded-lg p-3 sm:p-4 lg:p-6 border border-yellow-200 overflow-x-hidden">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Müşteri Yorumları (Yıldızlı)</h3>
                      
                      {(Array.isArray(pageContent.testimonials) ? pageContent.testimonials : []).map((testimonial, index) => (
                        <div key={index} className="bg-white p-4 rounded border border-gray-200 mb-3">
                          <p className="text-sm font-medium text-gray-700 mb-2">Yorum {index + 1}</p>
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              placeholder="İsim"
                              value={testimonial.name || ''}
                              onChange={(e) => {
                                const prev = Array.isArray(pageContent.testimonials) ? pageContent.testimonials : [];
                                const next = [...prev];
                                next[index] = { ...testimonial, name: e.target.value };
                                setPageContent({ ...pageContent, testimonials: next });
                              }}
                            />
                            <Input
                              placeholder="Şirket"
                              value={testimonial.company || ''}
                              onChange={(e) => {
                                const prev = Array.isArray(pageContent.testimonials) ? pageContent.testimonials : [];
                                const next = [...prev];
                                next[index] = { ...testimonial, company: e.target.value };
                                setPageContent({ ...pageContent, testimonials: next });
                              }}
                            />
                            <Input
                              placeholder="Hizmet"
                              value={testimonial.service || ''}
                              onChange={(e) => {
                                const prev = Array.isArray(pageContent.testimonials) ? pageContent.testimonials : [];
                                const next = [...prev];
                                next[index] = { ...testimonial, service: e.target.value };
                                setPageContent({ ...pageContent, testimonials: next });
                              }}
                            />
                            <Input
                              type="number"
                              min="1"
                              max="5"
                              placeholder="Puan (1-5) - yıldız"
                              value={testimonial.rating || ''}
                              onChange={(e) => {
                                const prev = Array.isArray(pageContent.testimonials) ? pageContent.testimonials : [];
                                const next = [...prev];
                                const value = e.target.value ? parseInt(e.target.value, 10) : undefined;
                                next[index] = { ...testimonial, rating: value };
                                setPageContent({ ...pageContent, testimonials: next });
                              }}
                            />
                          </div>
                          <Textarea
                            placeholder="Yorum metni"
                            value={testimonial.text || ''}
                            onChange={(e) => {
                              const prev = Array.isArray(pageContent.testimonials) ? pageContent.testimonials : [];
                              const next = [...prev];
                              next[index] = { ...testimonial, text: e.target.value };
                              setPageContent({ ...pageContent, testimonials: next });
                            }}
                            rows={2}
                            className="mt-2"
                          />
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => setPageContent({
                          ...pageContent,
                          testimonials: [
                            ...(Array.isArray(pageContent.testimonials) ? pageContent.testimonials : []),
                            { name: '', company: '', service: '', rating: 5, text: '' }
                          ]
                        })}
                      >
                        + Müşteri yorumu ekle
                      </Button>
                    </div>
                  )}
                  
                  {/* Projects Section - Anasayfa */}
                  {editingPage.path === '/' && (
                    <div className="bg-indigo-50 rounded-lg p-3 sm:p-4 lg:p-6 border border-indigo-200 overflow-x-hidden">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Projelerimiz</h3>
                      {(() => {
                        const sec = pageContent.additional_sections?.projects ?? {};
                        return (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Badge Metni</label>
                          <Input 
                            value={sec.badge_text || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                projects: {...(pageContent.additional_sections?.projects || {}), badge_text: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Başlık</label>
                          <Input 
                            value={sec.title || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                projects: {...(pageContent.additional_sections?.projects || {}), title: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Açıklama</label>
                          <Textarea 
                            value={sec.description || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                projects: {...(pageContent.additional_sections?.projects || {}), description: e.target.value}
                              }
                            })}
                            rows={2}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Buton Metni</label>
                          <Input 
                            value={sec.button_text || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                projects: {...(pageContent.additional_sections?.projects || {}), button_text: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-2">İstatistikler (4 adet)</label>
                          <div className="grid grid-cols-2 gap-2">
                            {[0, 1, 2, 3].map((idx) => {
                              const stat = (sec.stats && sec.stats[idx]) ? sec.stats[idx] : {};
                              const statIconOptions = [
                                'Users', 'CheckCircle', 'Star', 'Clock',
                                'Heart', 'Award', 'Shield', 'Activity'
                              ];
                              const statColorOptions = [
                                { value: 'text-blue-600', label: 'Mavi (Başarılı Proje)' },
                                { value: 'text-green-600', label: 'Yeşil (Memnuniyet)' },
                                { value: 'text-yellow-600', label: 'Sarı (Kalite)' },
                                { value: 'text-[#7A5AFF]', label: 'Mor (#7A5AFF)' }
                              ];
                              return (
                                <div key={idx} className="flex flex-col gap-1">
                                  <Input 
                                    placeholder={`Stat ${idx + 1} metni`}
                                    value={stat.label || ''} 
                                    onChange={(e) => {
                                      const base = pageContent.additional_sections?.projects || {};
                                      const currentStats = Array.isArray(base.stats) ? [...base.stats] : [];
                                      const updated = { ...(currentStats[idx] || {}), label: e.target.value };
                                      currentStats[idx] = updated;
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...pageContent.additional_sections,
                                          projects: {...base, stats: currentStats}
                                        }
                                      });
                                    }}
                                    className="text-xs"
                                  />
                                  <select
                                    value={stat.icon || ''}
                                    onChange={(e) => {
                                      const base = pageContent.additional_sections?.projects || {};
                                      const currentStats = Array.isArray(base.stats) ? [...base.stats] : [];
                                      const updated = { ...(currentStats[idx] || {}), icon: e.target.value };
                                      currentStats[idx] = updated;
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...pageContent.additional_sections,
                                          projects: {...base, stats: currentStats}
                                        }
                                      });
                                    }}
                                    className="w-full px-2 py-1 text-[11px] border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                                  >
                                    <option value="">İkon Seçin</option>
                                    {statIconOptions.map((iconName) => (
                                      <option key={iconName} value={iconName}>
                                        {iconName}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    value={stat.color || ''}
                                    onChange={(e) => {
                                      const base = pageContent.additional_sections?.projects || {};
                                      const currentStats = Array.isArray(base.stats) ? [...base.stats] : [];
                                      const updated = { ...(currentStats[idx] || {}), color: e.target.value };
                                      currentStats[idx] = updated;
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...pageContent.additional_sections,
                                          projects: {...base, stats: currentStats}
                                        }
                                      });
                                    }}
                                    className="w-full px-2 py-1 text-[11px] border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                                  >
                                    <option value="">Renk (varsayılan şablon)</option>
                                    {statColorOptions.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Services Section - Anasayfa */}
                  {editingPage.path === '/' && (
                    <div className="bg-orange-50 rounded-lg p-3 sm:p-4 lg:p-6 border border-orange-200 overflow-x-hidden">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Hizmetlerimiz</h3>
                      {(() => {
                        const sec = pageContent.additional_sections?.services ?? {};
                        return (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Badge Metni</label>
                          <Input 
                            value={sec.badge_text || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                services: {...(pageContent.additional_sections?.services || {}), badge_text: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Başlık</label>
                          <Input 
                            value={sec.title || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                services: {...(pageContent.additional_sections?.services || {}), title: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Açıklama</label>
                          <Textarea 
                            value={sec.description || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                services: {...(pageContent.additional_sections?.services || {}), description: e.target.value}
                              }
                            })}
                            rows={2}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Görsel URL (sağ taraftaki resim)</label>
                          <Input 
                            value={sec.image_url || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                services: {...(pageContent.additional_sections?.services || {}), image_url: e.target.value}
                              }
                            })}
                            placeholder="/website-al-logo.png"
                          />
                          <div className="mt-1">
                            <MediaSelector
                              label="Medya Kütüphanesinden Seç"
                              buttonText="Görsel Seç"
                              onSelect={(url) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...pageContent.additional_sections,
                                    services: {...(pageContent.additional_sections?.services || {}), image_url: url}
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Buton Metni</label>
                          <Input 
                            value={sec.button_text || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                services: {...(pageContent.additional_sections?.services || {}), button_text: e.target.value}
                              }
                            })}
                          />
                        </div>
                      </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Gogi AI Section - Anasayfa */}
                  {editingPage.path === '/' && (
                    <div className="bg-purple-50 rounded-lg p3 sm:p-4 lg:p-6 border border-purple-200 overflow-x-hidden">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Gogi AI ile Tanışın</h3>
                      {(() => {
                        const sec = pageContent.additional_sections?.gogi_ai ?? {};
                        return (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Badge Metni</label>
                          <Input 
                            value={sec.badge_text || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                gogi_ai: {...(pageContent.additional_sections?.gogi_ai || {}), badge_text: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Başlık (sadece metin kısmı)</label>
                          <Input 
                            placeholder="Gogi AI" 
                            value={(() => {
                              const match = sec.title?.match(/>([^<]+)</);
                              return match ? match[1] : (sec.title || '');
                            })()} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                gogi_ai: {
                                  ...(pageContent.additional_sections?.gogi_ai || {}), 
                                  title: `<span class="text-purple-600">${e.target.value}</span> ile Tanışın`
                                }
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Açıklama</label>
                          <Textarea 
                            value={sec.description || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                gogi_ai: {...(pageContent.additional_sections?.gogi_ai || {}), description: e.target.value}
                              }
                            })}
                            rows={3}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Buton Metni</label>
                          <Input 
                            value={sec.button_text || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                gogi_ai: {...(pageContent.additional_sections?.gogi_ai || {}), button_text: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Buton Link</label>
                          <Input 
                            value={sec.button_link || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                gogi_ai: {...(pageContent.additional_sections?.gogi_ai || {}), button_link: e.target.value}
                              }
                            })}
                            placeholder="/yapay-zeka"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Görsel URL</label>
                          <Input 
                            value={sec.image_url || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                gogi_ai: {...(pageContent.additional_sections?.gogi_ai || {}), image_url: e.target.value}
                              }
                            })}
                            placeholder="/gogi-ai-gorsel.png"
                          />
                          <div className="mt-1">
                            <MediaSelector
                              label="Medya Kütüphanesinden Seç"
                              buttonText="Görsel Seç"
                              onSelect={(url) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...pageContent.additional_sections,
                                    gogi_ai: {...(pageContent.additional_sections?.gogi_ai || {}), image_url: url}
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Education Section - Anasayfa */}
                  {editingPage.path === '/' && (
                    <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Dijital Becerilerinizi Geliştirin</h3>
                      {(() => {
                        const sec = pageContent.additional_sections?.education ?? {};
                        const base = pageContent.additional_sections?.education || {};
                        return (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Badge Metni</label>
                          <Input 
                            value={sec.badge_text || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                education: {...base, badge_text: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Başlık</label>
                          <Input 
                            value={sec.title || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                education: {...base, title: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Açıklama</label>
                          <Textarea 
                            value={sec.description || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                education: {...base, description: e.target.value}
                              }
                            })}
                            rows={3}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Buton Metni</label>
                          <Input 
                            value={sec.button_text || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                education: {...base, button_text: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Buton Link</label>
                          <Input 
                            value={sec.button_link || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                education: {...base, button_link: e.target.value}
                              }
                            })}
                            placeholder="/egitim"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Görsel URL</label>
                          <Input 
                            value={sec.image_url || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                education: {...base, image_url: e.target.value}
                              }
                            })}
                            placeholder="/egitim-gorsel.png"
                          />
                          <div className="mt-1">
                            <MediaSelector
                              label="Medya Kütüphanesinden Seç"
                              buttonText="Görsel Seç"
                              onSelect={(url) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...pageContent.additional_sections,
                                    education: {...base, image_url: url}
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-2">Liste öğeleri (madde madde, yeşil tik ile)</label>
                          {(sec.list_items || []).map((item, idx) => (
                            <div key={idx} className="flex gap-2 mb-2">
                              <Input 
                                value={typeof item === 'string' ? item : (item.text || item)} 
                                onChange={(e) => {
                                  const list = [...(base.list_items || [])];
                                  list[idx] = e.target.value;
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...pageContent.additional_sections,
                                      education: {...base, list_items: list}
                                    }
                                  });
                                }}
                                placeholder={`Öğe ${idx + 1}`}
                                className="flex-1"
                              />
                              <Button type="button" variant="outline" size="sm" onClick={() => {
                                const list = (base.list_items || []).filter((_, i) => i !== idx);
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...pageContent.additional_sections,
                                    education: {...base, list_items: list}
                                  }
                                });
                              }}>Sil</Button>
                            </div>
                          ))}
                          <Button type="button" variant="outline" size="sm" onClick={() => setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              education: {...base, list_items: [...(base.list_items || []), '']}
                            }
                          })}>+ Liste öğesi ekle</Button>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-2">Eğitim Kartları (video kartları)</label>
                          <div className="grid grid-cols-2 gap-2">
                            {(sec.video_cards || []).map((card, idx) => (
                              <div key={idx} className="bg-white p-2 rounded border border-gray-200">
                                <Input 
                                  placeholder="Başlık"
                                  value={card.title || ''} 
                                  onChange={(e) => {
                                    const newCards = [...(base.video_cards || [])];
                                    newCards[idx] = {...card, title: e.target.value};
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...pageContent.additional_sections,
                                        education: {...base, video_cards: newCards}
                                      }
                                    });
                                  }}
                                  className="text-xs mb-1"
                                />
                                <Input 
                                  placeholder="Açıklama"
                                  value={card.description || ''} 
                                  onChange={(e) => {
                                    const newCards = [...(base.video_cards || [])];
                                    newCards[idx] = {...card, description: e.target.value};
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...pageContent.additional_sections,
                                        education: {...base, video_cards: newCards}
                                      }
                                    });
                                  }}
                                  className="text-xs"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* SEO Hizmetleri - additional_sections JSON editor (advanced) */}
                  {pageContent.page_slug === 'seo-hizmetleri' && (
                    <div className="bg-rose-50 rounded-lg p-3 sm:p-4 lg:p-6 border border-rose-200 overflow-x-auto">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">SEO Hizmetleri - Ek İçerik (JSON)</h3>
                      <p className="text-xs text-gray-600 mb-3">
                        Bu alan SEO Hizmetleri sayfasındaki ek bölümleri (audit_checklist, pillars, roadmap, pricing_plans, articles, faqs vb.) JSON formatında düzenlemenizi sağlar.
                        Sadece geçerli JSON girin; yapı bozulursa sayfa bileşeni hatalı görünebilir.
                      </p>
                      <div className="overflow-x-auto">
                        <Textarea
                          rows={12}
                          className="text-xs sm:text-sm font-mono min-w-0 w-full"
                          value={JSON.stringify(pageContent.additional_sections || {}, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              setPageContent({
                                ...pageContent,
                                additional_sections: parsed
                              });
                            } catch (err) {
                              // Geçersiz JSON yazılırsa state'i güncellemiyoruz, kullanıcı düzeltmeli
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Blog / Haftalık Dijital Insights - Anasayfa */}
                  {editingPage.path === '/' && (
                    <div className="bg-pink-50 rounded-lg p-6 border border-pink-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Haftalık Dijital Insights (Blog)</h3>
                      {(() => {
                        const sec = pageContent.additional_sections?.blog ?? {};
                        const base = pageContent.additional_sections?.blog || {};
                        return (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Badge Metni</label>
                          <Input 
                            value={sec.badge_text || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                blog: {...base, badge_text: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Başlık</label>
                          <Input 
                            value={sec.title || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                blog: {...base, title: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Açıklama</label>
                          <Textarea 
                            value={sec.description || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                blog: {...base, description: e.target.value}
                              }
                            })}
                            rows={2}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Buton Metni</label>
                          <Input 
                            value={sec.button_text || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                blog: {...base, button_text: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-2">İkonlu madde öğeleri (başlık altında)</label>
                          {(sec.items || []).map((item, idx) => (
                            <div key={idx} className="flex gap-2 mb-2">
                              <Input 
                                value={typeof item === 'string' ? item : (item.text || item.label || '')} 
                                onChange={(e) => {
                                  const list = [...(base.items || [])];
                                  list[idx] = typeof list[idx] === 'object' ? { ...list[idx], text: e.target.value } : e.target.value;
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...pageContent.additional_sections,
                                      blog: {...base, items: list}
                                    }
                                  });
                                }}
                                placeholder={`Madde ${idx + 1}`}
                                className="flex-1"
                              />
                              <Button type="button" variant="outline" size="sm" onClick={() => {
                                const list = (base.items || []).filter((_, i) => i !== idx);
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...pageContent.additional_sections,
                                    blog: {...base, items: list}
                                  }
                                });
                              }}>Sil</Button>
                            </div>
                          ))}
                          <Button type="button" variant="outline" size="sm" onClick={() => setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              blog: {...base, items: [...(base.items || []), '']}
                            }
                          })}>+ Madde ekle</Button>
                        </div>
                      </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* FAQ Section - Anasayfa */}
                  {editingPage.path === '/' && (
                    <div className="bg-teal-50 rounded-lg p-6 border border-teal-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Sıkça Sorulan Sorular</h3>
                      {(() => {
                        const sec = pageContent.additional_sections?.faq ?? {};
                        const base = pageContent.additional_sections?.faq || {};
                        return (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Badge Metni</label>
                          <Input 
                            value={sec.badge_text || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                faq: {...base, badge_text: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Başlık</label>
                          <Input 
                            value={sec.title || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                faq: {...base, title: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Açıklama</label>
                          <Textarea 
                            value={sec.description || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                faq: {...base, description: e.target.value}
                              }
                            })}
                            rows={2}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Buton Metni</label>
                          <Input 
                            value={sec.button_text || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                faq: {...base, button_text: e.target.value}
                              }
                            })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Buton Link</label>
                          <Input 
                            value={sec.button_link || ''} 
                            onChange={(e) => setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                faq: {...base, button_link: e.target.value}
                              }
                            })}
                            placeholder="/sikca-sorulan-sorular"
                          />
                        </div>
                      </div>
                        );
                      })()}
                    </div>
                  )}
                  
                  {/* Save Button */}
                  <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                    <Button variant="outline" onClick={() => { setEditingPage(null); setPageContent(null); }}>
                      İptal
                    </Button>
                    <Button onClick={handleSavePageContent} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white">
                      {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                    </Button>
                  </div>
                </div>
              ) : editingPage.path === '/hakkimizda' && !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm overflow-x-hidden">
                  <p className="font-medium mb-2">⚠️ Veritabanında İçerik Yok</p>
                  <p className="mb-3 sm:mb-4 break-words">
                    Hakkımızda sayfası içeriklerini düzenleyebilmek için migration çalıştırmanız gerekiyor. Bu işlem sayfa
                    verilerini veritabanına aktarır.
                  </p>
                  <Button
                    onClick={() => runMigration(PAGE_SLUG_MAP[editingPage.path] || null)}
                    disabled={migrationRunning}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                  </Button>
                </div>
              ) : editingPage.path === '/hakkimizda' && pageContent ? (
                <div className="space-y-6">

                  {/* Hero Section */}
                  <div className="bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 rounded-lg p-3 sm:p-4 lg:p-6 border border-purple-200 overflow-x-hidden">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Image className="w-5 h-5" />
                      Hero Bölümü
                    </h3>
                    <div className="grid lg:grid-cols-2 gap-4 w-full">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                        <Input
                          value={pageContent.hero_badge_text || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Badge İkonu</label>
                        <Input
                          value={pageContent.hero_badge_icon || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_badge_icon: e.target.value })}
                          placeholder="Örn: Rocket"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 mt-4">
                      <label className="block text-sm font-medium text-gray-700">Hero Başlık</label>
                      <Textarea
                        rows={2}
                        value={pageContent.hero_title || ''}
                        onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2 mt-4">
                      <label className="block text-sm font-medium text-gray-700">Hero Açıklama</label>
                      <Textarea
                        rows={3}
                        value={pageContent.hero_description || ''}
                        onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                      />
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Hero Görseli</label>
                      <div className="flex gap-2">
                        <Input
                          value={pageContent.hero_image_url || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_image_url: e.target.value })}
                          placeholder="https://..."
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => openMediaLibrary('hero_image_url')}
                          className="flex items-center gap-2"
                        >
                          <Image className="w-4 h-4" />
                          Medya Seç
                        </Button>
                      </div>
                      {pageContent.hero_image_url && (
                        <div className="mt-2">
                          <img
                            src={pageContent.hero_image_url}
                            alt="Hero"
                            className="w-40 h-28 object-cover rounded-lg border border-gray-200"
                          />
                        </div>
                      )}
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-gray-700">Buton 1 Metni</label>
                        <Input
                          value={pageContent.hero_button1_text || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_button1_text: e.target.value })}
                        />
                        <Input
                          value={pageContent.hero_button1_link || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_button1_link: e.target.value })}
                          placeholder="/iletisim"
                        />
                        <Input
                          value={pageContent.hero_button1_icon || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_button1_icon: e.target.value })}
                          placeholder="Örn: MessageCircle"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-gray-700">Buton 2 Metni</label>
                        <Input
                          value={pageContent.hero_button2_text || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_button2_text: e.target.value })}
                        />
                        <Input
                          value={pageContent.hero_button2_link || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_button2_link: e.target.value })}
                          placeholder="/projeler"
                        />
                        <Input
                          value={pageContent.hero_button2_icon || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_button2_icon: e.target.value })}
                          placeholder="Opsiyonel ikon"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Features Section */}
                  {Array.isArray(pageContent.features_section) && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          <FileText className="w-5 h-5" />
                          Öne Çıkan Değerler
                        </h3>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setPageContent({
                              ...pageContent,
                              features_section: [
                                ...pageContent.features_section,
                                { icon: '', title: '', description: '', gradient: '' }
                              ]
                            })
                          }
                        >
                          Yeni Kart Ekle
                        </Button>
                      </div>
                      <div className="grid gap-4 lg:grid-cols-2 w-full">
                        {pageContent.features_section.map((feature, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">İkon</label>
                                <Input
                                  value={feature.icon || ''}
                                  onChange={(e) => {
                                    const newFeatures = [...pageContent.features_section];
                                    newFeatures[index] = { ...newFeatures[index], icon: e.target.value };
                                    setPageContent({ ...pageContent, features_section: newFeatures });
                                  }}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Gradyan</label>
                                <Input
                                  value={feature.gradient || ''}
                                  onChange={(e) => {
                                    const newFeatures = [...pageContent.features_section];
                                    newFeatures[index] = { ...newFeatures[index], gradient: e.target.value };
                                    setPageContent({ ...pageContent, features_section: newFeatures });
                                  }}
                                  placeholder="Örn: from-purple-500 to-blue-600"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600 mb-1">Başlık</label>
                              <Input
                                value={feature.title || ''}
                                onChange={(e) => {
                                  const newFeatures = [...pageContent.features_section];
                                  newFeatures[index] = { ...newFeatures[index], title: e.target.value };
                                  setPageContent({ ...pageContent, features_section: newFeatures });
                                }}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600 mb-1">Açıklama</label>
                              <Textarea
                                rows={3}
                                value={feature.description || ''}
                                onChange={(e) => {
                                  const newFeatures = [...pageContent.features_section];
                                  newFeatures[index] = { ...newFeatures[index], description: e.target.value };
                                  setPageContent({ ...pageContent, features_section: newFeatures });
                                }}
                              />
                            </div>
                            <div className="flex justify-end">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => {
                                  const newFeatures = pageContent.features_section.filter((_, i) => i !== index);
                                  setPageContent({ ...pageContent, features_section: newFeatures });
                                }}
                              >
                                Kartı Sil
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mission & Vision */}
                  <div className="grid gap-6 md:grid-cols-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Misyon Bölümü
                      </h3>
                      <Input
                        value={pageContent.additional_sections?.mission?.badge_text || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              mission: {
                                ...pageContent.additional_sections?.mission,
                                badge_text: e.target.value
                              }
                            }
                          })
                        }
                        placeholder="Badge Metni"
                      />
                      <Input
                        value={pageContent.additional_sections?.mission?.title || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              mission: {
                                ...pageContent.additional_sections?.mission,
                                title: e.target.value
                              }
                            }
                          })
                        }
                        placeholder="Başlık"
                      />
                      <Textarea
                        rows={4}
                        value={pageContent.additional_sections?.mission?.description || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              mission: {
                                ...pageContent.additional_sections?.mission,
                                description: e.target.value
                              }
                            }
                          })
                        }
                        placeholder="Açıklama"
                      />
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Görsel URL</label>
                        <div className="flex gap-2">
                          <Input
                            value={pageContent.additional_sections?.mission?.image_url || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...pageContent.additional_sections,
                                  mission: {
                                    ...pageContent.additional_sections?.mission,
                                    image_url: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="https://..."
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => openMediaLibrary('additional_sections.mission.image_url')}
                            className="flex items-center gap-2"
                          >
                            <Image className="w-4 h-4" />
                            Medya Seç
                          </Button>
                        </div>
                        {pageContent.additional_sections?.mission?.image_url && (
                          <img
                            src={pageContent.additional_sections?.mission?.image_url}
                            alt="Misyon"
                            className="mt-2 w-full max-w-xs rounded-lg border border-gray-200 object-cover"
                          />
                        )}
                      </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Vizyon Bölümü
                      </h3>
                      <Input
                        value={pageContent.additional_sections?.vision?.badge_text || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              vision: {
                                ...pageContent.additional_sections?.vision,
                                badge_text: e.target.value
                              }
                            }
                          })
                        }
                        placeholder="Badge Metni"
                      />
                      <Input
                        value={pageContent.additional_sections?.vision?.title || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              vision: {
                                ...pageContent.additional_sections?.vision,
                                title: e.target.value
                              }
                            }
                          })
                        }
                        placeholder="Başlık"
                      />
                      <Textarea
                        rows={4}
                        value={pageContent.additional_sections?.vision?.description || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              vision: {
                                ...pageContent.additional_sections?.vision,
                                description: e.target.value
                              }
                            }
                          })
                        }
                        placeholder="Açıklama"
                      />
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Görsel URL</label>
                        <div className="flex gap-2">
                          <Input
                            value={pageContent.additional_sections?.vision?.image_url || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...pageContent.additional_sections,
                                  vision: {
                                    ...pageContent.additional_sections?.vision,
                                    image_url: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="https://..."
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => openMediaLibrary('additional_sections.vision.image_url')}
                            className="flex items-center gap-2"
                          >
                            <Image className="w-4 h-4" />
                            Medya Seç
                          </Button>
                        </div>
                        {pageContent.additional_sections?.vision?.image_url && (
                          <img
                            src={pageContent.additional_sections?.vision?.image_url}
                            alt="Vizyon"
                            className="mt-2 w-full max-w-xs rounded-lg border border-gray-200 object-cover"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <Settings className="w-5 h-5" />
                        Zaman Çizelgesi
                      </h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const items = Array.isArray(pageContent.additional_sections?.timeline?.items)
                            ? [...pageContent.additional_sections.timeline.items]
                            : [];
                          items.push({ year: '', title: '', description: '' });
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              timeline: {
                                ...pageContent.additional_sections?.timeline,
                                items
                              }
                            }
                          });
                        }}
                      >
                        Yeni Adım Ekle
                      </Button>
                    </div>
                    <div className="grid lg:grid-cols-2 gap-4 w-full">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Badge Metni</label>
                        <Input
                          value={pageContent.additional_sections?.timeline?.badge_text || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                timeline: {
                                  ...pageContent.additional_sections?.timeline,
                                  badge_text: e.target.value
                                }
                              }
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Başlık</label>
                        <Input
                          value={pageContent.additional_sections?.timeline?.title || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                timeline: {
                                  ...pageContent.additional_sections?.timeline,
                                  title: e.target.value
                                }
                              }
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-4">
                      {(pageContent.additional_sections?.timeline?.items || []).length === 0 ? (
                        <p className="text-sm text-gray-500">Henüz zaman çizelgesi öğesi eklenmedi.</p>
                      ) : (
                        (pageContent.additional_sections?.timeline?.items || []).map((item, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
                            <div className="grid md:grid-cols-3 gap-3">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Yıl</label>
                                <Input
                                  value={item.year || ''}
                                  onChange={(e) => {
                                    const items = [...(pageContent.additional_sections?.timeline?.items || [])];
                                    items[index] = { ...items[index], year: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...pageContent.additional_sections,
                                        timeline: {
                                          ...pageContent.additional_sections?.timeline,
                                          items
                                        }
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Başlık</label>
                                <Input
                                  value={item.title || ''}
                                  onChange={(e) => {
                                    const items = [...(pageContent.additional_sections?.timeline?.items || [])];
                                    items[index] = { ...items[index], title: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...pageContent.additional_sections,
                                        timeline: {
                                          ...pageContent.additional_sections?.timeline,
                                          items
                                        }
                                      }
                                    });
                                  }}
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600 mb-1">Açıklama</label>
                              <Textarea
                                rows={3}
                                value={item.description || ''}
                                onChange={(e) => {
                                  const items = [...(pageContent.additional_sections?.timeline?.items || [])];
                                  items[index] = { ...items[index], description: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...pageContent.additional_sections,
                                      timeline: {
                                        ...pageContent.additional_sections?.timeline,
                                        items
                                      }
                                    }
                                  });
                                }}
                              />
                            </div>
                            <div className="flex justify-end">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => {
                                  const items = (pageContent.additional_sections?.timeline?.items || []).filter(
                                    (_, i) => i !== index
                                  );
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...pageContent.additional_sections,
                                      timeline: {
                                        ...pageContent.additional_sections?.timeline,
                                        items
                                      }
                                    }
                                  });
                                }}
                              >
                                Adımı Kaldır
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Services */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Hizmet Kartları
                      </h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const cards = Array.isArray(pageContent.additional_sections?.services?.cards)
                            ? [...pageContent.additional_sections.services.cards]
                            : [];
                          cards.push({ icon: '', title: '', description: '', link: '' });
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              services: {
                                ...pageContent.additional_sections?.services,
                                cards
                              }
                            }
                          });
                        }}
                      >
                        Yeni Hizmet Kartı Ekle
                      </Button>
                    </div>
                    <div className="space-y-3">
                      <Input
                        value={pageContent.additional_sections?.services?.badge_text || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              services: {
                                ...pageContent.additional_sections?.services,
                                badge_text: e.target.value
                              }
                            }
                          })
                        }
                        placeholder="Badge Metni"
                      />
                      <Input
                        value={pageContent.additional_sections?.services?.title || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              services: {
                                ...pageContent.additional_sections?.services,
                                title: e.target.value
                              }
                            }
                          })
                        }
                        placeholder="Başlık"
                      />
                      <Textarea
                        rows={3}
                        value={pageContent.additional_sections?.services?.description || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              services: {
                                ...pageContent.additional_sections?.services,
                                description: e.target.value
                              }
                            }
                          })
                        }
                        placeholder="Açıklama"
                      />
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      {(pageContent.additional_sections?.services?.cards || []).map((card, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-600 mb-1">İkon</label>
                              <Input
                                value={card.icon || ''}
                                onChange={(e) => {
                                  const cards = [...(pageContent.additional_sections?.services?.cards || [])];
                                  cards[index] = { ...cards[index], icon: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...pageContent.additional_sections,
                                      services: {
                                        ...pageContent.additional_sections?.services,
                                        cards
                                      }
                                    }
                                  });
                                }}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600 mb-1">Başlık</label>
                              <Input
                                value={card.title || ''}
                                onChange={(e) => {
                                  const cards = [...(pageContent.additional_sections?.services?.cards || [])];
                                  cards[index] = { ...cards[index], title: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...pageContent.additional_sections,
                                      services: {
                                        ...pageContent.additional_sections?.services,
                                        cards
                                      }
                                    }
                                  });
                                }}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={card.description || ''}
                              onChange={(e) => {
                                const cards = [...(pageContent.additional_sections?.services?.cards || [])];
                                cards[index] = { ...cards[index], description: e.target.value };
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...pageContent.additional_sections,
                                    services: {
                                      ...pageContent.additional_sections?.services,
                                      cards
                                    }
                                  }
                                });
                              }}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Link</label>
                            <Input
                              value={card.link || ''}
                              onChange={(e) => {
                                const cards = [...(pageContent.additional_sections?.services?.cards || [])];
                                cards[index] = { ...cards[index], link: e.target.value };
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...pageContent.additional_sections,
                                    services: {
                                      ...pageContent.additional_sections?.services,
                                      cards
                                    }
                                  }
                                });
                              }}
                              placeholder="/seo-hizmetleri"
                            />
                          </div>
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => {
                                const cards = (pageContent.additional_sections?.services?.cards || []).filter(
                                  (_, i) => i !== index
                                );
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...pageContent.additional_sections,
                                    services: {
                                      ...pageContent.additional_sections?.services,
                                      cards
                                    }
                                  }
                                });
                              }}
                            >
                              Kartı Sil
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* References */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Referanslar Bölümü
                    </h3>
                    <div className="grid lg:grid-cols-2 gap-4 w-full">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Badge Metni</label>
                        <Input
                          value={pageContent.additional_sections?.references?.badge_text || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                references: {
                                  ...pageContent.additional_sections?.references,
                                  badge_text: e.target.value
                                }
                              }
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Başlık</label>
                        <Input
                          value={pageContent.additional_sections?.references?.title || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                references: {
                                  ...pageContent.additional_sections?.references,
                                  title: e.target.value
                                }
                              }
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={Boolean(pageContent.additional_sections?.references?.show_partner_grid)}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                references: {
                                  ...pageContent.additional_sections?.references,
                                  show_partner_grid: e.target.checked
                                }
                              }
                            })
                          }
                        />
                        Partner grid görünür olsun
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={Boolean(pageContent.additional_sections?.references?.show_slider)}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                references: {
                                  ...pageContent.additional_sections?.references,
                                  show_slider: e.target.checked
                                }
                              }
                            })
                          }
                        />
                        Slider aktif olsun
                      </label>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="bg-gradient-to-r from-purple-600/10 to-blue-600/10 border border-purple-200 rounded-lg p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      CTA Bölümü
                    </h3>
                    <div className="space-y-3">
                      <Input
                        value={pageContent.additional_sections?.cta?.title || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              cta: {
                                ...pageContent.additional_sections?.cta,
                                title: e.target.value
                              }
                            }
                          })
                        }
                        placeholder="Başlık"
                      />
                      <Textarea
                        rows={3}
                        value={pageContent.additional_sections?.cta?.description || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              cta: {
                                ...pageContent.additional_sections?.cta,
                                description: e.target.value
                              }
                            }
                          })
                        }
                        placeholder="Açıklama"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-gray-700">Birincil Buton</h4>
                        <Input
                          value={pageContent.additional_sections?.cta?.primary_button?.text || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                cta: {
                                  ...pageContent.additional_sections?.cta,
                                  primary_button: {
                                    ...pageContent.additional_sections?.cta?.primary_button,
                                    text: e.target.value
                                  }
                                }
                              }
                            })
                          }
                          placeholder="Metin"
                        />
                        <Input
                          value={pageContent.additional_sections?.cta?.primary_button?.action || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                cta: {
                                  ...pageContent.additional_sections?.cta,
                                  primary_button: {
                                    ...pageContent.additional_sections?.cta?.primary_button,
                                    action: e.target.value
                                  }
                                }
                              }
                            })
                          }
                          placeholder="Aksiyon (örn: open_consultation_modal)"
                        />
                        <Input
                          value={pageContent.additional_sections?.cta?.primary_button?.icon || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                cta: {
                                  ...pageContent.additional_sections?.cta,
                                  primary_button: {
                                    ...pageContent.additional_sections?.cta?.primary_button,
                                    icon: e.target.value
                                  }
                                }
                              }
                            })
                          }
                          placeholder="İkon (örn: Lightbulb)"
                        />
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-gray-700">İkincil Buton</h4>
                        <Input
                          value={pageContent.additional_sections?.cta?.secondary_button?.text || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                cta: {
                                  ...pageContent.additional_sections?.cta,
                                  secondary_button: {
                                    ...pageContent.additional_sections?.cta?.secondary_button,
                                    text: e.target.value
                                  }
                                }
                              }
                            })
                          }
                          placeholder="Metin"
                        />
                        <Input
                          value={pageContent.additional_sections?.cta?.secondary_button?.link || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                cta: {
                                  ...pageContent.additional_sections?.cta,
                                  secondary_button: {
                                    ...pageContent.additional_sections?.cta?.secondary_button,
                                    link: e.target.value
                                  }
                                }
                              }
                            })
                          }
                          placeholder="Link (örn: /iletisim)"
                        />
                        <Input
                          value={pageContent.additional_sections?.cta?.secondary_button?.variant || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                cta: {
                                  ...pageContent.additional_sections?.cta,
                                  secondary_button: {
                                    ...pageContent.additional_sections?.cta?.secondary_button,
                                    variant: e.target.value
                                  }
                                }
                              }
                            })
                          }
                          placeholder="Variant (örn: outline)"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                    <Button variant="outline" onClick={() => { setEditingPage(null); setPageContent(null); }}>
                      İptal
                    </Button>
                    <Button onClick={handleSavePageContent} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white">
                      {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                    </Button>
                  </div>
                </div>
              ) : editingPage.path === '/iletisim' && !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm overflow-x-hidden">
                  <p className="font-medium mb-2">⚠️ Veritabanında İçerik Yok</p>
                  <p className="mb-3 sm:mb-4 break-words">
                    İletişim sayfası içeriklerini düzenleyebilmek için migration çalıştırmanız gerekiyor. Bu işlem sayfa
                    verilerini veritabanına aktarır.
                  </p>
                  <Button
                    onClick={() => runMigration(PAGE_SLUG_MAP[editingPage.path] || null)}
                    disabled={migrationRunning}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                  </Button>
                </div>
              ) : editingPage.path === '/iletisim' && pageContent ? (
                <div className="space-y-6">
                  {/* Hero Section */}
                  <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 sm:p-4 lg:p-6 overflow-x-hidden">
                    <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900">
                      <Image className="h-5 w-5" />
                      Hero Bölümü
                    </h3>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                        <Input
                          value={pageContent.hero_badge_text || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Badge İkonu</label>
                        <Input
                          value={pageContent.hero_badge_icon || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_badge_icon: e.target.value })}
                          placeholder="Örn: MessageSquare"
                        />
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Hero Başlık</label>
                      <Textarea
                        rows={2}
                        value={pageContent.hero_title || ''}
                        onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                      />
                    </div>

                    <div className="mt-4 space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Hero Açıklama</label>
                      <Textarea
                        rows={3}
                        value={pageContent.hero_description || ''}
                        onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                      />
                    </div>

                    <div className="mt-4">
                      <label className="mb-2 block text-sm font-medium text-gray-700">Hero Görseli</label>
                      <div className="flex gap-2">
                        <Input
                          value={pageContent.hero_image_url || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_image_url: e.target.value })}
                          placeholder="https://..."
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => openMediaLibrary('hero_image_url')}
                          className="flex items-center gap-2"
                        >
                          <Image className="h-4 w-4" />
                          Medya Seç
                        </Button>
                      </div>
                      {pageContent.hero_image_url && (
                        <div className="mt-2">
                          <img
                            src={pageContent.hero_image_url}
                            alt="İletişim Hero"
                            className="h-28 w-40 rounded-lg border border-gray-200 object-cover"
                          />
                        </div>
                      )}
                    </div>

                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Buton 1 Metni</label>
                        <Input
                          value={pageContent.hero_button1_text || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_button1_text: e.target.value })}
                        />
                        <Input
                          value={pageContent.hero_button1_link || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_button1_link: e.target.value })}
                          placeholder="/iletisim"
                        />
                        <Input
                          value={pageContent.hero_button1_icon || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_button1_icon: e.target.value })}
                          placeholder="Buton ikonu (Örn: Phone)"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Buton 2 Metni</label>
                        <Input
                          value={pageContent.hero_button2_text || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_button2_text: e.target.value })}
                        />
                        <Input
                          value={pageContent.hero_button2_link || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_button2_link: e.target.value })}
                          placeholder="/hizmetler"
                        />
                        <Input
                          value={pageContent.hero_button2_icon || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_button2_icon: e.target.value })}
                          placeholder="Opsiyonel ikon"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Cards */}
                  <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                        <FileText className="h-5 w-5" />
                        İletişim Kartları
                      </h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const cards = Array.isArray(pageContent.additional_sections?.contact_cards)
                            ? [...pageContent.additional_sections.contact_cards]
                            : [];
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              contact_cards: [...cards, { icon: '', title: '', description: '', url: '' }]
                            }
                          });
                        }}
                      >
                        Yeni Kart Ekle
                      </Button>
                    </div>
                    <div className="space-y-4">
                      {(pageContent.additional_sections?.contact_cards || []).map((card, index) => (
                        <div key={index} className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                          <div className="grid gap-3 md:grid-cols-2">
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">İkon</label>
                              <Input
                                value={card.icon || ''}
                                onChange={(e) => {
                                  const cards = [...(pageContent.additional_sections?.contact_cards || [])];
                                  cards[index] = { ...cards[index], icon: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...pageContent.additional_sections,
                                      contact_cards: cards
                                    }
                                  });
                                }}
                                placeholder="Örn: Mail, Phone, MapPin"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                              <Input
                                value={card.title || ''}
                                onChange={(e) => {
                                  const cards = [...(pageContent.additional_sections?.contact_cards || [])];
                                  cards[index] = { ...cards[index], title: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...pageContent.additional_sections,
                                      contact_cards: cards
                                    }
                                  });
                                }}
                                placeholder="Örn: E-posta"
                              />
                            </div>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">İçerik</label>
                              <Input
                                value={card.description || ''}
                                onChange={(e) => {
                                  const cards = [...(pageContent.additional_sections?.contact_cards || [])];
                                  cards[index] = { ...cards[index], description: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...pageContent.additional_sections,
                                      contact_cards: cards
                                    }
                                  });
                                }}
                                placeholder="İçerik metni"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">URL (Opsiyonel)</label>
                              <Input
                                value={card.url || ''}
                                onChange={(e) => {
                                  const cards = [...(pageContent.additional_sections?.contact_cards || [])];
                                  cards[index] = { ...cards[index], url: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...pageContent.additional_sections,
                                      contact_cards: cards
                                    }
                                  });
                                }}
                                placeholder="mailto:, tel: veya https://"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Boş bırakılırsa otomatik oluşturulur
                              </p>
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="border-red-200 text-red-600 hover:bg-red-50"
                              onClick={() => {
                                const cards = (pageContent.additional_sections?.contact_cards || []).filter(
                                  (_, i) => i !== index
                                );
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...pageContent.additional_sections,
                                    contact_cards: cards
                                  }
                                });
                              }}
                            >
                              Kartı Sil
                            </Button>
                          </div>
                        </div>
                      ))}
                      {(pageContent.additional_sections?.contact_cards || []).length === 0 && (
                        <p className="text-sm text-gray-500">Henüz kart eklenmedi.</p>
                      )}
                    </div>
                  </div>

                  {/* Social Links */}
                  <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                        <FileText className="h-5 w-5" />
                        Sosyal Medya
                      </h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const links = Array.isArray(pageContent.additional_sections?.social_links)
                            ? [...pageContent.additional_sections.social_links]
                            : [];
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              social_links: [...links, { icon: '', url: '' }]
                            }
                          });
                        }}
                      >
                        Yeni Sosyal Link Ekle
                      </Button>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Başlık</label>
                      <Input
                        value={pageContent.additional_sections?.social_title || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              social_title: e.target.value
                            }
                          })
                        }
                        placeholder="Sosyal medya başlığı"
                      />
                    </div>
                    <div className="space-y-4">
                      {(pageContent.additional_sections?.social_links || []).map((link, index) => (
                        <div key={index} className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                          <div className="grid gap-3 md:grid-cols-2">
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">İkon</label>
                              <Input
                                value={link.icon || ''}
                                onChange={(e) => {
                                  const links = [...(pageContent.additional_sections?.social_links || [])];
                                  links[index] = { ...links[index], icon: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...pageContent.additional_sections,
                                      social_links: links
                                    }
                                  });
                                }}
                                placeholder="Örn: Facebook"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">URL</label>
                              <Input
                                value={link.url || ''}
                                onChange={(e) => {
                                  const links = [...(pageContent.additional_sections?.social_links || [])];
                                  links[index] = { ...links[index], url: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...pageContent.additional_sections,
                                      social_links: links
                                    }
                                  });
                                }}
                                placeholder="https://..."
                              />
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="border-red-200 text-red-600 hover:bg-red-50"
                              onClick={() => {
                                const links = (pageContent.additional_sections?.social_links || []).filter((_, i) => i !== index);
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...pageContent.additional_sections,
                                    social_links: links
                                  }
                                });
                              }}
                            >
                              Linki Sil
                            </Button>
                          </div>
                        </div>
                      ))}
                      {(pageContent.additional_sections?.social_links || []).length === 0 && (
                        <p className="text-sm text-gray-500">Henüz sosyal medya linki eklenmedi.</p>
                      )}
                    </div>
                  </div>

                  {/* Form Settings */}
                  <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-6">
                    <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                      <FileText className="h-5 w-5" />
                      Form Ayarları
                    </h3>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Form Başlığı</label>
                        <Input
                          value={pageContent.additional_sections?.form?.title || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                form: {
                                  ...pageContent.additional_sections?.form,
                                  title: e.target.value
                                }
                              }
                            })
                          }
                          placeholder="Bize Yazın"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Başarı Rozeti Metni</label>
                        <Input
                          value={pageContent.additional_sections?.form?.success_badge || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                form: {
                                  ...pageContent.additional_sections?.form,
                                  success_badge: e.target.value
                                }
                              }
                            })
                          }
                          placeholder="Gönderildi"
                        />
                      </div>
                    </div>
                    <div className="grid gap-4 md:grid-cols-3">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Buton Metni</label>
                        <Input
                          value={pageContent.additional_sections?.form?.submit_button?.text || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                form: {
                                  ...pageContent.additional_sections?.form,
                                  submit_button: {
                                    ...pageContent.additional_sections?.form?.submit_button,
                                    text: e.target.value
                                  }
                                }
                              }
                            })
                          }
                          placeholder="Gönder"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Buton İkonu</label>
                        <Input
                          value={pageContent.additional_sections?.form?.submit_button?.icon || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                form: {
                                  ...pageContent.additional_sections?.form,
                                  submit_button: {
                                    ...pageContent.additional_sections?.form?.submit_button,
                                    icon: e.target.value
                                  }
                                }
                              }
                            })
                          }
                          placeholder="Örn: Send"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Yükleniyor Metni</label>
                        <Input
                          value={pageContent.additional_sections?.form?.submit_button?.loading_text || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                form: {
                                  ...pageContent.additional_sections?.form,
                                  submit_button: {
                                    ...pageContent.additional_sections?.form?.submit_button,
                                    loading_text: e.target.value
                                  }
                                }
                              }
                            })
                          }
                          placeholder="Gönderiliyor..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Map Settings */}
                  <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-6">
                    <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                      <FileText className="h-5 w-5" />
                      Harita Alanı
                    </h3>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={pageContent.additional_sections?.map?.enabled ?? true}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              map: {
                                ...pageContent.additional_sections?.map,
                                enabled: e.target.checked
                              }
                            }
                          })
                        }
                      />
                      Harita bölümü aktif olsun
                    </label>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Embed HTML</label>
                      <Textarea
                        rows={4}
                        disabled={!(pageContent.additional_sections?.map?.enabled ?? true)}
                        value={pageContent.additional_sections?.map?.embed_html || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              map: {
                                ...pageContent.additional_sections?.map,
                                embed_html: e.target.value
                              }
                            }
                          })
                        }
                        placeholder="<iframe ... />"
                      />
                      <p className="text-xs text-gray-500">
                        Google Maps veya benzeri bir iframe embed kodu ekleyin.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingPage(null);
                        setPageContent(null);
                      }}
                    >
                      İptal
                    </Button>
                    <Button
                      onClick={handleSavePageContent}
                      disabled={saving}
                      className="bg-purple-600 text-white hover:bg-purple-700"
                    >
                      {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                    </Button>
                  </div>
                </div>
              ) : editingPage.path === '/eticaret' && !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm overflow-x-hidden">
                  <p className="font-medium mb-2">⚠️ Veritabanında İçerik Yok</p>
                  <p className="mb-3 sm:mb-4 break-words">
                    E-ticaret sayfası içeriklerini düzenleyebilmek için migration çalıştırmanız gerekiyor. Bu işlem sayfa
                    verilerini veritabanına aktarır.
                  </p>
                  <Button
                    onClick={() => runMigration(PAGE_SLUG_MAP[editingPage.path] || null)}
                    disabled={migrationRunning}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                  </Button>
                </div>
              ) : editingPage.path === '/eticaret' && pageContent ? (
                <div className="space-y-6">
                  {/* Hero Section */}
                  <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 sm:p-4 lg:p-6 overflow-x-hidden">
                    <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900">
                      <Image className="h-5 w-5" />
                      Hero Bölümü
                    </h3>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Başlık</label>
                        <Input
                          value={pageContent.hero_title || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                        <Textarea
                          rows={3}
                          value={pageContent.hero_description || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Buton Metni</label>
                        <Input
                          value={pageContent.hero_button1_text || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_button1_text: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Buton Linki</label>
                        <Input
                          value={pageContent.hero_button1_link || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_button1_link: e.target.value })}
                          placeholder="/iletisim"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Buton İkonu</label>
                        <Input
                          value={pageContent.hero_button1_icon || ''}
                          onChange={(e) => setPageContent({ ...pageContent, hero_button1_icon: e.target.value })}
                          placeholder="Örn: Lightbulb"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Integration Sections */}
                  <div className="rounded-lg border border-gray-200 bg-white p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                        <Settings className="h-5 w-5" />
                        Entegrasyon Bölümleri
                      </h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const sections = Array.isArray(pageContent.additional_sections?.integration_sections)
                            ? [...pageContent.additional_sections.integration_sections]
                            : [];
                          sections.push({ title: '', description: '', logos: [] });
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              integration_sections: sections
                            }
                          });
                        }}
                      >
                        Bölüm Ekle
                      </Button>
                    </div>

                    {(pageContent.additional_sections?.integration_sections || []).map((section, idx) => (
                      <div key={idx} className="space-y-4">
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-gray-700">Bölüm #{idx + 1}</h4>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            onClick={() => {
                              const sections = (pageContent.additional_sections?.integration_sections || []).filter((_, i) => i !== idx);
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...pageContent.additional_sections,
                                  integration_sections: sections
                                }
                              });
                            }}
                          >
                            Bölümü Sil
                          </Button>
                        </div>

                        <div className="grid gap-3 md:grid-cols-2">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Başlık</label>
                            <Input
                              value={section.title || ''}
                              onChange={(e) => {
                                const sections = [...(pageContent.additional_sections?.integration_sections || [])];
                                sections[idx] = { ...sections[idx], title: e.target.value };
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...pageContent.additional_sections,
                                    integration_sections: sections
                                  }
                                });
                              }}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={section.description || ''}
                              onChange={(e) => {
                                const sections = [...(pageContent.additional_sections?.integration_sections || [])];
                                sections[idx] = { ...sections[idx], description: e.target.value };
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...pageContent.additional_sections,
                                    integration_sections: sections
                                  }
                                });
                              }}
                            />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h5 className="text-xs font-semibold text-gray-600">Logolar</h5>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const sections = [...(pageContent.additional_sections?.integration_sections || [])];
                                const logos = Array.isArray(sections[idx].logos) ? [...sections[idx].logos] : [];
                                logos.push({ name: '', image_url: '' });
                                sections[idx] = { ...sections[idx], logos };
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...pageContent.additional_sections,
                                    integration_sections: sections
                                  }
                                });
                              }}
                            >
                              Logo Ekle
                            </Button>
                          </div>

                          {(section.logos || []).map((logo, logoIdx) => (
                            <div key={logoIdx} className="grid gap-3 md:grid-cols-2 bg-white border border-gray-200 rounded-lg p-3">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Logo Adı</label>
                                <Input
                                  value={logo.name || ''}
                                  onChange={(e) => {
                                    const sections = [...(pageContent.additional_sections?.integration_sections || [])];
                                    const logos = Array.isArray(sections[idx].logos) ? [...sections[idx].logos] : [];
                                    logos[logoIdx] = { ...logos[logoIdx], name: e.target.value };
                                    sections[idx] = { ...sections[idx], logos };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...pageContent.additional_sections,
                                        integration_sections: sections
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Logo URL</label>
                                <Input
                                  value={logo.image_url || logo.img || ''}
                                  onChange={(e) => {
                                    const sections = [...(pageContent.additional_sections?.integration_sections || [])];
                                    const logos = Array.isArray(sections[idx].logos) ? [...sections[idx].logos] : [];
                                    logos[logoIdx] = { ...logos[logoIdx], image_url: e.target.value };
                                    sections[idx] = { ...sections[idx], logos };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...pageContent.additional_sections,
                                        integration_sections: sections
                                      }
                                    });
                                  }}
                                  placeholder="https://..."
                                />
                              </div>
                              <div className="md:col-span-2 flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const sections = [...(pageContent.additional_sections?.integration_sections || [])];
                                    const logos = Array.isArray(sections[idx].logos) ? [...sections[idx].logos] : [];
                                    sections[idx] = { ...sections[idx], logos: logos.filter((_, l) => l !== logoIdx) };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...pageContent.additional_sections,
                                        integration_sections: sections
                                      }
                                    });
                                  }}
                                >
                                  Logoyu Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* FAQ */}
                  <div className="rounded-lg border border-gray-200 bg-white p-6 grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">SSS Başlığı</label>
                      <Input
                        value={pageContent.additional_sections?.faq?.title || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              faq: {
                                ...pageContent.additional_sections?.faq,
                                title: e.target.value
                              }
                            }
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">SSS Açıklaması</label>
                      <Textarea
                        rows={3}
                        value={pageContent.additional_sections?.faq?.description || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              faq: {
                                ...pageContent.additional_sections?.faq,
                                description: e.target.value
                              }
                            }
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="rounded-lg border border-gray-200 bg-white p-6 space-y-4">
                    <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                      <FileText className="h-5 w-5" />
                      CTA Bölümü
                    </h3>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Başlık</label>
                        <Input
                          value={pageContent.additional_sections?.cta?.title || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                cta: {
                                  ...pageContent.additional_sections?.cta,
                                  title: e.target.value
                                }
                              }
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                        <Textarea
                          rows={3}
                          value={pageContent.additional_sections?.cta?.description || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                cta: {
                                  ...pageContent.additional_sections?.cta,
                                  description: e.target.value
                                }
                              }
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Buton Metni</label>
                        <Input
                          value={pageContent.additional_sections?.cta?.primary_button?.text || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                cta: {
                                  ...pageContent.additional_sections?.cta,
                                  primary_button: {
                                    ...pageContent.additional_sections?.cta?.primary_button,
                                    text: e.target.value
                                  }
                                }
                              }
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Buton Linki</label>
                        <Input
                          value={pageContent.additional_sections?.cta?.primary_button?.link || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                cta: {
                                  ...pageContent.additional_sections?.cta,
                                  primary_button: {
                                    ...pageContent.additional_sections?.cta?.primary_button,
                                    link: e.target.value
                                  }
                                }
                              }
                            })
                          }
                          placeholder="/iletisim"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Buton İkonu</label>
                        <Input
                          value={pageContent.additional_sections?.cta?.primary_button?.icon || ''}
                          onChange={(e) =>
                            setPageContent({
                              ...pageContent,
                              additional_sections: {
                                ...pageContent.additional_sections,
                                cta: {
                                  ...pageContent.additional_sections?.cta,
                                  primary_button: {
                                    ...pageContent.additional_sections?.cta?.primary_button,
                                    icon: e.target.value
                                  }
                                }
                              }
                            })
                          }
                          placeholder="Örn: Lightbulb"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Yan Metin</label>
                      <Input
                        value={pageContent.additional_sections?.cta?.secondary_highlight || ''}
                        onChange={(e) =>
                          setPageContent({
                            ...pageContent,
                            additional_sections: {
                              ...pageContent.additional_sections,
                              cta: {
                                ...pageContent.additional_sections?.cta,
                                secondary_highlight: e.target.value
                              }
                            }
                          })
                        }
                        placeholder="Uzman Danışmanlık Hizmeti"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingPage(null);
                        setPageContent(null);
                      }}
                    >
                      İptal
                    </Button>
                    <Button
                      onClick={handleSavePageContent}
                      disabled={saving}
                      className="bg-purple-600 text-white hover:bg-purple-700"
                    >
                      {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                    </Button>
                  </div>
                </div>
              ) : editingPage.path === '/sinirsiz-hosting' && !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm overflow-x-hidden">
                  <p className="font-medium mb-2">⚠️ Veritabanında İçerik Yok</p>
                  <p className="mb-3 sm:mb-4 break-words">
                    Sınırsız Hosting sayfası içeriklerini düzenleyebilmek için migration çalıştırmanız gerekiyor. Bu işlem
                    sayfa verilerini veritabanına aktarır.
                  </p>
                  <Button
                    onClick={() => runMigration(PAGE_SLUG_MAP[editingPage.path] || null)}
                    disabled={migrationRunning}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                  </Button>
                </div>
              ) : editingPage.path === '/sinirsiz-hosting' && pageContent ? (
                (() => {
                  const additional = pageContent.additional_sections || {};
                  const heroAdditional = additional.hero || {};
                  const heroBullets = Array.isArray(heroAdditional.bullets)
                    ? heroAdditional.bullets
                    : [];
                  const packagesAdditional = additional.packages_section || {};
                  const managementSection = additional.management_section || {};
                  const managementFeatures = Array.isArray(additional.management_features)
                    ? additional.management_features
                    : [];
                  const performanceSection = additional.performance_section || {};
                  const performanceFeatures = Array.isArray(performanceSection.features)
                    ? performanceSection.features
                    : [];
                  const commonFeatures = additional.common_features || {};
                  const commonItems = Array.isArray(commonFeatures.items)
                    ? commonFeatures.items
                    : [];
                  const appsSection = additional.apps_section || {};
                  const appsList = Array.isArray(appsSection.apps) ? appsSection.apps : [];
                  const appsParagraphs = Array.isArray(appsSection.paragraphs)
                    ? appsSection.paragraphs
                    : [];
                  const infoSections = Array.isArray(additional.info_sections)
                    ? additional.info_sections
                    : [];
                  const infoSectionOne = infoSections[0] || {};
                  const infoSectionTwo = infoSections[1] || {};
                  const infoSectionOneParagraphs = Array.isArray(infoSectionOne.paragraphs)
                    ? infoSectionOne.paragraphs
                    : [];
                  const infoSectionTwoParagraphs = Array.isArray(infoSectionTwo.paragraphs)
                    ? infoSectionTwo.paragraphs
                    : [];
                  const infoSectionTwoCard = infoSectionTwo.card || {};
                  const infoSectionTwoCardDescription = Array.isArray(infoSectionTwoCard.description)
                    ? infoSectionTwoCard.description
                    : [];
                  const infoSectionTwoCardBullets = Array.isArray(infoSectionTwoCard.bullets)
                    ? infoSectionTwoCard.bullets
                    : [];
                  const blogsSection = additional.blogs_section || {};
                  const faqSection = additional.faq || {};
                  const contactSection = additional.contact || {};
                  const contactHighlights = Array.isArray(contactSection.highlights)
                    ? contactSection.highlights
                    : [];

                  return (
                    <div className="space-y-4 sm:space-y-6 overflow-x-hidden">
                      {/* Hero Section */}
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Image className="h-5 w-5" />
                          Hero Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                            <Input
                              value={pageContent.hero_badge_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hero Başlığı</label>
                            <Input
                              value={pageContent.hero_title || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                              placeholder="Örn: Sınırsız Hosting ile"
                            />
                          </div>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Vurgulu Metin</label>
                            <Input
                              value={heroAdditional.highlight_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero: {
                                      ...heroAdditional,
                                      highlight_text: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Sınırsız Güç!"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık Soneki</label>
                            <Input
                              value={heroAdditional.title_suffix || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero: {
                                      ...heroAdditional,
                                      title_suffix: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Opsiyonel ek metin"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Açıklama</label>
                          <Textarea
                            rows={3}
                            value={pageContent.hero_description || ''}
                            onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Görseli</label>
                          <Input
                            value={heroAdditional.image_url || pageContent.hero_image_url || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                hero_image_url: e.target.value,
                                additional_sections: {
                                  ...additional,
                                  hero: {
                                    ...heroAdditional,
                                    image_url: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="https://..."
                          />
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">
                              Vurgu Maddeleri
                            </label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const bullets = [...heroBullets, ''];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero: {
                                      ...heroAdditional,
                                      bullets
                                    }
                                  }
                                });
                              }}
                            >
                              Madde Ekle
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {heroBullets.map((bullet, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <Input
                                  value={bullet}
                                  onChange={(e) => {
                                    const bullets = [...heroBullets];
                                    bullets[index] = e.target.value;
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        hero: {
                                          ...heroAdditional,
                                          bullets
                                        }
                                      }
                                    });
                                  }}
                                  placeholder="Örn: Sınırsız Disk Alanı"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => {
                                    const bullets = heroBullets.filter((_, i) => i !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        hero: {
                                          ...heroAdditional,
                                          bullets
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Sil
                                </Button>
                              </div>
                            ))}
                            {heroBullets.length === 0 && (
                              <p className="text-xs text-gray-500">
                                Listenin en az bir öğe içermesi önerilir. Madde eklemek için yukarıdaki butonu kullanın.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Paketler Bölümü */}
                      <div className="rounded-lg border border-gray-200 bg-white p-6 grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Paket Başlığı</label>
                          <Input
                            value={packagesAdditional.title || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  packages_section: {
                                    ...packagesAdditional,
                                    title: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="Örn: Sınırsız Hosting Paketleri"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Paket Açıklaması</label>
                          <Textarea
                            rows={3}
                            value={packagesAdditional.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  packages_section: {
                                    ...packagesAdditional,
                                    description: e.target.value
                                  }
                                }
                              })
                            }
                          />
                        </div>
                      </div>

                      {/* Yönetim Özellikleri */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <Settings className="h-5 w-5" />
                            Yönetim Özellikleri
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const features = [...managementFeatures, { icon: '', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  management_features: features
                                }
                              });
                            }}
                          >
                            Özellik Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <Input
                              value={managementSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    management_section: {
                                      ...managementSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={managementSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    management_section: {
                                      ...managementSection,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="space-y-4">
                          {managementFeatures.map((feature, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={feature.icon || ''}
                                    onChange={(e) => {
                                      const features = [...managementFeatures];
                                      features[index] = { ...features[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          management_features: features
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Globe"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={feature.title || ''}
                                    onChange={(e) => {
                                      const features = [...managementFeatures];
                                      features[index] = { ...features[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          management_features: features
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={2}
                                    value={feature.description || ''}
                                    onChange={(e) => {
                                      const features = [...managementFeatures];
                                      features[index] = { ...features[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          management_features: features
                                        }
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const features = managementFeatures.filter((_, i) => i !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        management_features: features
                                      }
                                    });
                                  }}
                                >
                                  Özelliği Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {managementFeatures.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz özellik eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      {/* Performans Bölümü */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Zap className="h-5 w-5" />
                          Performans & Güvenlik
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={performanceSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    performance_section: {
                                      ...performanceSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={performanceSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    performance_section: {
                                      ...performanceSection,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="space-y-4">
                          {performanceFeatures.map((feature, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={feature.icon || ''}
                                    onChange={(e) => {
                                      const features = [...performanceFeatures];
                                      features[index] = { ...features[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          performance_section: {
                                            ...performanceSection,
                                            features
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Zap"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={feature.title || ''}
                                    onChange={(e) => {
                                      const features = [...performanceFeatures];
                                      features[index] = { ...features[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          performance_section: {
                                            ...performanceSection,
                                            features
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={2}
                                    value={feature.description || ''}
                                    onChange={(e) => {
                                      const features = [...performanceFeatures];
                                      features[index] = { ...features[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          performance_section: {
                                            ...performanceSection,
                                            features
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const features = performanceFeatures.filter((_, i) => i !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        performance_section: {
                                          ...performanceSection,
                                          features
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Özelliği Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const features = [...performanceFeatures, { icon: '', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  performance_section: {
                                    ...performanceSection,
                                    features
                                  }
                                }
                              });
                            }}
                          >
                            Performans Özelliği Ekle
                          </Button>
                        </div>
                      </div>

                      {/* Ortak Özellikler */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Globe className="h-5 w-5" />
                          Ortak Özellikler
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={commonFeatures.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    common_features: {
                                      ...commonFeatures,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={commonFeatures.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    common_features: {
                                      ...commonFeatures,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="space-y-4">
                          {commonItems.map((item, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={item.icon || ''}
                                    onChange={(e) => {
                                      const items = [...commonItems];
                                      items[index] = { ...items[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          common_features: {
                                            ...commonFeatures,
                                            items
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Settings"
                                  />
                                </div>
                                <div className="md:col-span-2">
                                  <label className="block text-xs font-semibold text-gray-600">Etiket</label>
                                  <Textarea
                                    rows={2}
                                    value={item.label || ''}
                                    onChange={(e) => {
                                      const items = [...commonItems];
                                      items[index] = { ...items[index], label: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          common_features: {
                                            ...commonFeatures,
                                            items
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Metin satırlarını yeni satırla ayırabilirsiniz."
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const items = commonItems.filter((_, i) => i !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        common_features: {
                                          ...commonFeatures,
                                          items
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Öğeyi Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const items = [...commonItems, { icon: '', label: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  common_features: {
                                    ...commonFeatures,
                                    items
                                  }
                                }
                              });
                            }}
                          >
                            Ortak Özellik Ekle
                          </Button>
                        </div>
                      </div>

                      {/* Uygulamalar */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Code className="h-5 w-5" />
                          Uygulamalar
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={appsSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    apps_section: {
                                      ...appsSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Input
                              value={appsSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    apps_section: {
                                      ...appsSection,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Paragraflar</label>
                          <Textarea
                            rows={4}
                            value={appsParagraphs.join('\n')}
                            onChange={(e) => {
                              const paragraphs = e.target.value
                                .split('\n')
                                .map((line) => line.trim())
                                .filter(Boolean);
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  apps_section: {
                                    ...appsSection,
                                    paragraphs
                                  }
                                }
                              });
                            }}
                            placeholder="Her satır bir paragraf olarak kaydedilir."
                          />
                        </div>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">Uygulama Listesi</label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const apps = [...appsList, { name: '', icon: '' }];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    apps_section: {
                                      ...appsSection,
                                      apps
                                    }
                                  }
                                });
                              }}
                            >
                              Uygulama Ekle
                            </Button>
                          </div>
                          {appsList.map((app, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 grid gap-3 md:grid-cols-2">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Uygulama Adı</label>
                                <Input
                                  value={app.name || ''}
                                  onChange={(e) => {
                                    const apps = [...appsList];
                                    apps[index] = { ...apps[index], name: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        apps_section: {
                                          ...appsSection,
                                          apps
                                        }
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                <Input
                                  value={app.icon || ''}
                                  onChange={(e) => {
                                    const apps = [...appsList];
                                    apps[index] = { ...apps[index], icon: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        apps_section: {
                                          ...appsSection,
                                          apps
                                        }
                                      }
                                    });
                                  }}
                                  placeholder="Örn: Code"
                                />
                              </div>
                              <div className="md:col-span-2 flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const apps = appsList.filter((_, i) => i !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        apps_section: {
                                          ...appsSection,
                                          apps
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Uygulamayı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {appsList.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz uygulama eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      {/* Bilgi Bölümleri */}
                      <div className="rounded-lg border border-gray-200 bg-white p-6 space-y-6">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <FileText className="h-5 w-5" />
                          Bilgilendirici Bölümler
                        </h3>
                        {[infoSectionOne, infoSectionTwo].map((section, index) => {
                          const isSecond = index === 1;
                          const paragraphs = index === 0 ? infoSectionOneParagraphs : infoSectionTwoParagraphs;
                          const cardDescription = isSecond ? infoSectionTwoCardDescription : [];
                          const cardBullets = isSecond ? infoSectionTwoCardBullets : [];
                          return (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <h4 className="text-sm font-semibold text-gray-700">
                                Bölüm #{index + 1}
                              </h4>
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Vurgu Metni</label>
                                  <Input
                                    value={section.highlight || ''}
                                    onChange={(e) => {
                                      const sections = [...infoSections];
                                      sections[index] = { ...sections[index], highlight: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          info_sections: sections
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div className="md:col-span-2">
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={section.title || ''}
                                    onChange={(e) => {
                                      const sections = [...infoSections];
                                      sections[index] = { ...sections[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          info_sections: sections
                                        }
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Paragraflar</label>
                                <Textarea
                                  rows={4}
                                  value={paragraphs.join('\n')}
                                  onChange={(e) => {
                                    const sections = [...infoSections];
                                    const updatedParagraphs = e.target.value
                                      .split('\n')
                                      .map((line) => line.trim())
                                      .filter(Boolean);
                                    sections[index] = { ...sections[index], paragraphs: updatedParagraphs };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        info_sections: sections
                                      }
                                    });
                                  }}
                                  placeholder="Her satır yeni bir paragraf olarak kaydedilir."
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Görsel URL</label>
                                <Input
                                  value={section.image_url || ''}
                                  onChange={(e) => {
                                    const sections = [...infoSections];
                                    sections[index] = { ...sections[index], image_url: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        info_sections: sections
                                      }
                                    });
                                  }}
                                  placeholder="https://..."
                                />
                              </div>
                              {isSecond && (
                                <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
                                  <h5 className="text-xs font-semibold text-gray-700">Kart Alanı</h5>
                                  <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                    <div>
                                      <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                      <Input
                                        value={infoSectionTwoCard.icon || ''}
                                        onChange={(e) => {
                                          const sections = [...infoSections];
                                          sections[1] = {
                                            ...sections[1],
                                            card: {
                                              ...infoSectionTwoCard,
                                              icon: e.target.value
                                            }
                                          };
                                          setPageContent({
                                            ...pageContent,
                                            additional_sections: {
                                              ...additional,
                                              info_sections: sections
                                            }
                                          });
                                        }}
                                        placeholder="Örn: TrendingUp"
                                      />
                                    </div>
                                    <div className="md:col-span-2">
                                      <label className="block text-xs font-semibold text-gray-600">Kart Başlığı</label>
                                      <Input
                                        value={infoSectionTwoCard.title || ''}
                                        onChange={(e) => {
                                          const sections = [...infoSections];
                                          sections[1] = {
                                            ...sections[1],
                                            card: {
                                              ...infoSectionTwoCard,
                                              title: e.target.value
                                            }
                                          };
                                          setPageContent({
                                            ...pageContent,
                                            additional_sections: {
                                              ...additional,
                                              info_sections: sections
                                            }
                                          });
                                        }}
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-600">Kart Açıklaması</label>
                                    <Textarea
                                      rows={3}
                                      value={cardDescription.join('\n')}
                                      onChange={(e) => {
                                        const sections = [...infoSections];
                                        sections[1] = {
                                          ...sections[1],
                                          card: {
                                            ...infoSectionTwoCard,
                                            description: e.target.value
                                              .split('\n')
                                              .map((line) => line.trim())
                                              .filter(Boolean)
                                          }
                                        };
                                        setPageContent({
                                          ...pageContent,
                                          additional_sections: {
                                            ...additional,
                                            info_sections: sections
                                          }
                                        });
                                      }}
                                      placeholder="Her satır yeni bir paragraf olarak kaydedilir."
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-600">Kart Maddeleri</label>
                                    <Textarea
                                      rows={3}
                                      value={cardBullets.join('\n')}
                                      onChange={(e) => {
                                        const sections = [...infoSections];
                                        sections[1] = {
                                          ...sections[1],
                                          card: {
                                            ...infoSectionTwoCard,
                                            bullets: e.target.value
                                              .split('\n')
                                              .map((line) => line.trim())
                                              .filter(Boolean)
                                          }
                                        };
                                        setPageContent({
                                          ...pageContent,
                                          additional_sections: {
                                            ...additional,
                                            info_sections: sections
                                          }
                                        });
                                      }}
                                      placeholder="Her satır yeni bir madde olarak kaydedilir."
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Blog & SSS */}
                      <div className="rounded-lg border border-gray-200 bg-white p-6 grid gap-4 md:grid-cols-2">
                        <div className="space-y-3">
                          <h3 className="text-sm font-semibold text-gray-700">Blog Bölümü</h3>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                            <Input
                              value={blogsSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    blogs_section: {
                                      ...blogsSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={blogsSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    blogs_section: {
                                      ...blogsSection,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          <h3 className="text-sm font-semibold text-gray-700">SSS Bölümü</h3>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                            <Input
                              value={faqSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    faq: {
                                      ...faqSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={faqSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    faq: {
                                      ...faqSection,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>

                      {/* İletişim Bölümü */}
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <MessageCircle className="h-5 w-5" />
                          İletişim Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={contactSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    contact: {
                                      ...contactSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={contactSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    contact: {
                                      ...contactSection,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600">Birincil Buton Metni</label>
                            <Input
                              value={contactSection.primary_button?.text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    contact: {
                                      ...contactSection,
                                      primary_button: {
                                        ...contactSection.primary_button,
                                        text: e.target.value
                                      }
                                    }
                                  }
                                })
                              }
                            />
                            <label className="mt-2 block text-xs font-semibold text-gray-600">Birincil Buton Linki</label>
                            <Input
                              value={contactSection.primary_button?.link || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    contact: {
                                      ...contactSection,
                                      primary_button: {
                                        ...contactSection.primary_button,
                                        link: e.target.value
                                      }
                                    }
                                  }
                                })
                              }
                              placeholder="/iletisim"
                            />
                            <label className="mt-2 block text-xs font-semibold text-gray-600">Birincil Buton İkonu</label>
                            <Input
                              value={contactSection.primary_button?.icon || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    contact: {
                                      ...contactSection,
                                      primary_button: {
                                        ...contactSection.primary_button,
                                        icon: e.target.value
                                      }
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Mail"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600">İkincil Buton Metni</label>
                            <Input
                              value={contactSection.secondary_button?.text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    contact: {
                                      ...contactSection,
                                      secondary_button: {
                                        ...contactSection.secondary_button,
                                        text: e.target.value
                                      }
                                    }
                                  }
                                })
                              }
                            />
                            <label className="mt-2 block text-xs font-semibold text-gray-600">İkincil Buton Linki</label>
                            <Input
                              value={contactSection.secondary_button?.link || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    contact: {
                                      ...contactSection,
                                      secondary_button: {
                                        ...contactSection.secondary_button,
                                        link: e.target.value
                                      }
                                    }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                            <label className="mt-2 block text-xs font-semibold text-gray-600">İkincil Buton Aksiyonu</label>
                            <Input
                              value={contactSection.secondary_button?.action || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    contact: {
                                      ...contactSection,
                                      secondary_button: {
                                        ...contactSection.secondary_button,
                                        action: e.target.value
                                      }
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: whatsapp"
                            />
                            <label className="mt-2 block text-xs font-semibold text-gray-600">İkincil Buton İkonu</label>
                            <Input
                              value={contactSection.secondary_button?.icon || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    contact: {
                                      ...contactSection,
                                      secondary_button: {
                                        ...contactSection.secondary_button,
                                        icon: e.target.value
                                      }
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: MessageCircle"
                            />
                            <label className="mt-2 block text-xs font-semibold text-gray-600">WhatsApp Mesajı</label>
                            <Textarea
                              rows={2}
                              value={contactSection.secondary_button?.prefill_message || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    contact: {
                                      ...contactSection,
                                      secondary_button: {
                                        ...contactSection.secondary_button,
                                        prefill_message: e.target.value
                                      }
                                    }
                                  }
                                })
                              }
                              placeholder="Varsayılan WhatsApp mesajı"
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-gray-700">Öne Çıkan Kartlar</h4>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const highlights = [...contactHighlights, { icon: '', title: '', description: '' }];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    contact: {
                                      ...contactSection,
                                      highlights
                                    }
                                  }
                                });
                              }}
                            >
                              Kart Ekle
                            </Button>
                          </div>
                          {contactHighlights.map((highlight, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-white p-4 grid gap-3 md:grid-cols-3">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                <Input
                                  value={highlight.icon || ''}
                                  onChange={(e) => {
                                    const highlights = [...contactHighlights];
                                    highlights[index] = { ...highlights[index], icon: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        contact: {
                                          ...contactSection,
                                          highlights
                                        }
                                      }
                                    });
                                  }}
                                  placeholder="Örn: Clock"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                <Input
                                  value={highlight.title || ''}
                                  onChange={(e) => {
                                    const highlights = [...contactHighlights];
                                    highlights[index] = { ...highlights[index], title: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        contact: {
                                          ...contactSection,
                                          highlights
                                        }
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                <Textarea
                                  rows={2}
                                  value={highlight.description || ''}
                                  onChange={(e) => {
                                    const highlights = [...contactHighlights];
                                    highlights[index] = { ...highlights[index], description: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        contact: {
                                          ...contactSection,
                                          highlights
                                        }
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <div className="md:col-span-3 flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const highlights = contactHighlights.filter((_, i) => i !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        contact: {
                                          ...contactSection,
                                          highlights
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {contactHighlights.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz öne çıkan kart eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPage(null);
                            setPageContent(null);
                          }}
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handleSavePageContent}
                          disabled={saving}
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </Button>
                      </div>
                    </div>
                  );
                })()
              ) : editingPage.path === '/eticaret-hosting' && !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm overflow-x-hidden">
                  <p className="font-medium mb-2">⚠️ Veritabanında İçerik Yok</p>
                  <p className="mb-3 sm:mb-4 break-words">
                    E-Ticaret Hosting sayfası içeriklerini düzenleyebilmek için migration çalıştırmanız gerekiyor. Bu işlem
                    sayfa verilerini veritabanına aktarır.
                  </p>
                  <Button
                    onClick={() => runMigration(PAGE_SLUG_MAP[editingPage.path] || null)}
                    disabled={migrationRunning}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                  </Button>
                </div>
              ) : editingPage.path === '/eticaret-hosting' && pageContent ? (
                (() => {
                  const additional = pageContent.additional_sections || {};
                  const heroSection = additional.hero || {};
                  const heroBullets = Array.isArray(heroSection.bullets) ? heroSection.bullets : [];
                  const packagesSection = additional.packages_section || {};
                  const posSection = additional.pos_section || {};
                  const posCards = Array.isArray(posSection.cards) ? posSection.cards : [];
                  const highlightsSection = additional.highlights_section || {};
                  const highlightCards = Array.isArray(highlightsSection.cards) ? highlightsSection.cards : [];
                  const faqSection = additional.faq_section || {};
                  const ctaSection = additional.cta_section || {};
                  const ctaHighlights = Array.isArray(ctaSection.highlights) ? ctaSection.highlights : [];
                  const primaryButton = ctaSection.primary_button || {};
                  const secondaryButton = ctaSection.secondary_button || {};

                  return (
                    <div className="space-y-4 sm:space-y-6 overflow-x-hidden w-full">
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden w-full">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Image className="h-5 w-5" />
                          Hero Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                            <Input
                              value={pageContent.hero_badge_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                              placeholder="Örn: E-Ticaret Hosting"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hero Başlığı</label>
                            <Input
                              value={pageContent.hero_title || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                              placeholder="Örn: Hazır Sanal POS ile Satışa Başlayın"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Açıklama</label>
                          <Textarea
                            rows={3}
                            value={pageContent.hero_description || ''}
                            onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                            placeholder="Kısa açıklama metni"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Görseli</label>
                          <Input
                            value={heroSection.image_url || pageContent.hero_image_url || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                hero_image_url: e.target.value,
                                additional_sections: {
                                  ...additional,
                                  hero: {
                                    ...heroSection,
                                    image_url: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="https://..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Vurgusu</label>
                          <Input
                            value={heroSection.highlight_text || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  hero: {
                                    ...heroSection,
                                    highlight_text: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="Örn: Hazır Sanal POS, hazır temalar..."
                          />
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">Hero Maddeleri</label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const bullets = [...heroBullets, { icon: 'CheckCircle', text: '' }];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero: {
                                      ...heroSection,
                                      bullets
                                    }
                                  }
                                });
                              }}
                            >
                              Madde Ekle
                            </Button>
                          </div>
                          {heroBullets.length === 0 && (
                            <p className="text-xs text-gray-500">Hero bölümünde gösterilecek maddeler ekleyin.</p>
                          )}
                          <div className="space-y-3">
                            {heroBullets.map((bullet, index) => (
                              <div key={index} className="grid gap-3 md:grid-cols-5 rounded-lg border border-emerald-200 bg-white p-4">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={bullet.icon || ''}
                                    onChange={(e) => {
                                      const bullets = [...heroBullets];
                                      bullets[index] = { ...bullets[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          hero: {
                                            ...heroSection,
                                            bullets
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: CheckCircle"
                                  />
                                </div>
                                <div className="md:col-span-4">
                                  <label className="block text-xs font-semibold text-gray-600">Metin</label>
                                  <Input
                                    value={bullet.text || ''}
                                    onChange={(e) => {
                                      const bullets = [...heroBullets];
                                      bullets[index] = { ...bullets[index], text: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          hero: {
                                            ...heroSection,
                                            bullets
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Hazır Sanal POS"
                                  />
                                </div>
                                <div className="md:col-span-5 flex justify-end">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                    onClick={() => {
                                      const bullets = heroBullets.filter((_, idx) => idx !== index);
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          hero: {
                                            ...heroSection,
                                            bullets
                                          }
                                        }
                                      });
                                    }}
                                  >
                                    Sil
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <FileText className="h-5 w-5" />
                          Paketler Bölümü
                        </h3>
                        <div className="grid gap-4 md:grid-cols-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={packagesSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    packages_section: {
                                      ...packagesSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: E-Ticaret Hosting Paketleri"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={packagesSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    packages_section: {
                                      ...packagesSection,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Not</label>
                            <Input
                              value={packagesSection.note || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    packages_section: {
                                      ...packagesSection,
                                      note: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: 3 yıllık alımlarda geçerlidir"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <Settings className="h-5 w-5" />
                            POS & WooCommerce Kartları
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...posCards, { image_url: '', title: '', description: '', secondary_description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  pos_section: {
                                    ...posSection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        {posCards.length === 0 && (
                          <p className="text-xs text-gray-500">POS / WooCommerce kartları ekleyin.</p>
                        )}
                        <div className="space-y-3">
                          {posCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...posCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          pos_section: {
                                            ...posSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Görsel URL</label>
                                  <Input
                                    value={card.image_url || ''}
                                    onChange={(e) => {
                                      const cards = [...posCards];
                                      cards[index] = { ...cards[index], image_url: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          pos_section: {
                                            ...posSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="https://..."
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                <Textarea
                                  rows={2}
                                  value={card.description || ''}
                                  onChange={(e) => {
                                    const cards = [...posCards];
                                    cards[index] = { ...cards[index], description: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        pos_section: {
                                          ...posSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Ek Açıklama</label>
                                <Textarea
                                  rows={2}
                                  value={card.secondary_description || ''}
                                  onChange={(e) => {
                                    const cards = [...posCards];
                                    cards[index] = { ...cards[index], secondary_description: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        pos_section: {
                                          ...posSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = posCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        pos_section: {
                                          ...posSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <Zap className="h-5 w-5" />
                            Öne Çıkan Özellikler
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...highlightCards, { icon: '', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  highlights_section: {
                                    ...highlightsSection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={highlightsSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    highlights_section: {
                                      ...highlightsSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Input
                              value={highlightsSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    highlights_section: {
                                      ...highlightsSection,
                                      subtitle: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {highlightCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...highlightCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          highlights_section: {
                                            ...highlightsSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Shield"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...highlightCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          highlights_section: {
                                            ...highlightsSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={2}
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = [...highlightCards];
                                      cards[index] = { ...cards[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          highlights_section: {
                                            ...highlightsSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = highlightCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        highlights_section: {
                                          ...highlightsSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {highlightCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz öne çıkan kart eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <HelpCircle className="h-5 w-5" />
                          SSS Başlıkları
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={faqSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    faq_section: {
                                      ...faqSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Sıkça Sorulan Sorular"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Textarea
                              rows={2}
                              value={faqSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    faq_section: {
                                      ...faqSection,
                                      subtitle: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <MessageCircle className="h-5 w-5" />
                          CTA Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={ctaSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    cta_section: {
                                      ...ctaSection,
                                      title: e.target.value,
                                      primary_button: primaryButton,
                                      secondary_button: secondaryButton,
                                      highlights: ctaHighlights
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={ctaSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    cta_section: {
                                      ...ctaSection,
                                      description: e.target.value,
                                      primary_button: primaryButton,
                                      secondary_button: secondaryButton,
                                      highlights: ctaHighlights
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>

                        <div className="rounded-lg border border-emerald-200 bg-white p-4 space-y-3">
                          <h4 className="text-sm font-semibold text-gray-900">Birincil Buton</h4>
                          <div className="grid gap-3 md:grid-cols-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Metin</label>
                              <Input
                                value={primaryButton.text || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: { ...primaryButton, text: e.target.value },
                                        secondary_button: secondaryButton,
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Link</label>
                              <Input
                                value={primaryButton.link || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: { ...primaryButton, link: e.target.value },
                                        secondary_button: secondaryButton,
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="/iletisim veya https://"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">İkon</label>
                              <Input
                                value={primaryButton.icon || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: { ...primaryButton, icon: e.target.value },
                                        secondary_button: secondaryButton,
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="Örn: Mail"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="rounded-lg border border-emerald-200 bg-white p-4 space-y-3">
                          <h4 className="text-sm font-semibold text-gray-900">İkincil Buton</h4>
                          <div className="grid gap-3 md:grid-cols-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Metin</label>
                              <Input
                                value={secondaryButton.text || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: primaryButton,
                                        secondary_button: { ...secondaryButton, text: e.target.value },
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Link/Aksiyon</label>
                              <Input
                                value={secondaryButton.link || secondaryButton.action || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: primaryButton,
                                        secondary_button: {
                                          ...secondaryButton,
                                          link: value.startsWith('http') || value.startsWith('/') ? value : secondaryButton.link || '',
                                          action: value === 'whatsapp' ? 'whatsapp' : secondaryButton.action || ''
                                        },
                                        highlights: ctaHighlights
                                      }
                                    }
                                  });
                                }}
                                placeholder="/iletisim, whatsapp veya https://"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">İkon</label>
                              <Input
                                value={secondaryButton.icon || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: primaryButton,
                                        secondary_button: { ...secondaryButton, icon: e.target.value },
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="Örn: MessageCircle"
                              />
                            </div>
                            <div className="md:col-span-3 grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Telefon</label>
                                <Input
                                  value={secondaryButton.phone || ''}
                                  onChange={(e) =>
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        cta_section: {
                                          ...ctaSection,
                                          title: ctaSection.title,
                                          description: ctaSection.description,
                                          primary_button: primaryButton,
                                          secondary_button: { ...secondaryButton, phone: e.target.value },
                                          highlights: ctaHighlights
                                        }
                                      }
                                    })
                                  }
                                  placeholder="Örn: 905324404011"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">WhatsApp Mesajı</label>
                                <Textarea
                                  rows={2}
                                  value={secondaryButton.prefill_message || ''}
                                  onChange={(e) =>
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        cta_section: {
                                          ...ctaSection,
                                          title: ctaSection.title,
                                          description: ctaSection.description,
                                          primary_button: primaryButton,
                                          secondary_button: { ...secondaryButton, prefill_message: e.target.value },
                                          highlights: ctaHighlights
                                        }
                                      }
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="block text-xs font-semibold text-gray-600">Öne Çıkan Kartlar</label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const highlights = [...ctaHighlights, { icon: '', title: '', description: '' }];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    cta_section: {
                                      ...ctaSection,
                                      title: ctaSection.title,
                                      description: ctaSection.description,
                                      primary_button: primaryButton,
                                      secondary_button: secondaryButton,
                                      highlights
                                    }
                                  }
                                });
                              }}
                            >
                              Kart Ekle
                            </Button>
                          </div>
                          {ctaHighlights.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz CTA kartı eklenmedi.</p>
                          )}
                          {ctaHighlights.map((highlight, index) => (
                            <div key={index} className="grid gap-2 md:grid-cols-5 rounded-lg border border-emerald-200 bg-white p-3">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                <Input
                                  value={highlight.icon || ''}
                                  onChange={(e) => {
                                    const highlights = [...ctaHighlights];
                                    highlights[index] = { ...highlights[index], icon: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        cta_section: {
                                          ...ctaSection,
                                          title: ctaSection.title,
                                          description: ctaSection.description,
                                          primary_button: primaryButton,
                                          secondary_button: secondaryButton,
                                          highlights
                                        }
                                      }
                                    });
                                  }}
                                  placeholder="Örn: Clock"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                <Input
                                  value={highlight.title || ''}
                                  onChange={(e) => {
                                    const highlights = [...ctaHighlights];
                                    highlights[index] = { ...highlights[index], title: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        cta_section: {
                                          ...ctaSection,
                                          title: ctaSection.title,
                                          description: ctaSection.description,
                                          primary_button: primaryButton,
                                          secondary_button: secondaryButton,
                                          highlights
                                        }
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                <Textarea
                                  rows={2}
                                  value={highlight.description || ''}
                                  onChange={(e) => {
                                    const highlights = [...ctaHighlights];
                                    highlights[index] = { ...highlights[index], description: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        cta_section: {
                                          ...ctaSection,
                                          title: ctaSection.title,
                                          description: ctaSection.description,
                                          primary_button: primaryButton,
                                          secondary_button: secondaryButton,
                                          highlights
                                        }
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <div className="md:col-span-5 flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const highlights = ctaHighlights.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        cta_section: {
                                          ...ctaSection,
                                          title: ctaSection.title,
                                          description: ctaSection.description,
                                          primary_button: primaryButton,
                                          secondary_button: secondaryButton,
                                          highlights
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPage(null);
                            setPageContent(null);
                          }}
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handleSavePageContent}
                          disabled={saving}
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </Button>
                      </div>
                    </div>
                  );
                })()
              ) : editingPage.path === '/domain' && !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm overflow-x-hidden">
                  <p className="font-medium mb-2">⚠️ Veritabanında İçerik Yok</p>
                  <p className="mb-3 sm:mb-4 break-words">
                    Domain sayfası içeriklerini düzenleyebilmek için migration çalıştırmanız gerekiyor. Bu işlem sayfa
                    verilerini veritabanına aktarır.
                  </p>
                  <Button
                    onClick={() => runMigration(PAGE_SLUG_MAP[editingPage.path] || null)}
                    disabled={migrationRunning}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                  </Button>
                </div>
              ) : editingPage.path === '/domain' && pageContent ? (
                (() => {
                  const additional = pageContent.additional_sections || {};
                  const heroSection = additional.hero || {};
                  const heroBullets = Array.isArray(heroSection.bullets) ? heroSection.bullets : [];
                  const featureSection = additional.feature_cards || {};
                  const featureCards = Array.isArray(featureSection.cards) ? featureSection.cards : [];
                  const infoSections = {
                    what_is_section: additional.what_is_section || {},
                    purpose_section: additional.purpose_section || {},
                    purchase_section: additional.purchase_section || {}
                  };
                  const pricingSection = additional.pricing_section || {};
                  const faqSection = additional.faq_section || {};
                  const faqItems = Array.isArray(faqSection.items) ? faqSection.items : [];

                  const renderInfoSectionForm = (sectionKey, heading, iconPlaceholder = 'Globe') => {
                    const sectionData = infoSections[sectionKey] || {};
                    const highlightCard = sectionData.highlight_card || {};

                    const updateSection = (updates) => {
                      setPageContent({
                        ...pageContent,
                        additional_sections: {
                          ...additional,
                          [sectionKey]: {
                            ...sectionData,
                            ...updates
                          }
                        }
                      });
                    };

                    const updateHighlight = (updates) => {
                      updateSection({
                        highlight_card: {
                          ...highlightCard,
                          ...updates
                        }
                      });
                    };

                    return (
                      <div key={sectionKey} className="rounded-lg border border-gray-200 bg-white p-6 space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">{heading}</h3>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Başlık</label>
                          <Input
                            value={sectionData.title || ''}
                            onChange={(e) => updateSection({ title: e.target.value })}
                            placeholder="Örn: Domain Sorgulama Nedir?"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Açıklama (paragrafları ayırmak için boş satır bırakabilirsiniz)
                          </label>
                          <Textarea
                            rows={5}
                            value={sectionData.description || ''}
                            onChange={(e) => updateSection({ description: e.target.value })}
                          />
                        </div>
                        <div className="grid gap-4 md:grid-cols-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600">Öne Çıkan Kart İkonu</label>
                            <Input
                              value={highlightCard.icon || ''}
                              onChange={(e) => updateHighlight({ icon: e.target.value })}
                              placeholder={`Örn: ${iconPlaceholder}`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600">Öne Çıkan Kart Başlığı</label>
                            <Input
                              value={highlightCard.title || ''}
                              onChange={(e) => updateHighlight({ title: e.target.value })}
                              placeholder="Örn: Anında Sorgulama"
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className="block text-xs font-semibold text-gray-600">Öne Çıkan Kart Açıklaması</label>
                            <Textarea
                              rows={3}
                              value={highlightCard.description || ''}
                              onChange={(e) => updateHighlight({ description: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  };

                  return (
                    <div className="space-y-4 sm:space-y-6 overflow-x-hidden">
                      {/* Hero Section */}
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Image className="h-5 w-5" />
                          Hero Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                            <Input
                              value={pageContent.hero_badge_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                              placeholder="Örn: Domain Hizmetleri"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hero Başlığı</label>
                            <Input
                              value={pageContent.hero_title || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                              placeholder="Örn: Alan Adınızı Güvenle Kaydedin"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Açıklaması</label>
                          <Textarea
                            rows={3}
                            value={pageContent.hero_description || ''}
                            onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                            placeholder="Kısa açıklama metni"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Görseli</label>
                          <Input
                            value={heroSection.image_url || pageContent.hero_image_url || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                hero_image_url: e.target.value,
                                additional_sections: {
                                  ...additional,
                                  hero: {
                                    ...heroSection,
                                    image_url: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="https://..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Vurgusu</label>
                          <Input
                            value={heroSection.highlight_text || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  hero: {
                                    ...heroSection,
                                    highlight_text: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="Örn: Domain sorgulama ile markanızı dijitale taşıyın"
                          />
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">Hero Maddeleri</label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const bullets = [...heroBullets, { icon: 'CheckCircle', text: '' }];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero: {
                                      ...heroSection,
                                      bullets
                                    }
                                  }
                                });
                              }}
                            >
                              Madde Ekle
                            </Button>
                          </div>
                          {heroBullets.length === 0 && (
                            <p className="text-xs text-gray-500">Hero bölümünde gösterilecek maddeler ekleyin.</p>
                          )}
                          <div className="space-y-3">
                            {heroBullets.map((bullet, index) => (
                              <div key={index} className="grid gap-3 md:grid-cols-5 rounded-lg border border-emerald-200 bg-white p-4">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={bullet.icon || ''}
                                    onChange={(e) => {
                                      const bullets = [...heroBullets];
                                      bullets[index] = { ...bullets[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          hero: {
                                            ...heroSection,
                                            bullets
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Shield"
                                  />
                                </div>
                                <div className="md:col-span-4">
                                  <label className="block text-xs font-semibold text-gray-600">Metin</label>
                                  <Input
                                    value={bullet.text || ''}
                                    onChange={(e) => {
                                      const bullets = [...heroBullets];
                                      bullets[index] = { ...bullets[index], text: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          hero: {
                                            ...heroSection,
                                            bullets
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: WHOIS koruması ve güvenli kayıt"
                                  />
                                </div>
                                <div className="md:col-span-5 flex justify-end">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                    onClick={() => {
                                      const bullets = heroBullets.filter((_, idx) => idx !== index);
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          hero: {
                                            ...heroSection,
                                            bullets
                                          }
                                        }
                                      });
                                    }}
                                  >
                                    Sil
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Feature Cards */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <Settings className="h-5 w-5" />
                            Domain Özellik Kartları
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...featureCards, { icon: '', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  feature_cards: {
                                    ...featureSection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={featureSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    feature_cards: {
                                      ...featureSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Input
                              value={featureSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    feature_cards: {
                                      ...featureSection,
                                      subtitle: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {featureCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...featureCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          feature_cards: {
                                            ...featureSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Shield"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...featureCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          feature_cards: {
                                            ...featureSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={2}
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = [...featureCards];
                                      cards[index] = { ...cards[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          feature_cards: {
                                            ...featureSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = featureCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        feature_cards: {
                                          ...featureSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {featureCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz özellik kartı eklenmemiş.</p>
                          )}
                        </div>
                      </div>

                      {renderInfoSectionForm('what_is_section', 'Domain Sorgulama Nedir?', 'Globe')}
                      {renderInfoSectionForm('purpose_section', 'Domain Sorgulama Ne İşe Yarar?', 'Search')}
                      {renderInfoSectionForm('purchase_section', 'Domain Nasıl Satın Alınır?', 'CreditCard')}

                      {/* Pricing Section */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="text-lg font-bold text-gray-900">Fiyatlandırma Başlığı</h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={pricingSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    pricing_section: {
                                      ...pricingSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Alan Adı Kayıt ve Yenileme Ücretleri"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={pricingSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    pricing_section: {
                                      ...pricingSection,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Şeffaf fiyatlandırma ile domain işlemleri"
                            />
                          </div>
                        </div>
                      </div>

                      {/* FAQ Section */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <HelpCircle className="h-5 w-5" />
                            Sıkça Sorulan Sorular
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const items = [...faqItems, { question: '', answer: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  faq_section: {
                                    ...faqSection,
                                    items
                                  }
                                }
                              });
                            }}
                          >
                            Soru Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={faqSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    faq_section: {
                                      ...faqSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Input
                              value={faqSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    faq_section: {
                                      ...faqSection,
                                      subtitle: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {faqItems.map((item, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Soru</label>
                                <Input
                                  value={item.question || ''}
                                  onChange={(e) => {
                                    const items = [...faqItems];
                                    items[index] = { ...items[index], question: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        faq_section: {
                                          ...faqSection,
                                          items
                                        }
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Cevap</label>
                                <Textarea
                                  rows={3}
                                  value={item.answer || ''}
                                  onChange={(e) => {
                                    const items = [...faqItems];
                                    items[index] = { ...items[index], answer: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        faq_section: {
                                          ...faqSection,
                                          items
                                        }
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const items = faqItems.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        faq_section: {
                                          ...faqSection,
                                          items
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Soruyu Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {faqItems.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz soru eklenmemiş.</p>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPage(null);
                            setPageContent(null);
                          }}
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handleSavePageContent}
                          disabled={saving}
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </Button>
                      </div>
                    </div>
                  );
                })()
              ) : editingPage.path === '/alan-adi' && !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm overflow-x-hidden">
                  <p className="font-medium mb-2">⚠️ Veritabanında İçerik Yok</p>
                  <p className="mb-3 sm:mb-4 break-words">
                    Alan Adı sayfası içerikleri veritabanında bulunamadı.
                  </p>
                  <Button
                    onClick={() => handleEdit(editingPage)}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white"
                  >
                    İçeriği Oluştur
                  </Button>
                </div>
              ) : editingPage.path === '/alan-adi' && pageContent ? (
                (() => {
                  const additional = pageContent.additional_sections || {};
                  const heroSection = additional.hero || {};
                  const professionalMgmt = additional.professional_management || {};
                  const profFeatures = Array.isArray(professionalMgmt.features) ? professionalMgmt.features : [];
                  const ownerCapabilities = additional.domain_owner_capabilities || {};
                  const ownerCards = Array.isArray(ownerCapabilities.cards) ? ownerCapabilities.cards : [];
                  const selectionTips = additional.domain_selection_tips || {};
                  const tips = Array.isArray(selectionTips.tips) ? selectionTips.tips : [];

                  return (
                    <div className="space-y-4 sm:space-y-6 overflow-x-hidden">
                      {/* Hero Section */}
                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-6 space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Hero Bölümü</h3>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Başlığı</label>
                          <Input
                            value={heroSection.title || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  hero: { ...heroSection, title: e.target.value }
                                }
                              })
                            }
                            placeholder="Örn: Domain Sorgulama"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Alt Metni</label>
                          <Textarea
                            rows={2}
                            value={heroSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  hero: { ...heroSection, description: e.target.value }
                                }
                              })
                            }
                            placeholder="Örn: 0,99$'dan Başlayan Fiyatlarla Harika Bir Domain Kaydedin!"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Resmi (İsteğe Bağlı)</label>
                          <div className="flex gap-2">
                            <Input
                              value={heroSection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero: { ...heroSection, image_url: e.target.value }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                            <Button
                              type="button"
                              onClick={() => openMediaLibrary('additional_sections.hero.image_url')}
                              variant="outline"
                            >
                              Resim Seç
                            </Button>
                          </div>
                          {heroSection.image_url && (
                            <img src={heroSection.image_url} alt="Hero" className="mt-2 h-32 rounded object-cover" />
                          )}
                        </div>
                      </div>

                      {/* Profesyonel Domain Yönetimi */}
                      <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-6 space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Profesyonel Domain Yönetimi</h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={professionalMgmt.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    professional_management: { ...professionalMgmt, title: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Input
                              value={professionalMgmt.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    professional_management: { ...professionalMgmt, subtitle: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-semibold text-gray-700">Özellikler (6 Kart)</label>
                            <Button
                              size="sm"
                              onClick={() => {
                                const newFeatures = [
                                  ...profFeatures,
                                  { title: '', description: '', icon: 'CheckCircle' }
                                ];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    professional_management: { ...professionalMgmt, features: newFeatures }
                                  }
                                });
                              }}
                            >
                              + Özellik Ekle
                            </Button>
                          </div>
                          {profFeatures.map((feature, idx) => (
                            <div key={idx} className="border rounded p-4 bg-white space-y-2">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-gray-700">Özellik {idx + 1}</span>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    const newFeatures = profFeatures.filter((_, i) => i !== idx);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        professional_management: { ...professionalMgmt, features: newFeatures }
                                      }
                                    });
                                  }}
                                >
                                  Sil
                                </Button>
                              </div>
                              <Input
                                placeholder="İkon (örn: Settings, Shield, Lock)"
                                value={feature.icon || ''}
                                onChange={(e) => {
                                  const newFeatures = [...profFeatures];
                                  newFeatures[idx] = { ...feature, icon: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      professional_management: { ...professionalMgmt, features: newFeatures }
                                    }
                                  });
                                }}
                              />
                              <Input
                                placeholder="Başlık"
                                value={feature.title || ''}
                                onChange={(e) => {
                                  const newFeatures = [...profFeatures];
                                  newFeatures[idx] = { ...feature, title: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      professional_management: { ...professionalMgmt, features: newFeatures }
                                    }
                                  });
                                }}
                              />
                              <Textarea
                                placeholder="Açıklama"
                                rows={2}
                                value={feature.description || ''}
                                onChange={(e) => {
                                  const newFeatures = [...profFeatures];
                                  newFeatures[idx] = { ...feature, description: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      professional_management: { ...professionalMgmt, features: newFeatures }
                                    }
                                  });
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Domain Sahibi Olarak Yapabilecekleriniz */}
                      <div className="rounded-lg border border-purple-200 bg-purple-50 p-6 space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Domain Sahibi Olarak Yapabilecekleriniz</h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={ownerCapabilities.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    domain_owner_capabilities: { ...ownerCapabilities, title: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Input
                              value={ownerCapabilities.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    domain_owner_capabilities: { ...ownerCapabilities, subtitle: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Sol Taraf Görseli (İsteğe Bağlı)</label>
                          <div className="flex gap-2">
                            <Input
                              value={ownerCapabilities.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    domain_owner_capabilities: { ...ownerCapabilities, image_url: e.target.value }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                            <Button
                              type="button"
                              onClick={() => openMediaLibrary('additional_sections.domain_owner_capabilities.image_url')}
                              variant="outline"
                            >
                              Resim Seç
                            </Button>
                          </div>
                          {ownerCapabilities.image_url && (
                            <img src={ownerCapabilities.image_url} alt="Owner" className="mt-2 h-48 rounded object-cover" />
                          )}
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-semibold text-gray-700">Kartlar (4 Kart)</label>
                            <Button
                              size="sm"
                              onClick={() => {
                                const newCards = [
                                  ...ownerCards,
                                  { title: '', description: '', button_text: '', button_link: '' }
                                ];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    domain_owner_capabilities: { ...ownerCapabilities, cards: newCards }
                                  }
                                });
                              }}
                            >
                              + Kart Ekle
                            </Button>
                          </div>
                          {ownerCards.map((card, idx) => (
                            <div key={idx} className="border rounded p-4 bg-white space-y-2">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-gray-700">Kart {idx + 1}</span>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    const newCards = ownerCards.filter((_, i) => i !== idx);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        domain_owner_capabilities: { ...ownerCapabilities, cards: newCards }
                                      }
                                    });
                                  }}
                                >
                                  Sil
                                </Button>
                              </div>
                              <Input
                                placeholder="Başlık"
                                value={card.title || ''}
                                onChange={(e) => {
                                  const newCards = [...ownerCards];
                                  newCards[idx] = { ...card, title: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      domain_owner_capabilities: { ...ownerCapabilities, cards: newCards }
                                    }
                                  });
                                }}
                              />
                              <Textarea
                                placeholder="Açıklama"
                                rows={2}
                                value={card.description || ''}
                                onChange={(e) => {
                                  const newCards = [...ownerCards];
                                  newCards[idx] = { ...card, description: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      domain_owner_capabilities: { ...ownerCapabilities, cards: newCards }
                                    }
                                  });
                                }}
                              />
                              <div className="grid gap-2 md:grid-cols-2">
                                <Input
                                  placeholder="Buton Metni"
                                  value={card.button_text || ''}
                                  onChange={(e) => {
                                    const newCards = [...ownerCards];
                                    newCards[idx] = { ...card, button_text: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        domain_owner_capabilities: { ...ownerCapabilities, cards: newCards }
                                      }
                                    });
                                  }}
                                />
                                <Input
                                  placeholder="Buton Linki"
                                  value={card.button_link || ''}
                                  onChange={(e) => {
                                    const newCards = [...ownerCards];
                                    newCards[idx] = { ...card, button_link: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        domain_owner_capabilities: { ...ownerCapabilities, cards: newCards }
                                      }
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Domain Seçerken Dikkat Etmeniz Gerekenler */}
                      <div className="rounded-lg border border-green-200 bg-green-50 p-6 space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Domain Seçerken Dikkat Etmeniz Gerekenler</h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={selectionTips.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    domain_selection_tips: { ...selectionTips, title: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Input
                              value={selectionTips.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    domain_selection_tips: { ...selectionTips, subtitle: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-semibold text-gray-700">İpuçları (4 Madde)</label>
                            <Button
                              size="sm"
                              onClick={() => {
                                const newTips = [...tips, { title: '', description: '' }];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    domain_selection_tips: { ...selectionTips, tips: newTips }
                                  }
                                });
                              }}
                            >
                              + İpucu Ekle
                            </Button>
                          </div>
                          {tips.map((tip, idx) => (
                            <div key={idx} className="border rounded p-4 bg-white space-y-2">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-gray-700">İpucu {idx + 1}</span>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    const newTips = tips.filter((_, i) => i !== idx);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        domain_selection_tips: { ...selectionTips, tips: newTips }
                                      }
                                    });
                                  }}
                                >
                                  Sil
                                </Button>
                              </div>
                              <Input
                                placeholder="Başlık"
                                value={tip.title || ''}
                                onChange={(e) => {
                                  const newTips = [...tips];
                                  newTips[idx] = { ...tip, title: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      domain_selection_tips: { ...selectionTips, tips: newTips }
                                    }
                                  });
                                }}
                              />
                              <Textarea
                                placeholder="Açıklama"
                                rows={3}
                                value={tip.description || ''}
                                onChange={(e) => {
                                  const newTips = [...tips];
                                  newTips[idx] = { ...tip, description: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      domain_selection_tips: { ...selectionTips, tips: newTips }
                                    }
                                  });
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Save Button */}
                      <div className="flex justify-end pt-4">
                        <Button
                          onClick={handleSavePageContent}
                          disabled={saving}
                          className="bg-blue-600 text-white hover:bg-blue-700"
                        >
                          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </Button>
                      </div>
                    </div>
                  );
                })()
              ) : editingPage.path === '/wordpress-hosting' && !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm overflow-x-hidden">
                  <p className="font-medium mb-2">⚠️ Veritabanında İçerik Yok</p>
                  <p className="mb-3 sm:mb-4 break-words">
                    WordPress Hosting sayfası içeriklerini düzenleyebilmek için migration çalıştırmanız gerekiyor. Bu işlem
                    sayfa verilerini veritabanına aktarır.
                  </p>
                  <Button
                    onClick={() => runMigration(PAGE_SLUG_MAP[editingPage.path] || null)}
                    disabled={migrationRunning}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                  </Button>
                </div>
              ) : editingPage.path === '/wordpress-hosting' && pageContent ? (
                (() => {
                  const additional = pageContent.additional_sections || {};
                  const heroSection = additional.hero || {};
                  const heroBullets = Array.isArray(heroSection.bullets) ? heroSection.bullets : [];
                  const packagesSection = additional.packages_section || {};
                  const featuresSection = additional.features_section || {};
                  const featuresCards = Array.isArray(featuresSection.cards) ? featuresSection.cards : [];
                  const introSection = additional.intro_section || {};
                  const technologySection = additional.technology_section || {};
                  const technologyCards = Array.isArray(technologySection.cards) ? technologySection.cards : [];
                  const controlPanelSection = additional.control_panel_section || {};
                  const controlPanelCards = Array.isArray(controlPanelSection.cards) ? controlPanelSection.cards : [];
                  const securitySection = additional.security_section || {};
                  const securityBlocks = Array.isArray(securitySection.blocks) ? securitySection.blocks : [];
                  const supportSection = additional.support_section || {};
                  const supportCards = Array.isArray(supportSection.cards) ? supportSection.cards : [];
                  const blogSection = additional.blog_section || {};
                  const ctaSection = additional.cta_section || {};
                  const ctaHighlights = Array.isArray(ctaSection.highlights) ? ctaSection.highlights : [];
                  const primaryButton = ctaSection.primary_button || {};
                  const secondaryButton = ctaSection.secondary_button || {};

                  return (
                    <div className="space-y-4 sm:space-y-6 overflow-x-hidden">
                      {/* Hero Section */}
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Image className="h-5 w-5" />
                          Hero Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                            <Input
                              value={pageContent.hero_badge_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                              placeholder="Örn: WordPress Hosting"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hero Başlığı</label>
                            <Input
                              value={pageContent.hero_title || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                              placeholder="Örn: En Hızlı WordPress Hosting"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Açıklama</label>
                          <Textarea
                            rows={3}
                            value={pageContent.hero_description || ''}
                            onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                            placeholder="Kısa açıklama metni"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Görseli</label>
                          <Input
                            value={heroSection.image_url || pageContent.hero_image_url || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                hero_image_url: e.target.value,
                                additional_sections: {
                                  ...additional,
                                  hero: {
                                    ...heroSection,
                                    image_url: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="https://..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Vurgusu</label>
                          <Input
                            value={heroSection.highlight_text || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  hero: {
                                    ...heroSection,
                                    highlight_text: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="Örn: En hızlı WordPress deneyimi"
                          />
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">Hero Maddeleri</label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const bullets = [...heroBullets, { icon: 'CheckCircle', text: '' }];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero: {
                                      ...heroSection,
                                      bullets
                                    }
                                  }
                                });
                              }}
                            >
                              Madde Ekle
                            </Button>
                          </div>
                          {heroBullets.length === 0 && (
                            <p className="text-xs text-gray-500">Hero bölümünde gösterilecek maddeler ekleyin.</p>
                          )}
                          <div className="space-y-3">
                            {heroBullets.map((bullet, index) => (
                              <div key={index} className="grid gap-3 md:grid-cols-5 rounded-lg border border-emerald-200 bg-white p-4">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={bullet.icon || ''}
                                    onChange={(e) => {
                                      const bullets = [...heroBullets];
                                      bullets[index] = { ...bullets[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          hero: {
                                            ...heroSection,
                                            bullets
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: CheckCircle"
                                  />
                                </div>
                                <div className="md:col-span-4">
                                  <label className="block text-xs font-semibold text-gray-600">Metin</label>
                                  <Input
                                    value={bullet.text || ''}
                                    onChange={(e) => {
                                      const bullets = [...heroBullets];
                                      bullets[index] = { ...bullets[index], text: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          hero: {
                                            ...heroSection,
                                            bullets
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Sınırsız Site Barındırma"
                                  />
                                </div>
                                <div className="md:col-span-5 flex justify-end">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                    onClick={() => {
                                      const bullets = heroBullets.filter((_, idx) => idx !== index);
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          hero: {
                                            ...heroSection,
                                            bullets
                                          }
                                        }
                                      });
                                    }}
                                  >
                                    Sil
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Paketler Bölümü */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <FileText className="h-5 w-5" />
                          Paketler Başlıkları
                        </h3>
                        <div className="grid gap-4 md:grid-cols-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={packagesSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    packages_section: {
                                      ...packagesSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: WordPress Hosting Paketleri"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={packagesSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    packages_section: {
                                      ...packagesSection,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Not</label>
                            <Input
                              value={packagesSection.note || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    packages_section: {
                                      ...packagesSection,
                                      note: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: 3 yıllık alımlarda avantajlı fiyatlar"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Ortak Özellikler */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <Settings className="h-5 w-5" />
                            Ortak Özellikler
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...featuresCards, { icon: '', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  features_section: {
                                    ...featuresSection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            Özellik Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={featuresSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    features_section: {
                                      ...featuresSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Tüm Paketlerde Sunulan Ortak Özellikler"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Input
                              value={featuresSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    features_section: {
                                      ...featuresSection,
                                      subtitle: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {featuresCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...featuresCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          features_section: {
                                            ...featuresSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Download"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...featuresCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          features_section: {
                                            ...featuresSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={2}
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = [...featuresCards];
                                      cards[index] = { ...cards[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          features_section: {
                                            ...featuresSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = featuresCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        features_section: {
                                          ...featuresSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {featuresCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz özellik kartı eklenmemiş.</p>
                          )}
                        </div>
                      </div>

                      {/* WordPress Hosting Nedir */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Globe className="h-5 w-5" />
                          WordPress Hosting Nedir?
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={introSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    intro_section: {
                                      ...introSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: WordPress Hosting Nedir?"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Görsel URL</label>
                            <Input
                              value={introSection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    intro_section: {
                                      ...introSection,
                                      image_url: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={4}
                            value={introSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  intro_section: {
                                    ...introSection,
                                    description: e.target.value
                                  }
                                }
                              })
                            }
                          />
                        </div>
                      </div>

                      {/* JetPress Teknolojisi */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <Zap className="h-5 w-5" />
                            JetPress Teknolojisi
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...technologyCards, { icon: '', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  technology_section: {
                                    ...technologySection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={technologySection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    technology_section: {
                                      ...technologySection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Input
                              value={technologySection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    technology_section: {
                                      ...technologySection,
                                      subtitle: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Vurgu Başlığı</label>
                            <Input
                              value={technologySection.highlight_title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    technology_section: {
                                      ...technologySection,
                                      highlight_title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Görsel URL</label>
                            <Input
                              value={technologySection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    technology_section: {
                                      ...technologySection,
                                      image_url: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Vurgu Açıklaması</label>
                          <Textarea
                            rows={3}
                            value={technologySection.highlight_description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  technology_section: {
                                    ...technologySection,
                                    highlight_description: e.target.value
                                  }
                                }
                              })
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Alt Açıklama</label>
                          <Textarea
                            rows={3}
                            value={technologySection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  technology_section: {
                                    ...technologySection,
                                    description: e.target.value
                                  }
                                }
                              })
                            }
                          />
                        </div>
                        <div className="space-y-3">
                          {technologyCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...technologyCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          technology_section: {
                                            ...technologySection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Zap"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...technologyCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          technology_section: {
                                            ...technologySection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={2}
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = [...technologyCards];
                                      cards[index] = { ...cards[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          technology_section: {
                                            ...technologySection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = technologyCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        technology_section: {
                                          ...technologySection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {technologyCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz teknoloji kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      {/* Kontrol Paneli Bölümü */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <Settings className="h-5 w-5" />
                            Kontrol Paneli
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...controlPanelCards, { icon: '', title: '', description: '', subtitle: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  control_panel_section: {
                                    ...controlPanelSection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={controlPanelSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    control_panel_section: {
                                      ...controlPanelSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Görsel URL</label>
                            <Input
                              value={controlPanelSection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    control_panel_section: {
                                      ...controlPanelSection,
                                      image_url: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={3}
                            value={controlPanelSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  control_panel_section: {
                                    ...controlPanelSection,
                                    description: e.target.value
                                  }
                                }
                              })
                            }
                          />
                        </div>
                        <div className="space-y-3">
                          {controlPanelCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 md:grid-cols-4">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...controlPanelCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          control_panel_section: {
                                            ...controlPanelSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Shield"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...controlPanelCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          control_panel_section: {
                                            ...controlPanelSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Alt Başlık</label>
                                  <Input
                                    value={card.subtitle || ''}
                                    onChange={(e) => {
                                      const cards = [...controlPanelCards];
                                      cards[index] = { ...cards[index], subtitle: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          control_panel_section: {
                                            ...controlPanelSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={2}
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = [...controlPanelCards];
                                      cards[index] = { ...cards[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          control_panel_section: {
                                            ...controlPanelSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = controlPanelCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        control_panel_section: {
                                          ...controlPanelSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {controlPanelCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz kontrol paneli kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      {/* Güvenlik ve Güncelleme */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <Shield className="h-5 w-5" />
                            Güvenlik ve Güncelleme
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const blocks = [...securityBlocks, { title: '', description: '', secondary_description: '', cards: [] }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  security_section: {
                                    ...securitySection,
                                    blocks
                                  }
                                }
                              });
                            }}
                          >
                            Blok Ekle
                          </Button>
                        </div>
                        <div className="space-y-3">
                          {securityBlocks.map((block, index) => {
                            const cards = Array.isArray(block.cards) ? block.cards : [];
                            return (
                              <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
                                <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                    <Input
                                      value={block.title || ''}
                                      onChange={(e) => {
                                        const blocks = [...securityBlocks];
                                        blocks[index] = { ...blocks[index], title: e.target.value };
                                        setPageContent({
                                          ...pageContent,
                                          additional_sections: {
                                            ...additional,
                                            security_section: {
                                              ...securitySection,
                                              blocks
                                            }
                                          }
                                        });
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-600">İkinci Başlık</label>
                                    <Input
                                      value={block.subtitle || ''}
                                      onChange={(e) => {
                                        const blocks = [...securityBlocks];
                                        blocks[index] = { ...blocks[index], subtitle: e.target.value };
                                        setPageContent({
                                          ...pageContent,
                                          additional_sections: {
                                            ...additional,
                                            security_section: {
                                              ...securitySection,
                                              blocks
                                            }
                                          }
                                        });
                                      }}
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={3}
                                    value={block.description || ''}
                                    onChange={(e) => {
                                      const blocks = [...securityBlocks];
                                      blocks[index] = { ...blocks[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          security_section: {
                                            ...securitySection,
                                            blocks
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkinci Açıklama</label>
                                  <Textarea
                                    rows={3}
                                    value={block.secondary_description || ''}
                                    onChange={(e) => {
                                      const blocks = [...securityBlocks];
                                      blocks[index] = { ...blocks[index], secondary_description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          security_section: {
                                            ...securitySection,
                                            blocks
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <label className="block text-xs font-semibold text-gray-600">Kartlar</label>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        const blocks = [...securityBlocks];
                                        const cards = [...blocks[index].cards || [], { icon: '', title: '', description: '' }];
                                        blocks[index] = { ...blocks[index], cards };
                                        setPageContent({
                                          ...pageContent,
                                          additional_sections: {
                                            ...additional,
                                            security_section: {
                                              ...securitySection,
                                              blocks
                                            }
                                          }
                                        });
                                      }}
                                    >
                                      Kart Ekle
                                    </Button>
                                  </div>
                                  {cards.length === 0 && (
                                    <p className="text-xs text-gray-500">Henüz kart eklenmemiş.</p>
                                  )}
                                  {cards.map((card, cardIdx) => (
                                    <div key={cardIdx} className="grid gap-3 md:grid-cols-3 rounded-lg border border-gray-200 bg-white p-3">
                                      <div>
                                        <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                        <Input
                                          value={card.icon || ''}
                                          onChange={(e) => {
                                            const blocks = [...securityBlocks];
                                            const cards = [...blocks[index].cards || []];
                                            cards[cardIdx] = { ...cards[cardIdx], icon: e.target.value };
                                            blocks[index] = { ...blocks[index], cards };
                                            setPageContent({
                                              ...pageContent,
                                              additional_sections: {
                                                ...additional,
                                                security_section: {
                                                  ...securitySection,
                                                  blocks
                                                }
                                              }
                                            });
                                          }}
                                          placeholder="Örn: Shield"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                        <Input
                                          value={card.title || ''}
                                          onChange={(e) => {
                                            const blocks = [...securityBlocks];
                                            const cards = [...blocks[index].cards || []];
                                            cards[cardIdx] = { ...cards[cardIdx], title: e.target.value };
                                            blocks[index] = { ...blocks[index], cards };
                                            setPageContent({
                                              ...pageContent,
                                              additional_sections: {
                                                ...additional,
                                                security_section: {
                                                  ...securitySection,
                                                  blocks
                                                }
                                              }
                                            });
                                          }}
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                        <Textarea
                                          rows={2}
                                          value={card.description || ''}
                                          onChange={(e) => {
                                            const blocks = [...securityBlocks];
                                            const cards = [...blocks[index].cards || []];
                                            cards[cardIdx] = { ...cards[cardIdx], description: e.target.value };
                                            blocks[index] = { ...blocks[index], cards };
                                            setPageContent({
                                              ...pageContent,
                                              additional_sections: {
                                                ...additional,
                                                security_section: {
                                                  ...securitySection,
                                                  blocks
                                                }
                                              }
                                            });
                                          }}
                                        />
                                      </div>
                                      <div className="md:col-span-3 flex justify-end">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="border-red-200 text-red-600 hover:bg-red-50"
                                          onClick={() => {
                                            const blocks = [...securityBlocks];
                                            const cards = (blocks[index].cards || []).filter((_, idx) => idx !== cardIdx);
                                            blocks[index] = { ...blocks[index], cards };
                                            setPageContent({
                                              ...pageContent,
                                              additional_sections: {
                                                ...additional,
                                                security_section: {
                                                  ...securitySection,
                                                  blocks
                                                }
                                              }
                                            });
                                          }}
                                        >
                                          Kartı Sil
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <div className="flex justify-end">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                    onClick={() => {
                                      const blocks = securityBlocks.filter((_, idx) => idx !== index);
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          security_section: {
                                            ...securitySection,
                                            blocks
                                          }
                                        }
                                      });
                                    }}
                                  >
                                    Bloğu Sil
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                          {securityBlocks.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz güvenlik bloğu eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      {/* Destek Bölümü */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <Headphones className="h-5 w-5" />
                            Destek Bölümü
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...supportCards, { icon: '', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  support_section: {
                                    ...supportSection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={supportSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    support_section: {
                                      ...supportSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Görsel URL</label>
                            <Input
                              value={supportSection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    support_section: {
                                      ...supportSection,
                                      image_url: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                          <Textarea
                            rows={2}
                            value={supportSection.subtitle || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  support_section: {
                                    ...supportSection,
                                    subtitle: e.target.value
                                  }
                                }
                              })
                            }
                          />
                        </div>
                        <div className="space-y-3">
                          {supportCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...supportCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          support_section: {
                                            ...supportSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Rocket"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...supportCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          support_section: {
                                            ...supportSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={2}
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = [...supportCards];
                                      cards[index] = { ...cards[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          support_section: {
                                            ...supportSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = supportCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        support_section: {
                                          ...supportSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {supportCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz destek kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      {/* Blog Bölümü */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <FileText className="h-5 w-5" />
                          Blog Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={blogSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    blog_section: {
                                      ...blogSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: WordPress ile İlgili Güncel İçerikler"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={blogSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    blog_section: {
                                      ...blogSection,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>

                      {/* CTA Bölümü */}
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <MessageCircle className="h-5 w-5" />
                          CTA Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={ctaSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    cta_section: {
                                      ...ctaSection,
                                      title: e.target.value,
                                      primary_button: primaryButton,
                                      secondary_button: secondaryButton,
                                      highlights: ctaHighlights
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={ctaSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    cta_section: {
                                      ...ctaSection,
                                      description: e.target.value,
                                      primary_button: primaryButton,
                                      secondary_button: secondaryButton,
                                      highlights: ctaHighlights
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>

                        <div className="rounded-lg border border-emerald-200 bg-white p-4 space-y-3">
                          <h4 className="text-sm font-semibold text-gray-900">Birincil Buton</h4>
                          <div className="grid gap-3 md:grid-cols-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Metin</label>
                              <Input
                                value={primaryButton.text || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: { ...primaryButton, text: e.target.value },
                                        secondary_button: secondaryButton,
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Link</label>
                              <Input
                                value={primaryButton.link || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: { ...primaryButton, link: e.target.value },
                                        secondary_button: secondaryButton,
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="/iletisim veya https://"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">İkon</label>
                              <Input
                                value={primaryButton.icon || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: { ...primaryButton, icon: e.target.value },
                                        secondary_button: secondaryButton,
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="Örn: Mail"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="rounded-lg border border-emerald-200 bg-white p-4 space-y-3">
                          <h4 className="text-sm font-semibold text-gray-900">İkincil Buton</h4>
                          <div className="grid gap-3 md:grid-cols-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Metin</label>
                              <Input
                                value={secondaryButton.text || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: primaryButton,
                                        secondary_button: { ...secondaryButton, text: e.target.value },
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Link</label>
                              <Input
                                value={secondaryButton.link || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: primaryButton,
                                        secondary_button: { ...secondaryButton, link: e.target.value },
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="/iletisim veya https://"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">İkon</label>
                              <Input
                                value={secondaryButton.icon || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: primaryButton,
                                        secondary_button: { ...secondaryButton, icon: e.target.value },
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="Örn: MessageCircle"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Aksiyon</label>
                              <Input
                                value={secondaryButton.action || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: primaryButton,
                                        secondary_button: { ...secondaryButton, action: e.target.value },
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="Örn: whatsapp"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Telefon</label>
                              <Input
                                value={secondaryButton.phone || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: primaryButton,
                                        secondary_button: { ...secondaryButton, phone: e.target.value },
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="Örn: 905324404011"
                              />
                            </div>
                            <div className="md:col-span-3">
                              <label className="block text-xs font-semibold text-gray-600">Whatsapp Mesajı</label>
                              <Textarea
                                rows={2}
                                value={secondaryButton.prefill_message || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: primaryButton,
                                        secondary_button: { ...secondaryButton, prefill_message: e.target.value },
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="Merhaba, …"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="block text-xs font-semibold text-gray-600">Öne Çıkan Kartlar</label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const highlights = [...ctaHighlights, { icon: '', title: '', description: '' }];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    cta_section: {
                                      ...ctaSection,
                                      title: ctaSection.title,
                                      description: ctaSection.description,
                                      primary_button: primaryButton,
                                      secondary_button: secondaryButton,
                                      highlights
                                    }
                                  }
                                });
                              }}
                            >
                              Kart Ekle
                            </Button>
                          </div>
                          {ctaHighlights.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz CTA kartı eklenmedi.</p>
                          )}
                          {ctaHighlights.map((highlight, index) => (
                            <div key={index} className="grid gap-2 md:grid-cols-5 rounded-lg border border-emerald-200 bg-white p-3">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                <Input
                                  value={highlight.icon || ''}
                                  onChange={(e) => {
                                    const highlights = [...ctaHighlights];
                                    highlights[index] = { ...highlights[index], icon: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        cta_section: {
                                          ...ctaSection,
                                          title: ctaSection.title,
                                          description: ctaSection.description,
                                          primary_button: primaryButton,
                                          secondary_button: secondaryButton,
                                          highlights
                                        }
                                      }
                                    });
                                  }}
                                  placeholder="Örn: Clock"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                <Input
                                  value={highlight.title || ''}
                                  onChange={(e) => {
                                    const highlights = [...ctaHighlights];
                                    highlights[index] = { ...highlights[index], title: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        cta_section: {
                                          ...ctaSection,
                                          title: ctaSection.title,
                                          description: ctaSection.description,
                                          primary_button: primaryButton,
                                          secondary_button: secondaryButton,
                                          highlights
                                        }
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                <Textarea
                                  rows={2}
                                  value={highlight.description || ''}
                                  onChange={(e) => {
                                    const highlights = [...ctaHighlights];
                                    highlights[index] = { ...highlights[index], description: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        cta_section: {
                                          ...ctaSection,
                                          title: ctaSection.title,
                                          description: ctaSection.description,
                                          primary_button: primaryButton,
                                          secondary_button: secondaryButton,
                                          highlights
                                        }
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <div className="md:col-span-5 flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const highlights = ctaHighlights.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        cta_section: {
                                          ...ctaSection,
                                          title: ctaSection.title,
                                          description: ctaSection.description,
                                          primary_button: primaryButton,
                                          secondary_button: secondaryButton,
                                          highlights
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPage(null);
                            setPageContent(null);
                          }}
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handleSavePageContent}
                          disabled={saving}
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </Button>
                      </div>
                    </div>
                  );
                })()
              ) : editingPage.path === '/bayi-hosting' && pageContent ? (
                (() => {
                  const additional = pageContent.additional_sections || {};
                  const heroAdditional = additional.hero || {};
                  const heroBullets = Array.isArray(heroAdditional.bullets)
                    ? heroAdditional.bullets.map((bullet) =>
                        typeof bullet === 'string'
                          ? { icon: 'CheckCircle', text: bullet }
                          : { icon: bullet.icon || 'CheckCircle', text: bullet.text || '' }
                      )
                    : [];
                  const packagesSection = additional.packages_section || {};
                  const experienceSection = additional.experience_section || {};
                  const experienceCards = Array.isArray(experienceSection.cards)
                    ? experienceSection.cards.map((card) => ({
                        icon: card.icon || '',
                        title: card.title || '',
                        description: card.description || ''
                      }))
                    : [];
                  const highlightsSection = additional.highlights_section || {};
                  const highlightCards = Array.isArray(highlightsSection.cards)
                    ? highlightsSection.cards.map((card) => ({
                        icon: card.icon || '',
                        title: card.title || '',
                        description: card.description || ''
                      }))
                    : [];
                  const managementSection = additional.management_section || {};
                  const managementCards = Array.isArray(managementSection.cards)
                    ? managementSection.cards.map((card) => ({
                        icon: card.icon || '',
                        title: card.title || '',
                        description: card.description || '',
                        items: Array.isArray(card.items)
                          ? card.items.map((item) =>
                              typeof item === 'string'
                                ? { icon: 'CheckCircle', text: item }
                                : { icon: item.icon || 'CheckCircle', text: item.text || '' }
                            )
                          : []
                      }))
                    : [];
                  const ctaSection = additional.cta_section || {};
                  const ctaHighlights = Array.isArray(ctaSection.highlights)
                    ? ctaSection.highlights.map((highlight) => ({
                        icon: highlight.icon || '',
                        title: highlight.title || '',
                        description: highlight.description || ''
                      }))
                    : [];
                  const primaryButton = {
                    text: '',
                    link: '',
                    icon: '',
                    ...(ctaSection.primary_button || {})
                  };
                  const secondaryButton = {
                    text: '',
                    link: '',
                    icon: '',
                    action: '',
                    phone: '',
                    prefill_message: '',
                    ...(ctaSection.secondary_button || {})
                  };

                  return (
                    <div className="space-y-4 sm:space-y-6 overflow-x-hidden">
                      {/* Hero Section */}
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Image className="h-5 w-5" />
                          Hero Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                            <Input
                              value={pageContent.hero_badge_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                              placeholder="Örn: Bayi Hosting"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hero Başlığı</label>
                            <Input
                              value={pageContent.hero_title || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                              placeholder="Örn: Reseller (Bayi) Hosting"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Açıklama</label>
                          <Textarea
                            rows={3}
                            value={pageContent.hero_description || ''}
                            onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                            placeholder="Kısa açıklama metni"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Görseli</label>
                          <Input
                            value={heroAdditional.image_url || pageContent.hero_image_url || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                hero_image_url: e.target.value,
                                additional_sections: {
                                  ...additional,
                                  hero: {
                                    ...heroAdditional,
                                    image_url: e.target.value
                                  }
                                }
                              })
                            }
                            placeholder="https://..."
                          />
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">Vurgu Maddeleri</label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const bullets = [...heroBullets, { icon: 'CheckCircle', text: '' }];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero: {
                                      ...heroAdditional,
                                      bullets
                                    }
                                  }
                                });
                              }}
                            >
                              Madde Ekle
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {heroBullets.map((bullet, index) => (
                              <div key={index} className="grid gap-2 md:grid-cols-5 rounded-lg border border-emerald-200 bg-white p-3">
                                <div className="md:col-span-2">
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={bullet.icon || ''}
                                    onChange={(e) => {
                                      const bullets = heroBullets.map((item, idx) =>
                                        idx === index ? { ...item, icon: e.target.value } : item
                                      );
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          hero: {
                                            ...heroAdditional,
                                            bullets
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: CheckCircle"
                                  />
                                </div>
                                <div className="md:col-span-3">
                                  <label className="block text-xs font-semibold text-gray-600">Metin</label>
                                  <Input
                                    value={bullet.text || ''}
                                    onChange={(e) => {
                                      const bullets = heroBullets.map((item, idx) =>
                                        idx === index ? { ...item, text: e.target.value } : item
                                      );
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          hero: {
                                            ...heroAdditional,
                                            bullets
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: %100 SSD Disk"
                                  />
                                </div>
                                <div className="md:col-span-5 flex justify-end">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                    onClick={() => {
                                      const bullets = heroBullets.filter((_, idx) => idx !== index);
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          hero: {
                                            ...heroAdditional,
                                            bullets
                                          }
                                        }
                                      });
                                    }}
                                  >
                                    Sil
                                  </Button>
                                </div>
                              </div>
                            ))}
                            {heroBullets.length === 0 && (
                              <p className="text-xs text-gray-500">Henüz vurgu maddesi eklenmemiş.</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Paketler Bölümü */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <FileText className="h-5 w-5" />
                          Paketler Bölümü
                        </h3>
                        <div className="grid gap-4 md:grid-cols-3">
                          <div className="md:col-span-1">
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={packagesSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    packages_section: {
                                      ...packagesSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: Bayi Hosting Paketleri"
                            />
                          </div>
                          <div className="md:col-span-1">
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Input
                              value={packagesSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    packages_section: {
                                      ...packagesSection,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Kısa açıklama"
                            />
                          </div>
                          <div className="md:col-span-1">
                            <label className="block text-sm font-medium text-gray-700">Not</label>
                            <Input
                              value={packagesSection.note || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    packages_section: {
                                      ...packagesSection,
                                      note: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="Örn: 3 yıllık alımlarda geçerli fiyatlardır"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Deneyim Kartları */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <Settings className="h-5 w-5" />
                            Deneyim Kartları
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...experienceCards, { icon: '', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  experience_section: {
                                    ...experienceSection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={experienceSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    experience_section: {
                                      ...experienceSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={2}
                              value={experienceSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    experience_section: {
                                      ...experienceSection,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {experienceCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = experienceCards.map((item, idx) =>
                                        idx === index ? { ...item, icon: e.target.value } : item
                                      );
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          experience_section: {
                                            ...experienceSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: HardDrive"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = experienceCards.map((item, idx) =>
                                        idx === index ? { ...item, title: e.target.value } : item
                                      );
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          experience_section: {
                                            ...experienceSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={2}
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = experienceCards.map((item, idx) =>
                                        idx === index ? { ...item, description: e.target.value } : item
                                      );
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          experience_section: {
                                            ...experienceSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = experienceCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        experience_section: {
                                          ...experienceSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {experienceCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz deneyim kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      {/* Öne Çıkan Özellikler */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <Zap className="h-5 w-5" />
                            Öne Çıkan Özellikler
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...highlightCards, { icon: '', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  highlights_section: {
                                    ...highlightsSection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={highlightsSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    highlights_section: {
                                      ...highlightsSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Input
                              value={highlightsSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    highlights_section: {
                                      ...highlightsSection,
                                      subtitle: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {highlightCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = highlightCards.map((item, idx) =>
                                        idx === index ? { ...item, icon: e.target.value } : item
                                      );
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          highlights_section: {
                                            ...highlightsSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Settings"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = highlightCards.map((item, idx) =>
                                        idx === index ? { ...item, title: e.target.value } : item
                                      );
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          highlights_section: {
                                            ...highlightsSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={2}
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = highlightCards.map((item, idx) =>
                                        idx === index ? { ...item, description: e.target.value } : item
                                      );
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          highlights_section: {
                                            ...highlightsSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = highlightCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        highlights_section: {
                                          ...highlightsSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {highlightCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz öne çıkan kart eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      {/* Yönetim Kartları */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <Settings className="h-5 w-5" />
                            Yönetim Kartları
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [
                                ...managementCards,
                                { icon: '', title: '', description: '', items: [] }
                              ];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  management_section: {
                                    ...managementSection,
                                    cards
                                  }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={managementSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    management_section: {
                                      ...managementSection,
                                      title: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={2}
                              value={managementSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    management_section: {
                                      ...managementSection,
                                      description: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {managementCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = managementCards.map((item, idx) =>
                                        idx === index ? { ...item, icon: e.target.value } : item
                                      );
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          management_section: {
                                            ...managementSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Mail"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = managementCards.map((item, idx) =>
                                        idx === index ? { ...item, title: e.target.value } : item
                                      );
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          management_section: {
                                            ...managementSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={2}
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = managementCards.map((item, idx) =>
                                        idx === index ? { ...item, description: e.target.value } : item
                                      );
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          management_section: {
                                            ...managementSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <label className="block text-xs font-semibold text-gray-600">Kart Öğeleri</label>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const cards = managementCards.map((item, idx) =>
                                        idx === index
                                          ? {
                                              ...item,
                                              items: [...(item.items || []), { icon: 'CheckCircle', text: '' }]
                                            }
                                          : item
                                      );
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          management_section: {
                                            ...managementSection,
                                            cards
                                          }
                                        }
                                      });
                                    }}
                                  >
                                    Öğe Ekle
                                  </Button>
                                </div>
                                <div className="space-y-2">
                                  {(card.items || []).map((item, itemIndex) => (
                                    <div key={itemIndex} className="grid gap-2 md:grid-cols-5 rounded-lg border border-gray-200 bg-white p-3">
                                      <div>
                                        <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                        <Input
                                          value={item.icon || ''}
                                          onChange={(e) => {
                                            const cards = managementCards.map((entry, idx) => {
                                              if (idx !== index) return entry;
                                              const items = (entry.items || []).map((inner, iIdx) =>
                                                iIdx === itemIndex ? { ...inner, icon: e.target.value } : inner
                                              );
                                              return { ...entry, items };
                                            });
                                            setPageContent({
                                              ...pageContent,
                                              additional_sections: {
                                                ...additional,
                                                management_section: {
                                                  ...managementSection,
                                                  cards
                                                }
                                              }
                                            });
                                          }}
                                          placeholder="Örn: CheckCircle"
                                        />
                                      </div>
                                      <div className="md:col-span-3">
                                        <label className="block text-xs font-semibold text-gray-600">Metin</label>
                                        <Input
                                          value={item.text || ''}
                                          onChange={(e) => {
                                            const cards = managementCards.map((entry, idx) => {
                                              if (idx !== index) return entry;
                                              const items = (entry.items || []).map((inner, iIdx) =>
                                                iIdx === itemIndex ? { ...inner, text: e.target.value } : inner
                                              );
                                              return { ...entry, items };
                                            });
                                            setPageContent({
                                              ...pageContent,
                                              additional_sections: {
                                                ...additional,
                                                management_section: {
                                                  ...managementSection,
                                                  cards
                                                }
                                              }
                                            });
                                          }}
                                        />
                                      </div>
                                      <div className="md:col-span-1 flex justify-end">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="border-red-200 text-red-600 hover:bg-red-50"
                                          onClick={() => {
                                            const cards = managementCards.map((entry, idx) => {
                                              if (idx !== index) return entry;
                                              const items = (entry.items || []).filter((_, iIdx) => iIdx !== itemIndex);
                                              return { ...entry, items };
                                            });
                                            setPageContent({
                                              ...pageContent,
                                              additional_sections: {
                                                ...additional,
                                                management_section: {
                                                  ...managementSection,
                                                  cards
                                                }
                                              }
                                            });
                                          }}
                                        >
                                          Sil
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                  {(card.items || []).length === 0 && (
                                    <p className="text-xs text-gray-500">Bu kart için henüz öğe eklenmedi.</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = managementCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        management_section: {
                                          ...managementSection,
                                          cards
                                        }
                                      }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {managementCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz yönetim kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      {/* CTA Bölümü */}
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <MessageCircle className="h-5 w-5" />
                          CTA Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={ctaSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    cta_section: {
                                      ...ctaSection,
                                      title: e.target.value,
                                      primary_button: primaryButton,
                                      secondary_button: secondaryButton
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={2}
                              value={ctaSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    cta_section: {
                                      ...ctaSection,
                                      description: e.target.value,
                                      primary_button: primaryButton,
                                      secondary_button: secondaryButton
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>

                        <div className="rounded-lg border border-emerald-200 bg-white p-4 space-y-3">
                          <h4 className="text-sm font-semibold text-gray-900">Birincil Buton</h4>
                          <div className="grid gap-3 md:grid-cols-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Metin</label>
                              <Input
                                value={primaryButton.text || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: { ...primaryButton, text: e.target.value },
                                        secondary_button: secondaryButton,
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Link</label>
                              <Input
                                value={primaryButton.link || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: { ...primaryButton, link: e.target.value },
                                        secondary_button: secondaryButton,
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="/iletisim veya https://"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">İkon</label>
                              <Input
                                value={primaryButton.icon || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: { ...primaryButton, icon: e.target.value },
                                        secondary_button: secondaryButton,
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="Örn: Mail"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="rounded-lg border border-emerald-200 bg-white p-4 space-y-3">
                          <h4 className="text-sm font-semibold text-gray-900">İkincil Buton</h4>
                          <div className="grid gap-3 md:grid-cols-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Metin</label>
                              <Input
                                value={secondaryButton.text || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: primaryButton,
                                        secondary_button: { ...secondaryButton, text: e.target.value },
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Link</label>
                              <Input
                                value={secondaryButton.link || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: primaryButton,
                                        secondary_button: { ...secondaryButton, link: e.target.value },
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="Örn: /iletisim"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">İkon</label>
                              <Input
                                value={secondaryButton.icon || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: primaryButton,
                                        secondary_button: { ...secondaryButton, icon: e.target.value },
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="Örn: MessageCircle"
                              />
                            </div>
                          </div>
                          <div className="grid gap-3 md:grid-cols-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Aksiyon</label>
                              <Input
                                value={secondaryButton.action || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: primaryButton,
                                        secondary_button: { ...secondaryButton, action: e.target.value },
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="Örn: whatsapp"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Telefon</label>
                              <Input
                                value={secondaryButton.phone || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: primaryButton,
                                        secondary_button: { ...secondaryButton, phone: e.target.value },
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                                placeholder="Örn: 90532..."
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600">Hazır Mesaj</label>
                              <Textarea
                                rows={2}
                                value={secondaryButton.prefill_message || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      cta_section: {
                                        ...ctaSection,
                                        title: ctaSection.title,
                                        description: ctaSection.description,
                                        primary_button: primaryButton,
                                        secondary_button: { ...secondaryButton, prefill_message: e.target.value },
                                        highlights: ctaHighlights
                                      }
                                    }
                                  })
                                }
                              />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="block text-xs font-semibold text-gray-600">Öne Çıkan Kartlar</label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const highlights = [...ctaHighlights, { icon: '', title: '', description: '' }];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    cta_section: {
                                      ...ctaSection,
                                      title: ctaSection.title,
                                      description: ctaSection.description,
                                      primary_button: primaryButton,
                                      secondary_button: secondaryButton,
                                      highlights
                                    }
                                  }
                                });
                              }}
                            >
                              Kart Ekle
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {ctaHighlights.map((highlight, index) => (
                              <div key={index} className="grid gap-2 md:grid-cols-5 rounded-lg border border-emerald-200 bg-white p-3">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={highlight.icon || ''}
                                    onChange={(e) => {
                                      const highlights = ctaHighlights.map((item, idx) =>
                                        idx === index ? { ...item, icon: e.target.value } : item
                                      );
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          cta_section: {
                                            ...ctaSection,
                                            title: ctaSection.title,
                                            description: ctaSection.description,
                                            primary_button: primaryButton,
                                            secondary_button: secondaryButton,
                                            highlights
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={highlight.title || ''}
                                    onChange={(e) => {
                                      const highlights = ctaHighlights.map((item, idx) =>
                                        idx === index ? { ...item, title: e.target.value } : item
                                      );
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          cta_section: {
                                            ...ctaSection,
                                            title: ctaSection.title,
                                            description: ctaSection.description,
                                            primary_button: primaryButton,
                                            secondary_button: secondaryButton,
                                            highlights
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div className="md:col-span-3">
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Textarea
                                    rows={2}
                                    value={highlight.description || ''}
                                    onChange={(e) => {
                                      const highlights = ctaHighlights.map((item, idx) =>
                                        idx === index ? { ...item, description: e.target.value } : item
                                      );
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          cta_section: {
                                            ...ctaSection,
                                            title: ctaSection.title,
                                            description: ctaSection.description,
                                            primary_button: primaryButton,
                                            secondary_button: secondaryButton,
                                            highlights
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div className="md:col-span-5 flex justify-end">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                    onClick={() => {
                                      const highlights = ctaHighlights.filter((_, idx) => idx !== index);
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          cta_section: {
                                            ...ctaSection,
                                            title: ctaSection.title,
                                            description: ctaSection.description,
                                            primary_button: primaryButton,
                                            secondary_button: secondaryButton,
                                            highlights
                                          }
                                        }
                                      });
                                    }}
                                  >
                                    Kartı Sil
                                  </Button>
                                </div>
                              </div>
                            ))}
                            {ctaHighlights.length === 0 && (
                              <p className="text-xs text-gray-500">Henüz CTA kartı eklenmedi.</p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPage(null);
                            setPageContent(null);
                          }}
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handleSavePageContent}
                          disabled={saving}
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </Button>
                      </div>
                    </div>
                  );
                })()
              ) : editingPage.path === '/kurumsal-sunucu' && !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm overflow-x-hidden">
                  <p className="font-medium mb-2">⚠️ Veritabanında İçerik Yok</p>
                  <p className="mb-3 sm:mb-4 break-words">
                    Kurumsal Sunucu sayfası içeriklerini düzenleyebilmek için migration çalıştırmanız gerekiyor. Bu işlem sayfa
                    verilerini veritabanına aktarır.
                  </p>
                  <Button
                    onClick={() => runMigration(PAGE_SLUG_MAP[editingPage.path] || null)}
                    disabled={migrationRunning}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                  </Button>
                </div>
              ) : editingPage.path === '/kurumsal-sunucu' && pageContent ? (
                (() => {
                  const additional = pageContent.additional_sections || {};
                  const corpIntroSection = additional.intro_section || {};
                  const corpFeaturesSection = additional.features_section || {};
                  const corpFeatureCards = Array.isArray(corpFeaturesSection.cards) ? corpFeaturesSection.cards : [];
                  const corpStandardSection = additional.standard_section || {};
                  const corpStandardCards = Array.isArray(corpStandardSection.cards) ? corpStandardSection.cards : [];
                  const corpValueProductsSection = additional.value_products_section || {};
                  const corpValueProductCards = Array.isArray(corpValueProductsSection.cards) ? corpValueProductsSection.cards : [];

                  return (
                    <div className="space-y-4 sm:space-y-6 overflow-x-hidden w-full">
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden w-full">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Image className="h-5 w-5" />
                          Hero Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={pageContent.hero_title || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                              placeholder="Örn: Kurumsal Sunucu Kiralama"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge / Vurgu Metni</label>
                            <Input
                              value={pageContent.hero_badge_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                              placeholder="Örn: Tam Kontrol, Kurumsal Performans"
                            />
                          </div>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hero Görseli</label>
                            <Input
                              value={additional.hero?.image_url || pageContent.hero_image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  hero_image_url: e.target.value,
                                  additional_sections: {
                                    ...additional,
                                    hero: { ...(additional.hero || {}), image_url: e.target.value }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">İkincil Vurgu</label>
                            <Input
                              value={additional.hero?.highlight_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero: { ...(additional.hero || {}), highlight_text: e.target.value }
                                  }
                                })
                              }
                              placeholder="Örn: Tam Kontrol, Kurumsal Performans"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-bold text-gray-900">Kurumsal Sunucu Nedir?</h3>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={corpIntroSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    intro_section: { ...corpIntroSection, title: e.target.value }
                                  }
                                })
                              }
                              placeholder="Örn: Kurumsal Sunucu Nedir?"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Görsel URL</label>
                            <Input
                              value={corpIntroSection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    intro_section: { ...corpIntroSection, image_url: e.target.value }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={3}
                            value={corpIntroSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  intro_section: { ...corpIntroSection, description: e.target.value }
                                }
                              })
                            }
                            placeholder="Açıklama metni..."
                          />
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Tek Sunucuda Üstün Performans</h3>
                            <p className="text-sm text-gray-500">Kurumsal performansı öne çıkaran özellik kartlarını yönetin.</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...corpFeatureCards, { icon: '', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  features_section: { ...corpFeaturesSection, cards }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={corpFeaturesSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    features_section: { ...corpFeaturesSection, title: e.target.value }
                                  }
                                })
                              }
                              placeholder="Örn: Tek Sunucuda Üstün Performans"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Input
                              value={corpFeaturesSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    features_section: { ...corpFeaturesSection, subtitle: e.target.value }
                                  }
                                })
                              }
                              placeholder="Örn: Sunucu performansınızı zirveye taşıyacak üstün özellikler"
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {corpFeatureCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...corpFeatureCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, features_section: { ...corpFeaturesSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Cpu"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...corpFeatureCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, features_section: { ...corpFeaturesSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Xeon Silver & Gold İşlemci"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Input
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = [...corpFeatureCards];
                                      cards[index] = { ...cards[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, features_section: { ...corpFeaturesSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Yoğun işlem yükleri için yeni nesil işlemci seçenekleri."
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = corpFeatureCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, features_section: { ...corpFeaturesSection, cards } }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {corpFeatureCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz özellik kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Ortak Özellikler</h3>
                            <p className="text-sm text-gray-500">Tüm Kurumsal Sunucu paketlerinde standart alanlar.</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...corpStandardCards, { icon: '', title: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  standard_section: { ...corpStandardSection, cards }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={corpStandardSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    standard_section: { ...corpStandardSection, title: e.target.value }
                                  }
                                })
                              }
                              placeholder="Örn: Ortak Özellikler"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Input
                              value={corpStandardSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    standard_section: { ...corpStandardSection, subtitle: e.target.value }
                                  }
                                })
                              }
                              placeholder="Örn: Tüm Kurumsal Sunucu paketlerinde standart"
                            />
                          </div>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Görsel URL</label>
                            <Input
                              value={corpStandardSection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    standard_section: { ...corpStandardSection, image_url: e.target.value }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {corpStandardCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...corpStandardCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, standard_section: { ...corpStandardSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Shield"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...corpStandardCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, standard_section: { ...corpStandardSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: %99,9 Uptime"
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = corpStandardCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, standard_section: { ...corpStandardSection, cards } }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {corpStandardCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz standart özellik kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">İşinize Değer Katacak Ürünler</h3>
                            <p className="text-sm text-gray-500">Kurumsal’a değer katan tamamlayıcı hizmet kartlarını yönetin.</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...corpValueProductCards, { title: '', description: '', image_url: '', link: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  value_products_section: { ...corpValueProductsSection, cards }
                                }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={corpValueProductsSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    value_products_section: { ...corpValueProductsSection, title: e.target.value }
                                  }
                                })
                              }
                              placeholder="Örn: İşinize Değer Katacak Ürünler"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Textarea
                              rows={3}
                              value={corpValueProductsSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    value_products_section: { ...corpValueProductsSection, subtitle: e.target.value }
                                  }
                                })
                              }
                              placeholder="Örn: Kurumsal sunucunuzla birlikte tercih edebileceğiniz ürünler"
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {corpValueProductCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Kart Başlığı</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...corpValueProductCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, value_products_section: { ...corpValueProductsSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: SSL Sertifikası"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Görsel URL</label>
                                  <Input
                                    value={card.image_url || ''}
                                    onChange={(e) => {
                                      const cards = [...corpValueProductCards];
                                      cards[index] = { ...cards[index], image_url: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, value_products_section: { ...corpValueProductsSection, cards } }
                                      });
                                    }}
                                    placeholder="https://..."
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                <Textarea
                                  rows={2}
                                  value={card.description || ''}
                                  onChange={(e) => {
                                    const cards = [...corpValueProductCards];
                                    cards[index] = { ...cards[index], description: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, value_products_section: { ...corpValueProductsSection, cards } }
                                    });
                                  }}
                                  placeholder="Örn: Google’da yükselin, güvenliğinizi garanti altına alın."
                                />
                              </div>
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Link</label>
                                  <Input
                                    value={card.link || ''}
                                    onChange={(e) => {
                                      const cards = [...corpValueProductCards];
                                      cards[index] = { ...cards[index], link: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, value_products_section: { ...corpValueProductsSection, cards } }
                                      });
                                    }}
                                    placeholder="/ssl/ssl-sertifikasi"
                                  />
                                </div>
                                <div className="flex items-end justify-end">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                    onClick={() => {
                                      const cards = corpValueProductCards.filter((_, idx) => idx !== index);
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, value_products_section: { ...corpValueProductsSection, cards } }
                                      });
                                    }}
                                  >
                                    Kartı Sil
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                          {corpValueProductCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz tamamlayıcı ürün kartı eklenmedi.</p>
                          )}
                        </div>

                      </div>

                      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPage(null);
                            setPageContent(null);
                          }}
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handleSavePageContent}
                          disabled={saving}
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </Button>
                      </div>
                    </div>
                  );
                })()
              ) : editingPage.path === '/ssl-sertifikasi' && !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm overflow-x-hidden">
                  <p className="font-medium mb-2">⚠️ Veritabanında İçerik Yok</p>
                  <p className="mb-3 sm:mb-4 break-words">
                    SSL Sertifikası sayfası içeriklerini düzenleyebilmek için migration çalıştırmanız gerekiyor. Bu işlem sayfa
                    verilerini veritabanına aktarır.
                  </p>
                  <Button
                    onClick={() => runMigration(PAGE_SLUG_MAP[editingPage.path] || null)}
                    disabled={migrationRunning}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                  </Button>
                </div>
              ) : editingPage.path === '/ssl-sertifikasi' && pageContent ? (
                (() => {
                  const additional = pageContent.additional_sections || {};
                  const heroSection = additional.hero || {};
                  const whySection = additional.why_ssl_section || {};
                  const whyCards = Array.isArray(whySection.cards) ? whySection.cards : [];
                  const seoGainSection = additional.seo_gain_section || {};
                  const sslTypesSection = additional.ssl_types_section || {};
                  const sslTypeCards = Array.isArray(sslTypesSection.cards) ? sslTypesSection.cards : [];
                  const whichSection = additional.which_ssl_section || {};
                  const secureSection = additional.is_site_secure_section || {};
                  const benefitsSection = additional.ssl_benefits_section || {};
                  const benefitCards = Array.isArray(benefitsSection.cards) ? benefitsSection.cards : [];
                  const valueProductsSection = additional.value_products_section || {};
                  const valueProductCards = Array.isArray(valueProductsSection.cards) ? valueProductsSection.cards : [];

                  return (
                    <div className="space-y-4 sm:space-y-6 overflow-x-hidden w-full">
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden w-full">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Image className="h-5 w-5" />
                          Hero Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={pageContent.hero_title || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                              placeholder="Örn: SSL Sertifikası ile Güven ve SEO"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge / Vurgu Metni</label>
                            <Input
                              value={pageContent.hero_badge_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                              placeholder="Örn: SSL Sertifikası"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Açıklaması</label>
                          <Textarea
                            rows={3}
                            value={pageContent.hero_description || ''}
                            onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                            placeholder="Ziyaretçilerinize güven verin, Google’da yükselin..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Görseli</label>
                          <div className="grid gap-2 md:grid-cols-2">
                            <Input
                              value={pageContent.hero_image_url || heroSection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  hero_image_url: e.target.value,
                                  additional_sections: {
                                    ...additional,
                                    hero: {
                                      ...heroSection,
                                      image_url: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                            <MediaSelector
                              label="Medya Kütüphanesinden Seç"
                              onSelect={(url) =>
                                setPageContent({
                                  ...pageContent,
                                  hero_image_url: url,
                                  additional_sections: {
                                    ...additional,
                                    hero: {
                                      ...heroSection,
                                      image_url: url
                                    }
                                  }
                                })
                              }
                              buttonText="Görsel Seç"
                            />
                          </div>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hero Görseli</label>
                            <Input
                              value={heroSection.image_url || pageContent.hero_image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  hero_image_url: e.target.value,
                                  additional_sections: {
                                    ...additional,
                                    hero: { ...heroSection, image_url: e.target.value }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">İkincil Vurgu</label>
                            <Input
                              value={heroSection.highlight_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero: { ...heroSection, highlight_text: e.target.value }
                                  }
                                })
                              }
                              placeholder="Örn: Güvenli Bağlantı, Daha İyi Sıralama"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Neden SSL Sertifikası Almalıyım?</h3>
                            <p className="text-sm text-gray-500">Kullanıcı güveni ve veri güvenliği için zorunludur</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...whyCards, { icon: '', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, why_ssl_section: { ...whySection, cards } }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={whySection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, why_ssl_section: { ...whySection, title: e.target.value } }
                                })
                              }
                              placeholder="Neden SSL Sertifikası Almalıyım?"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Input
                              value={whySection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, why_ssl_section: { ...whySection, subtitle: e.target.value } }
                                })
                              }
                              placeholder="Kullanıcı güveni ve veri güvenliği için zorunludur"
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {whyCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...whyCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, why_ssl_section: { ...whySection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: ShieldCheck"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...whyCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, why_ssl_section: { ...whySection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Veri Güvenliği"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Input
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = [...whyCards];
                                      cards[index] = { ...cards[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, why_ssl_section: { ...whySection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Veri trafiği şifrelenir."
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = whyCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, why_ssl_section: { ...whySection, cards } }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {whyCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz madde eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-bold text-gray-900">SSL Sertifikası ile Google'da Yükselin!</h3>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={seoGainSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, seo_gain_section: { ...seoGainSection, title: e.target.value } }
                                })
                              }
                              placeholder="SSL Sertifikası ile Google'da Yükselin!"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={3}
                            value={seoGainSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, seo_gain_section: { ...seoGainSection, description: e.target.value } }
                              })
                            }
                            placeholder="HTTPS, Google tarafından bir sıralama sinyalidir..."
                          />
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Sol Görsel (image_left)</label>
                            <div className="grid gap-2 md:grid-cols-2">
                              <Input
                                value={seoGainSection.image_left || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      seo_gain_section: {
                                        ...seoGainSection,
                                        image_left: e.target.value
                                      }
                                    }
                                  })
                                }
                                placeholder="https://..."
                              />
                              <MediaSelector
                                label="Medya Seç"
                                onSelect={(url) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      seo_gain_section: {
                                        ...seoGainSection,
                                        image_left: url
                                      }
                                    }
                                  })
                                }
                                buttonText="Seç"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Sağ Görsel (image_right)</label>
                            <div className="grid gap-2 md:grid-cols-2">
                              <Input
                                value={seoGainSection.image_right || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      seo_gain_section: {
                                        ...seoGainSection,
                                        image_right: e.target.value
                                      }
                                    }
                                  })
                                }
                                placeholder="https://..."
                              />
                              <MediaSelector
                                label="Medya Seç"
                                onSelect={(url) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      seo_gain_section: {
                                        ...seoGainSection,
                                        image_right: url
                                      }
                                    }
                                  })
                                }
                                buttonText="Seç"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">SSL Sertifikası Türleri</h3>
                            <p className="text-sm text-gray-500">DV, OV ve EV türleri arasından seçim yapın</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...sslTypeCards, { title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, ssl_types_section: { ...sslTypesSection, cards } }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={sslTypesSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, ssl_types_section: { ...sslTypesSection, title: e.target.value } }
                                })
                              }
                              placeholder="SSL Sertifikası Türleri"
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {sslTypeCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...sslTypeCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, ssl_types_section: { ...sslTypesSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Domain Validation (DV)"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Input
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = [...sslTypeCards];
                                      cards[index] = { ...cards[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, ssl_types_section: { ...sslTypesSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Hızlı kurulum, temel doğrulama..."
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = sslTypeCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, ssl_types_section: { ...sslTypesSection, cards } }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {sslTypeCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz tür kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-bold text-gray-900">Hangi SSL Sertifikasını Almalıyım?</h3>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={whichSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, which_ssl_section: { ...whichSection, title: e.target.value } }
                                })
                              }
                              placeholder="Hangi SSL Sertifikasını Almalıyım?"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={3}
                            value={whichSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, which_ssl_section: { ...whichSection, description: e.target.value } }
                              })
                            }
                            placeholder="İhtiyacınıza göre DV, OV veya EV sertifikaları..."
                          />
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-bold text-gray-900">Web Sitem Güvende mi?</h3>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={secureSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, is_site_secure_section: { ...secureSection, title: e.target.value } }
                                })
                              }
                              placeholder="Web Sitem Güvende mi?"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={3}
                            value={secureSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, is_site_secure_section: { ...secureSection, description: e.target.value } }
                              })
                            }
                            placeholder="Tarayıcı adres çubuğundaki kilit simgesi..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Görsel</label>
                          <div className="grid gap-2 md:grid-cols-2">
                            <Input
                              value={secureSection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    is_site_secure_section: {
                                      ...secureSection,
                                      image_url: e.target.value
                                    }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                            <MediaSelector
                              label="Medya Seç"
                              onSelect={(url) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    is_site_secure_section: {
                                      ...secureSection,
                                      image_url: url
                                    }
                                  }
                                })
                              }
                              buttonText="Seç"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">SSL Sertifikası Neler Sağlar?</h3>
                            <p className="text-sm text-gray-500">Faydaları kartlar halinde listeleyin</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...benefitCards, { icon: '', title: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, ssl_benefits_section: { ...benefitsSection, cards } }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={benefitsSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, ssl_benefits_section: { ...benefitsSection, title: e.target.value } }
                                })
                              }
                              placeholder="SSL Sertifikası Neler Sağlar?"
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {benefitCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...benefitCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, ssl_benefits_section: { ...benefitsSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: ShieldCheck"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...benefitCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, ssl_benefits_section: { ...benefitsSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Şifreli İletişim"
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = benefitCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, ssl_benefits_section: { ...benefitsSection, cards } }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {benefitCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz fayda kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">İşinize Değer Katacak Ürünler</h3>
                            <p className="text-sm text-gray-500">SSL ile birlikte önerilen hizmetler</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...valueProductCards, { title: '', description: '', image_url: '', link: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, value_products_section: { ...valueProductsSection, cards } }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={valueProductsSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, value_products_section: { ...valueProductsSection, title: e.target.value } }
                                })
                              }
                              placeholder="İşinize Değer Katacak Ürünler"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Textarea
                              rows={3}
                              value={valueProductsSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, value_products_section: { ...valueProductsSection, subtitle: e.target.value } }
                                })
                              }
                              placeholder="SSL ile birlikte önerilen hizmetler"
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {valueProductCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Kart Başlığı</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...valueProductCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, value_products_section: { ...valueProductsSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Premium Destek"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Görsel URL</label>
                                  <Input
                                    value={card.image_url || ''}
                                    onChange={(e) => {
                                      const cards = [...valueProductCards];
                                      cards[index] = { ...cards[index], image_url: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, value_products_section: { ...valueProductsSection, cards } }
                                      });
                                    }}
                                    placeholder="https://..."
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                <Textarea
                                  rows={2}
                                  value={card.description || ''}
                                  onChange={(e) => {
                                    const cards = [...valueProductCards];
                                    cards[index] = { ...cards[index], description: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, value_products_section: { ...valueProductsSection, cards } }
                                    });
                                  }}
                                  placeholder="Örn: Kurulum ve yapılandırma için uzman desteği."
                                />
                              </div>
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Link</label>
                                  <Input
                                    value={card.link || ''}
                                    onChange={(e) => {
                                      const cards = [...valueProductCards];
                                      cards[index] = { ...cards[index], link: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, value_products_section: { ...valueProductsSection, cards } }
                                      });
                                    }}
                                    placeholder="/ssl/ssl-sertifikasi"
                                  />
                                </div>
                                <div className="flex items-end justify-end">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                    onClick={() => {
                                      const cards = valueProductCards.filter((_, idx) => idx !== index);
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, value_products_section: { ...valueProductsSection, cards } }
                                      });
                                    }}
                                  >
                                    Kartı Sil
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                          {valueProductCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz tamamlayıcı ürün kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPage(null);
                            setPageContent(null);
                          }}
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handleSavePageContent}
                          disabled={saving}
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </Button>
                      </div>
                    </div>
                  );
                })()
              ) : editingPage.path === '/marka-tescil-sorgulama' && !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm overflow-x-hidden">
                  <p className="font-medium mb-2">⚠️ Veritabanında İçerik Yok</p>
                  <p className="mb-3 sm:mb-4 break-words">
                    Marka Tescil Sorgulama sayfası içeriklerini düzenleyebilmek için migration çalıştırmanız gerekiyor. Bu işlem sayfa
                    verilerini veritabanına aktarır.
                  </p>
                  <Button
                    onClick={() => runMigration(PAGE_SLUG_MAP[editingPage.path] || null)}
                    disabled={migrationRunning}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                  </Button>
                </div>
              ) : editingPage.path === '/marka-tescil-sorgulama' && pageContent ? (
                (() => {
                  const additional = pageContent.additional_sections || {};
                  const heroSection = additional.hero || {};
                  const whatIsSection = additional.what_is_trademark_section || {};
                  const searchSection = additional.trademark_search_section || {};
                  const searchBullets = Array.isArray(searchSection.bullets) ? searchSection.bullets : [];
                  const processSection = additional.application_process_section || {};
                  const processSteps = Array.isArray(processSection.steps) ? processSection.steps : [];
                  const advantagesSection = additional.advantages_section || {};
                  const advantageCards = Array.isArray(advantagesSection.cards) ? advantagesSection.cards : [];
                  const ctaSection = additional.cta_section || {};

                  return (
                    <div className="space-y-4 sm:space-y-6 overflow-x-hidden w-full">
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden w-full">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Image className="h-5 w-5" />
                          Hero Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={pageContent.hero_title || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                              placeholder="Örn: Marka Tescil Sorgulama"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge / Vurgu Metni</label>
                            <Input
                              value={pageContent.hero_badge_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                              placeholder="Örn: Marka Tescil"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Açıklaması</label>
                          <Textarea
                            rows={3}
                            value={pageContent.hero_description || ''}
                            onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                            placeholder="Marka adınızı hızlıca sorgulayın, başvurunuzu uzman ekiple güvenle yönetin."
                          />
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hero Görseli</label>
                            <Input
                              value={heroSection.image_url || pageContent.hero_image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  hero_image_url: e.target.value,
                                  additional_sections: {
                                    ...additional,
                                    hero: { ...heroSection, image_url: e.target.value }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">İkincil Vurgu</label>
                            <Input
                              value={heroSection.highlight_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero: { ...heroSection, highlight_text: e.target.value }
                                  }
                                })
                              }
                              placeholder="Örn: Uzman ekiple hızlı başvuru ve takip"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-bold text-gray-900">Marka Tescili Nedir?</h3>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={whatIsSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, what_is_trademark_section: { ...whatIsSection, title: e.target.value } }
                                })
                              }
                              placeholder="Marka Tescili Nedir?"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Görsel URL</label>
                            <Input
                              value={whatIsSection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, what_is_trademark_section: { ...whatIsSection, image_url: e.target.value } }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={4}
                            value={whatIsSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, what_is_trademark_section: { ...whatIsSection, description: e.target.value } }
                              })
                            }
                            placeholder="Açıklama metni..."
                          />
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Marka Tescil Sorgulama</h3>
                            <p className="text-sm text-gray-500">Başvuru öncesi benzerlik taraması ile riskleri azaltın</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const bullets = [...searchBullets, { icon: '', text: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, trademark_search_section: { ...searchSection, bullets } }
                              });
                            }}
                          >
                            Madde Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={searchSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, trademark_search_section: { ...searchSection, title: e.target.value } }
                                })
                              }
                              placeholder="Marka Tescil Sorgulama"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Input
                              value={searchSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, trademark_search_section: { ...searchSection, subtitle: e.target.value } }
                                })
                              }
                              placeholder="Başvuru öncesi benzerlik taraması..."
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {searchBullets.map((b, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={b.icon || ''}
                                    onChange={(e) => {
                                      const bullets = [...searchBullets];
                                      bullets[index] = { ...bullets[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, trademark_search_section: { ...searchSection, bullets } }
                                      });
                                    }}
                                    placeholder="Örn: Search"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Metin</label>
                                  <Input
                                    value={b.text || ''}
                                    onChange={(e) => {
                                      const bullets = [...searchBullets];
                                      bullets[index] = { ...bullets[index], text: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, trademark_search_section: { ...searchSection, bullets } }
                                      });
                                    }}
                                    placeholder="Örn: Detaylı benzerlik analizi"
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const bullets = searchBullets.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, trademark_search_section: { ...searchSection, bullets } }
                                    });
                                  }}
                                >
                                  Maddeyi Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {searchBullets.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz madde eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Başvuru ve Takip Süreci</h3>
                            <p className="text-sm text-gray-500">Süreci adım adım yönetin</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const steps = [...processSteps, { title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, application_process_section: { ...processSection, steps } }
                              });
                            }}
                          >
                            Adım Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={processSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, application_process_section: { ...processSection, title: e.target.value } }
                                })
                              }
                              placeholder="Başvuru ve Takip Süreci"
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {processSteps.map((s, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Adım Başlığı</label>
                                  <Input
                                    value={s.title || ''}
                                    onChange={(e) => {
                                      const steps = [...processSteps];
                                      steps[index] = { ...steps[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, application_process_section: { ...processSection, steps } }
                                      });
                                    }}
                                    placeholder="Örn: Ön Araştırma"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Input
                                    value={s.description || ''}
                                    onChange={(e) => {
                                      const steps = [...processSteps];
                                      steps[index] = { ...steps[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, application_process_section: { ...processSection, steps } }
                                      });
                                    }}
                                    placeholder="Örn: Benzer marka ve sınıf taraması yapılır."
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const steps = processSteps.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, application_process_section: { ...processSection, steps } }
                                    });
                                  }}
                                >
                                  Adımı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {processSteps.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz adım eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Marka Tescili Avantajları</h3>
                            <p className="text-sm text-gray-500">Faydaları kartlar halinde listeleyin</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...advantageCards, { icon: '', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, advantages_section: { ...advantagesSection, cards } }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={advantagesSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, advantages_section: { ...advantagesSection, title: e.target.value } }
                                })
                              }
                              placeholder="Marka Tescili Avantajları"
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {advantageCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...advantageCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, advantages_section: { ...advantagesSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Shield"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...advantageCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, advantages_section: { ...advantagesSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Yasal Koruma"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Input
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = [...advantageCards];
                                      cards[index] = { ...cards[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, advantages_section: { ...advantagesSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Markanızı taklitlere karşı korur."
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = advantageCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, advantages_section: { ...advantagesSection, cards } }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {advantageCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz avantaj kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-bold text-gray-900">Başlamaya Hazır mısın?</h3>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={ctaSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, cta_section: { ...ctaSection, title: e.target.value } }
                                })
                              }
                              placeholder="Başlamaya Hazır mısın?"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Input
                              value={ctaSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, cta_section: { ...ctaSection, description: e.target.value } }
                                })
                              }
                              placeholder="Kısa açıklama..."
                            />
                          </div>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Buton Metni</label>
                            <Input
                              value={ctaSection.button_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, cta_section: { ...ctaSection, button_text: e.target.value } }
                                })
                              }
                              placeholder="Örn: Uzmanla Görüş"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Buton Linki</label>
                            <Input
                              value={ctaSection.button_link || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, cta_section: { ...ctaSection, button_link: e.target.value } }
                                })
                              }
                              placeholder="/iletisim"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPage(null);
                            setPageContent(null);
                          }}
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handleSavePageContent}
                          disabled={saving}
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </Button>
                      </div>
                    </div>
                  );
                })()
              ) : editingPage.path === '/kolay-site' && !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm overflow-x-hidden">
                  <p className="font-medium mb-2">⚠️ Veritabanında İçerik Yok</p>
                  <p className="mb-3 sm:mb-4 break-words">
                    Kolay Site sayfası içeriklerini düzenleyebilmek için migration çalıştırmanız gerekiyor. Bu işlem sayfa
                    verilerini veritabanına aktarır.
                  </p>
                  <Button
                    onClick={() => runMigration(PAGE_SLUG_MAP[editingPage.path] || null)}
                    disabled={migrationRunning}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                  </Button>
                </div>
              ) : editingPage.path === '/kolay-site' && pageContent ? (
                (() => {
                  const additional = pageContent.additional_sections || {};
                  const heroSection = additional.hero || {};
                  const easyBuildSection = additional.easy_build_section || {};
                  const designsSection = additional.designs_section || {};
                  const designCards = Array.isArray(designsSection.cards) ? designsSection.cards : [];
                  const blogSocialSection = additional.blog_social_section || {};
                  const aiStartSection = additional.ai_start_section || {};
                  const stockPhotosSection = additional.stock_photos_section || {};
                  const seoAnalyticsSection = additional.seo_analytics_section || {};
                  const ctaSection = additional.cta_section || {};
                  const valueProductsSection = additional.value_products_section || {};
                  const valueProductCards = Array.isArray(valueProductsSection.cards) ? valueProductsSection.cards : [];
                  const uniqueFeaturesSection = additional.unique_features_section || {};
                  const uniqueFeatureCards = Array.isArray(uniqueFeaturesSection.cards) ? uniqueFeaturesSection.cards : [];

                  return (
                    <div className="space-y-4 sm:space-y-6 overflow-x-hidden w-full">
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden w-full">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Image className="h-5 w-5" />
                          Hero Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={pageContent.hero_title || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                              placeholder="Örn: Kolay Site ile Hızla Web Sitenizi Kurun"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge / Vurgu Metni</label>
                            <Input
                              value={pageContent.hero_badge_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                              placeholder="Örn: Kolay Site"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Açıklaması</label>
                          <Textarea
                            rows={3}
                            value={pageContent.hero_description || ''}
                            onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                            placeholder="Hazır şablonlar, sürükle-bırak düzenleme ve güçlü araçlarla dakikalar içinde web sitesi oluşturun."
                          />
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hero Görseli</label>
                            <Input
                              value={heroSection.image_url || pageContent.hero_image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  hero_image_url: e.target.value,
                                  additional_sections: {
                                    ...additional,
                                    hero: { ...heroSection, image_url: e.target.value }
                                  }
                                })
                              }
                              placeholder="https://..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">İkincil Vurgu</label>
                            <Input
                              value={heroSection.highlight_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero: { ...heroSection, highlight_text: e.target.value }
                                  }
                                })
                              }
                              placeholder="Örn: Sürükle-Bırak, Hazır Şablonlar, Anında Yayın"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="text-lg font-bold text-gray-900">Kolay Site ile Web Sitesi Oluşturmak Çok Kolay</h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={easyBuildSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, easy_build_section: { ...easyBuildSection, title: e.target.value } }
                                })
                              }
                              placeholder="Kolay Site ile Web Sitesi Oluşturmak Çok Kolay"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={3}
                            value={easyBuildSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, easy_build_section: { ...easyBuildSection, description: e.target.value } }
                              })
                            }
                            placeholder="Kod bilmeden, sürükle-bırak editörle..."
                          />
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Yüzlerce Profesyonel Web Tasarımı</h3>
                            <p className="text-sm text-gray-500">Sektörünüze uygun hazır şablonlar</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...designCards, { title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, designs_section: { ...designsSection, cards } }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={designsSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, designs_section: { ...designsSection, title: e.target.value } }
                                })
                              }
                              placeholder="Yüzlerce Profesyonel Web Tasarımı"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Input
                              value={designsSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, designs_section: { ...designsSection, subtitle: e.target.value } }
                                })
                              }
                              placeholder="Sektörünüze uygun hazır şablonlar"
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {designCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Kart Başlığı</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...designCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, designs_section: { ...designsSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Kurumsal"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Input
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = [...designCards];
                                      cards[index] = { ...cards[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, designs_section: { ...designsSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Şirketiniz için modern arayüzler."
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = designCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, designs_section: { ...designsSection, cards } }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {designCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz tasarım kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      {[
                        { key: 'blog_social_section', obj: blogSocialSection, title: 'Blog Sayfası Oluşturma ve Sosyal Medya Desteği' },
                        { key: 'ai_start_section', obj: aiStartSection, title: 'Yapay Zeka Desteği ile Hızlı Başlangıç' },
                        { key: 'stock_photos_section', obj: stockPhotosSection, title: 'Ücretsiz Stok Görsel Kütüphanesi' },
                        { key: 'seo_analytics_section', obj: seoAnalyticsSection, title: 'SEO Araçları ve Analitik' }
                      ].map((section, idx) => (
                        <div key={idx} className="rounded-lg border border-gray-200 bg-white p-6 space-y-4">
                          <h3 className="text-lg font-bold text-gray-900">{section.title}</h3>
                          <div className="grid gap-4 md:grid-cols-1">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Başlık</label>
                              <Input
                                value={section.obj.title || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      [section.key]: { ...section.obj, title: e.target.value }
                                    }
                                  })
                                }
                                placeholder={section.title}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Textarea
                              rows={3}
                              value={section.obj.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    [section.key]: { ...section.obj, description: e.target.value }
                                  }
                                })
                              }
                              placeholder="Açıklama metni..."
                            />
                          </div>
                        </div>
                      ))}

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Web Sitenize Değer Katacak Ürünler</h3>
                            <p className="text-sm text-gray-500">Kolay Site ile birlikte önerilen servisler</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...valueProductCards, { title: '', description: '', image_url: '', link: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, value_products_section: { ...valueProductsSection, cards } }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={valueProductsSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, value_products_section: { ...valueProductsSection, title: e.target.value } }
                                })
                              }
                              placeholder="Web Sitenize Değer Katacak Ürünler"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt Başlık</label>
                            <Textarea
                              rows={3}
                              value={valueProductsSection.subtitle || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, value_products_section: { ...valueProductsSection, subtitle: e.target.value } }
                                })
                              }
                              placeholder="Kolay Site ile birlikte önerilen servisler"
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {valueProductCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Kart Başlığı</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...valueProductCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, value_products_section: { ...valueProductsSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Alan Adı"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Görsel URL</label>
                                  <Input
                                    value={card.image_url || ''}
                                    onChange={(e) => {
                                      const cards = [...valueProductCards];
                                      cards[index] = { ...cards[index], image_url: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, value_products_section: { ...valueProductsSection, cards } }
                                      });
                                    }}
                                    placeholder="https://..."
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                <Textarea
                                  rows={2}
                                  value={card.description || ''}
                                  onChange={(e) => {
                                    const cards = [...valueProductCards];
                                    cards[index] = { ...cards[index], description: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, value_products_section: { ...valueProductsSection, cards } }
                                    });
                                  }}
                                  placeholder="Örn: Markanıza uygun alan adını seçin."
                                />
                              </div>
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Link</label>
                                  <Input
                                    value={card.link || ''}
                                    onChange={(e) => {
                                      const cards = [...valueProductCards];
                                      cards[index] = { ...cards[index], link: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, value_products_section: { ...valueProductsSection, cards } }
                                      });
                                    }}
                                    placeholder="/domain"
                                  />
                                </div>
                                <div className="flex items-end justify-end">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                    onClick={() => {
                                      const cards = valueProductCards.filter((_, idx) => idx !== index);
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, value_products_section: { ...valueProductsSection, cards } }
                                      });
                                    }}
                                  >
                                    Kartı Sil
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                          {valueProductCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz tamamlayıcı ürün kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Kolay Site'nin Benzersiz Özellikleri</h3>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const cards = [...uniqueFeatureCards, { icon: '', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, unique_features_section: { ...uniqueFeaturesSection, cards } }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={uniqueFeaturesSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, unique_features_section: { ...uniqueFeaturesSection, title: e.target.value } }
                                })
                              }
                              placeholder="Kolay Site'nin Benzersiz Özellikleri"
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {uniqueFeatureCards.map((card, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <Input
                                    value={card.icon || ''}
                                    onChange={(e) => {
                                      const cards = [...uniqueFeatureCards];
                                      cards[index] = { ...cards[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, unique_features_section: { ...uniqueFeaturesSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: ClipboardList"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Başlık</label>
                                  <Input
                                    value={card.title || ''}
                                    onChange={(e) => {
                                      const cards = [...uniqueFeatureCards];
                                      cards[index] = { ...cards[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, unique_features_section: { ...uniqueFeaturesSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Sürükle-Bırak Editör"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Input
                                    value={card.description || ''}
                                    onChange={(e) => {
                                      const cards = [...uniqueFeatureCards];
                                      cards[index] = { ...cards[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, unique_features_section: { ...uniqueFeaturesSection, cards } }
                                      });
                                    }}
                                    placeholder="Örn: Bileşenleri kolayca ekleyin, düzenleyin."
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const cards = uniqueFeatureCards.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, unique_features_section: { ...uniqueFeaturesSection, cards } }
                                    });
                                  }}
                                >
                                  Kartı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {uniqueFeatureCards.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz benzersiz özellik kartı eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-bold text-gray-900">Başlamaya Hazır mısınız?</h3>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={ctaSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, cta_section: { ...ctaSection, title: e.target.value } }
                                })
                              }
                              placeholder="Başlamaya Hazır mısınız?"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                            <Input
                              value={ctaSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, cta_section: { ...ctaSection, description: e.target.value } }
                                })
                              }
                              placeholder="Kısa açıklama..."
                            />
                          </div>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Buton Metni</label>
                            <Input
                              value={ctaSection.button_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, cta_section: { ...ctaSection, button_text: e.target.value } }
                                })
                              }
                              placeholder="Örn: Hemen Başla"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Buton Linki</label>
                            <Input
                              value={ctaSection.button_link || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, cta_section: { ...ctaSection, button_link: e.target.value } }
                                })
                              }
                              placeholder="/kayit"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPage(null);
                            setPageContent(null);
                          }}
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handleSavePageContent}
                          disabled={saving}
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </Button>
                      </div>
                    </div>
                  );
                })()
              ) : editingPage.path === '/yapay-zeka' && !pageContent ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 text-yellow-800 text-xs sm:text-sm overflow-x-hidden">
                  <p className="font-medium mb-2">⚠️ Veritabanında İçerik Yok</p>
                  <p className="mb-3 sm:mb-4 break-words">
                    Yapay Zeka (Gogi AI) sayfası içeriklerini düzenleyebilmek için migration çalıştırmanız gerekiyor. Bu işlem sayfa
                    verilerini veritabanına aktarır.
                  </p>
                  <Button
                    onClick={() => runMigration(PAGE_SLUG_MAP[editingPage.path] || null)}
                    disabled={migrationRunning}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {migrationRunning ? 'Migration Çalıştırılıyor...' : 'Migration Çalıştır'}
                  </Button>
                </div>
              ) : editingPage.path === '/yapay-zeka' && pageContent ? (
                (() => {
                  // Parse additional_sections if it's a string
                  let additional = pageContent.additional_sections || {};
                  if (typeof additional === 'string') {
                    try {
                      additional = JSON.parse(additional);
                    } catch (e) {
                      additional = {};
                    }
                  }
                  
                  // Available icons for features section
                  const availableIcons = [
                    'Sparkles', 'Eye', 'MessageSquare', 'Users', 'Database', 'ShieldCheck', 
                    'BadgeCheck', 'Rocket', 'Bot', 'Zap', 'Brain', 'Globe', 'Code', 'Palette',
                    'BarChart3', 'Lightbulb', 'Target', 'TrendingUp', 'Star', 'Clock', 'CheckCircle',
                    'Shield', 'Key', 'Lock', 'Mail', 'Phone', 'Send', 'Bell', 'ShoppingCart',
                    'Package', 'Truck', 'CreditCard', 'Gift', 'MonitorSmartphone', 'Camera',
                    'Video', 'Mic', 'File', 'Folder', 'Copy', 'Share2', 'Heart', 'Award',
                    'Crown', 'GraduationCap', 'BookOpen', 'Tag', 'Flag', 'MapPin', 'Home',
                    'Settings', 'Search', 'HelpCircle', 'Info', 'AlertCircle',
                    'Server', 'Cpu', 'Cloud', 'Wifi', 'Activity', 'Terminal'
                  ];
                  
                  const heroSection = additional.hero || {};
                  const featuresSection = additional.features_section || {};
                  const featuresItems = Array.isArray(featuresSection.cards) ? featuresSection.cards : (Array.isArray(featuresSection.items) ? featuresSection.items : []);
                  // Try both how_it_works_section and how_it_works for compatibility
                  const howItWorksSection = additional.how_it_works_section || additional.how_it_works || {};
                  const howSteps = Array.isArray(howItWorksSection.steps) ? howItWorksSection.steps : [];
                  const howCapabilities = Array.isArray(howItWorksSection.capabilities) ? howItWorksSection.capabilities : [];

                  return (
                    <div className="space-y-4 sm:space-y-6 overflow-x-hidden w-full">
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden w-full">
                        <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                          <Image className="h-5 w-5" />
                          Hero Bölümü
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={pageContent.hero_title || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                              placeholder="Örn: Gogi AI ile Daha Hızlı Üretin"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge / Vurgu Metni</label>
                            <Input
                              value={pageContent.hero_badge_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                              placeholder="Örn: Gogi AI"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Açıklaması</label>
                          <Textarea
                            rows={3}
                            value={pageContent.hero_description || ''}
                            onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                            placeholder="Metin, görsel ve içerik üretimini hızlandıran akıllı asistan..."
                          />
                        </div>
                        
                        {/* Hero Özellikler Listesi */}
                        <div className="space-y-3 mt-4">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">Hero Özellikler Listesi</label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const currentFeatures = additional.hero_features || [];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero_features: [...currentFeatures, { text: '', bold: '' }]
                                  }
                                });
                              }}
                              className="flex items-center gap-2"
                            >
                              <Plus className="w-4 h-4" />
                              Özellik Ekle
                            </Button>
                          </div>
                          {(additional.hero_features || []).map((feature, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600 mb-1">Özellik Metni</label>
                                  <Textarea
                                    rows={2}
                                    value={feature.text || ''}
                                    onChange={(e) => {
                                      const features = [...(additional.hero_features || [])];
                                      features[index] = { ...features[index], text: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          hero_features: features
                                        }
                                      });
                                    }}
                                    placeholder="Örn: Canlı destek ile kesintisiz AI ve insan geçişi sağlayın"
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600 mb-1">Kalın Yazılacak Kısım (Opsiyonel)</label>
                                  <Input
                                    value={feature.bold || ''}
                                    onChange={(e) => {
                                      const features = [...(additional.hero_features || [])];
                                      features[index] = { ...features[index], bold: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          hero_features: features
                                        }
                                      });
                                    }}
                                    placeholder="Örn: kesintisiz AI ve insan geçişi sağlayın"
                                    className="text-sm"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">Metin içindeki vurgulanacak kısmı girin</p>
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const features = (additional.hero_features || []).filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        hero_features: features
                                      }
                                    });
                                  }}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                          {(!additional.hero_features || additional.hero_features.length === 0) && (
                            <p className="text-xs text-gray-500">Henüz özellik eklenmedi.</p>
                          )}
                        </div>
                        
                        {/* Hero Butonları */}
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Birincil Buton Metni</label>
                            <Input
                              value={pageContent.hero_button1_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_button1_text: e.target.value })}
                              placeholder="Örn: Demo Talep Et"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Birincil Buton Linki</label>
                            <Input
                              value={pageContent.hero_button1_link || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_button1_link: e.target.value })}
                              placeholder="/iletisim"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">İkincil Buton Metni</label>
                            <Input
                              value={pageContent.hero_button2_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_button2_text: e.target.value })}
                              placeholder="Örn: İletişime Geç"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">İkincil Buton Linki</label>
                            <Input
                              value={pageContent.hero_button2_link || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_button2_link: e.target.value })}
                              placeholder="/iletisim"
                            />
                          </div>
                        </div>
                        
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Hero Görseli</label>
                            <div className="flex gap-2">
                              <Input
                                value={heroSection.image_url || pageContent.hero_image_url || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    hero_image_url: e.target.value,
                                    additional_sections: {
                                      ...additional,
                                      hero: { ...heroSection, image_url: e.target.value }
                                    }
                                  })
                                }
                                placeholder="https://..."
                                className="flex-1"
                              />
                              <MediaSelector
                                label="Medya Seç"
                                onSelect={(url) =>
                                  setPageContent({
                                    ...pageContent,
                                    hero_image_url: url,
                                    additional_sections: {
                                      ...additional,
                                      hero: {
                                        ...heroSection,
                                        image_url: url
                                      }
                                    }
                                  })
                                }
                              />
                            </div>
                            {(heroSection.image_url || pageContent.hero_image_url) && (
                              <div className="mt-2">
                                <img
                                  src={heroSection.image_url || pageContent.hero_image_url}
                                  alt="Hero"
                                  className="w-40 h-28 object-cover rounded-lg border border-gray-200"
                                />
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">İkincil Vurgu</label>
                            <Input
                              value={heroSection.highlight_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero: { ...heroSection, highlight_text: e.target.value }
                                  }
                                })
                              }
                              placeholder="Örn: Metin • Görsel • Otomasyon"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Neler Sunuyor Section */}
                      <div className="rounded-lg border border-purple-200 bg-purple-50 p-6 space-y-4">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Neler Sunuyor?</h3>
                            <p className="text-sm text-gray-500">Özellikler bölümü - Sol tarafta görünecek özellik kartları</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const items = [...featuresItems, { icon: 'MessageSquare', title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, features_section: { ...featuresSection, items, cards: items } }
                              });
                            }}
                            className="flex items-center gap-2"
                          >
                            <Plus className="w-4 h-4" />
                            Özellik Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={featuresSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, features_section: { ...featuresSection, title: e.target.value } }
                                })
                              }
                              placeholder="Neler Sunuyor?"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Açıklama / Alt Başlık</label>
                            <Input
                              value={featuresSection.subtitle || featuresSection.description || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, features_section: { ...featuresSection, subtitle: e.target.value, description: e.target.value } }
                                })
                              }
                              placeholder="Gogi AI ile müşteri deneyimini ileri taşıyın."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Görsel URL (Sağ Taraf)</label>
                          <div className="flex gap-2">
                            <Input
                              value={featuresSection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, features_section: { ...featuresSection, image_url: e.target.value } }
                                })
                              }
                              placeholder="https://..."
                              className="flex-1"
                            />
                            <MediaSelector
                              label="Medya Seç"
                              onSelect={(url) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    features_section: {
                                      ...featuresSection,
                                      image_url: url
                                    }
                                  }
                                })
                              }
                            />
                          </div>
                          {featuresSection.image_url && (
                            <div className="mt-2">
                              <img
                                src={featuresSection.image_url}
                                alt="Features"
                                className="w-40 h-28 object-cover rounded-lg border border-gray-200"
                              />
                            </div>
                          )}
                        </div>
                        <div className="space-y-3 mt-4">
                          {featuresItems.map((feature, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">İkon</label>
                                  <select
                                    value={feature.icon || ''}
                                    onChange={(e) => {
                                      const items = [...featuresItems];
                                      items[index] = { ...items[index], icon: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, features_section: { ...featuresSection, items, cards: items } }
                                      });
                                    }}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  >
                                    <option value="">İkon Seçin</option>
                                    {availableIcons.map((iconName) => (
                                      <option key={iconName} value={iconName}>
                                        {iconName}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Özellik Başlığı</label>
                                  <Input
                                    value={feature.title || ''}
                                    onChange={(e) => {
                                      const items = [...featuresItems];
                                      items[index] = { ...items[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, features_section: { ...featuresSection, items, cards: items } }
                                      });
                                    }}
                                    placeholder="Örn: Akıllı Sohbet"
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Input
                                    value={feature.description || ''}
                                    onChange={(e) => {
                                      const items = [...featuresItems];
                                      items[index] = { ...items[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, features_section: { ...featuresSection, items, cards: items } }
                                      });
                                    }}
                                    placeholder="Kısa açıklama"
                                    className="text-sm"
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const items = featuresItems.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, features_section: { ...featuresSection, items, cards: items } }
                                    });
                                  }}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                          {featuresItems.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz özellik eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Nasıl Çalışır?</h3>
                            <p className="text-sm text-gray-500">Hedefini tanımla, özelleştir, yayınla ve ölç</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const steps = [...howSteps, { title: '', description: '' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, how_it_works_section: { ...howItWorksSection, steps } }
                              });
                            }}
                            className="flex items-center gap-2"
                          >
                            <Plus className="w-4 h-4" />
                            Adım Ekle
                          </Button>
                        </div>
                        <div className="grid gap-4 md:grid-cols-1">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={howItWorksSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, how_it_works_section: { ...howItWorksSection, title: e.target.value } }
                                })
                              }
                              placeholder="Nasıl Çalışır?"
                            />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {howSteps.map((s, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Adım Başlığı</label>
                                  <Input
                                    value={s.title || ''}
                                    onChange={(e) => {
                                      const steps = [...howSteps];
                                      steps[index] = { ...steps[index], title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, how_it_works_section: { ...howItWorksSection, steps } }
                                      });
                                    }}
                                    placeholder="Örn: Hedefini Tanımla"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600">Açıklama</label>
                                  <Input
                                    value={s.description || ''}
                                    onChange={(e) => {
                                      const steps = [...howSteps];
                                      steps[index] = { ...steps[index], description: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: { ...additional, how_it_works_section: { ...howItWorksSection, steps } }
                                      });
                                    }}
                                    placeholder="Örn: Ne üretmek istediğini ve hedeflerini belirt."
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const steps = howSteps.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, how_it_works_section: { ...howItWorksSection, steps } }
                                    });
                                  }}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                          {howSteps.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz adım eklenmedi.</p>
                          )}
                        </div>

                        {/* Başlıca Yetkinlikler (Capabilities) */}
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
                            <div>
                              <h4 className="text-base font-bold text-gray-900">Başlıca Yetkinlikler</h4>
                              <p className="text-sm text-gray-500">Sağ tarafta görünecek yetkinlik listesi</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const capabilities = [...howCapabilities, ''];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: { ...additional, how_it_works_section: { ...howItWorksSection, capabilities } }
                                });
                              }}
                              className="flex items-center gap-2"
                            >
                              <Plus className="w-4 h-4" />
                              Yetkinlik Ekle
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {howCapabilities.map((capability, index) => (
                              <div key={index} className="flex gap-2 items-center">
                                <Input
                                  value={capability || ''}
                                  onChange={(e) => {
                                    const capabilities = [...howCapabilities];
                                    capabilities[index] = e.target.value;
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, how_it_works_section: { ...howItWorksSection, capabilities } }
                                    });
                                  }}
                                  placeholder={`Yetkinlik ${index + 1}`}
                                  className="flex-1"
                                />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const capabilities = howCapabilities.filter((_, idx) => idx !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, how_it_works_section: { ...howItWorksSection, capabilities } }
                                    });
                                  }}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                            {howCapabilities.length === 0 && (
                              <p className="text-xs text-gray-500">Henüz yetkinlik eklenmedi.</p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPage(null);
                            setPageContent(null);
                          }}
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handleSavePageContent}
                          disabled={saving}
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </Button>
                      </div>
                    </div>
                  );
                })()
              ) : editingPage.path === '/seo-hizmetleri' && pageContent ? (
                (() => {
                  const additional = pageContent.additional_sections || {};
                  const auditSection = additional.audit_section || {};
                  const roadmapSection = additional.roadmap_section || {};
                  const deliverables = additional.deliverables || {};
                  const caseStudy = additional.case_study || {};
                  const pillars = Array.isArray(additional.pillars) ? additional.pillars : [];
                  const auditChecklistItems = Array.isArray(additional.audit_checklist?.items)
                    ? additional.audit_checklist.items
                    : [];
                  const roadmapItems = Array.isArray(additional.roadmap) ? additional.roadmap : [];
                  const deliverableItems = Array.isArray(deliverables.items) ? deliverables.items : [];

                  return (
                    <div className="space-y-4 sm:space-y-6 overflow-x-hidden">
                      {/* Hero */}
                      <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">SEO Hizmetleri Hero Bölümü</h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                            <Input
                              value={pageContent.hero_badge_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                              placeholder="Örn: SEO Hizmetleri"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hero Başlığı</label>
                            <Input
                              value={pageContent.hero_title || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                              placeholder="Ana başlık"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Açıklaması</label>
                          <Textarea
                            rows={3}
                            value={pageContent.hero_description || ''}
                            onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                            placeholder="Kısa açıklama metni"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Hero Görseli (HERO_IMAGE_URL)
                          </label>
                          <div className="flex gap-2">
                            <Input
                              value={pageContent.hero_image_url || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_image_url: e.target.value })}
                              placeholder="https://..."
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => openMediaLibrary('hero_image_url')}
                              className="flex items-center gap-2"
                            >
                              <Image className="w-4 h-4" />
                              Medya Seç
                            </Button>
                          </div>
                          {pageContent.hero_image_url && (
                            <div className="mt-2">
                              <img
                                src={pageContent.hero_image_url}
                                alt="SEO Hero Preview"
                                className="w-32 h-32 object-cover rounded border border-gray-300"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* İlk adım / Audit bölümü */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="text-lg font-bold text-gray-900">İlk Adım Bölümü (Audit)</h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                            <Input
                              value={auditSection.badge_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    audit_section: { ...auditSection, badge_text: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={auditSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    audit_section: { ...auditSection, title: e.target.value }
                                  }
                                })
                              }
                              placeholder="İlk adım: Derinlemesine teknik ve içerik analizi"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={3}
                            value={auditSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  audit_section: { ...auditSection, description: e.target.value }
                                }
                              })
                            }
                          />
                        </div>
                      </div>

                      {/* Audit Checklist maddeleri */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Audit Checklist Maddeleri</h3>
                            <p className="text-xs text-gray-600">
                              Bu alan SEO Denetim checklistinde görünen maddeleri düzenlemenizi sağlar.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const items = [...auditChecklistItems, ''];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  audit_checklist: {
                                    ...(additional.audit_checklist || {}),
                                    items
                                  }
                                }
                              });
                            }}
                          >
                            Madde Ekle
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {auditChecklistItems.map((item, index) => (
                            <div key={index} className="flex gap-2 items-start">
                              <Textarea
                                rows={2}
                                className="flex-1"
                                value={item}
                                onChange={(e) => {
                                  const items = [...auditChecklistItems];
                                  items[index] = e.target.value;
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      audit_checklist: {
                                        ...(additional.audit_checklist || {}),
                                        items
                                      }
                                    }
                                  });
                                }}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => {
                                  const items = auditChecklistItems.filter((_, i) => i !== index);
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      audit_checklist: {
                                        ...(additional.audit_checklist || {}),
                                        items
                                      }
                                    }
                                  });
                                }}
                              >
                                Sil
                              </Button>
                            </div>
                          ))}
                          {auditChecklistItems.length === 0 && (
                            <p className="text-xs text-gray-500">Henüz checklist maddesi eklenmedi.</p>
                          )}
                        </div>
                      </div>

                      {/* SEO Sütun Kartları (Teknik SEO, İçerik & Arama Niyeti, vb.) - Audit altında */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="text-lg font-bold text-gray-900">SEO Sütun Kartları</h3>
                        <p className="text-xs text-gray-600">
                          Bu alan Teknik SEO ve Altyapı, İçerik & Arama Niyeti, Otorite & Backlink Stratejisi, Dönüşüm Optimizasyonu
                          kartlarının başlık ve açıklamalarını düzenlemenizi sağlar.
                        </p>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          {pillars.map((pillar, index) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-2 bg-gray-50">
                              <label className="block text-xs font-semibold text-gray-700">Kart Başlığı</label>
                              <Input
                                value={pillar.title || ''}
                                onChange={(e) => {
                                  const newPillars = [...pillars];
                                  newPillars[index] = { ...pillar, title: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      pillars: newPillars
                                    }
                                  });
                                }}
                                placeholder="Örn: Teknik SEO ve Altyapı"
                              />
                              <label className="block text-xs font-semibold text-gray-700 mt-2">Açıklama</label>
                              <Textarea
                                rows={3}
                                value={pillar.description || ''}
                                onChange={(e) => {
                                  const newPillars = [...pillars];
                                  newPillars[index] = { ...pillar, description: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      pillars: newPillars
                                    }
                                  });
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Roadmap bölümü */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="text-lg font-bold text-gray-900">Roadmap Başlığı Bölümü</h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                            <Input
                              value={roadmapSection.badge_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    roadmap_section: { ...roadmapSection, badge_text: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={roadmapSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    roadmap_section: { ...roadmapSection, title: e.target.value }
                                  }
                                })
                              }
                              placeholder="8 haftada kronik SEO sorunlarını çözüyoruz"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={3}
                            value={roadmapSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  roadmap_section: { ...roadmapSection, description: e.target.value }
                                }
                              })
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Roadmap Görseli (ROADMAP_IMAGE_URL)
                          </label>
                          <div className="flex gap-2">
                            <Input
                              value={roadmapSection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    roadmap_section: { ...roadmapSection, image_url: e.target.value }
                                  }
                                })
                              }
                              placeholder="https://..."
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => openMediaLibrary('additional_sections.roadmap_section.image_url')}
                              className="flex items-center gap-2"
                            >
                              <Image className="w-4 h-4" />
                              Medya Seç
                            </Button>
                          </div>
                          {roadmapSection.image_url && (
                            <div className="mt-2">
                              <img
                                src={roadmapSection.image_url}
                                alt="Roadmap Preview"
                                className="w-32 h-32 object-cover rounded border border-gray-300"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Roadmap Adımları */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Roadmap Adımları</h3>
                            <p className="text-xs text-gray-600">
                              Her faz için başlık ve maddeleri düzenleyebilirsiniz. Maddeleri satır satır yazın.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const steps = [
                                ...roadmapItems,
                                { phase: '', title: '', points: [] }
                              ];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  roadmap: steps
                                }
                              });
                            }}
                          >
                            Faz Ekle
                          </Button>
                        </div>
                        <div className="space-y-3">
                          {roadmapItems.map((step, index) => (
                            <div
                              key={index}
                              className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-2"
                            >
                              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 w-full">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-700">
                                    Faz (ör: Hafta 1-2)
                                  </label>
                                  <Input
                                    value={step.phase || ''}
                                    onChange={(e) => {
                                      const steps = [...roadmapItems];
                                      steps[index] = { ...step, phase: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          roadmap: steps
                                        }
                                      });
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-700">
                                    Başlık
                                  </label>
                                  <Input
                                    value={step.title || ''}
                                    onChange={(e) => {
                                      const steps = [...roadmapItems];
                                      steps[index] = { ...step, title: e.target.value };
                                      setPageContent({
                                        ...pageContent,
                                        additional_sections: {
                                          ...additional,
                                          roadmap: steps
                                        }
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-700">
                                  Maddeler (her satır bir madde)
                                </label>
                                <Textarea
                                  rows={3}
                                  value={Array.isArray(step.points) ? step.points.join('\n') : ''}
                                  onChange={(e) => {
                                    const lines = e.target.value
                                      .split('\n')
                                      .map((l) => l.trim())
                                      .filter((l) => l !== '');
                                    const steps = [...roadmapItems];
                                    steps[index] = { ...step, points: lines };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        roadmap: steps
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const steps = roadmapItems.filter((_, i) => i !== index);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        roadmap: steps
                                      }
                                    });
                                  }}
                                >
                                  Fazı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                          {roadmapItems.length === 0 && (
                            <p className="text-xs text-gray-500">
                              Henüz roadmap fazı eklenmedi.
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Deliverables başlık bölümü (\"SEO iş ortaklığımızdan neler elde edeceksiniz?\") */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="text-lg font-bold text-gray-900">
                          Teslimatlar Başlık Bölümü
                          <span className="block text-xs font-normal text-gray-500">
                            (Sayfadaki \"SEO iş ortaklığımızdan neler elde edeceksiniz?\" alanı)
                          </span>
                        </h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                            <Input
                              value={deliverables.badge_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    deliverables: { ...deliverables, badge_text: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={deliverables.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    deliverables: { ...deliverables, title: e.target.value }
                                  }
                                })
                              }
                              placeholder="SEO iş ortaklığımızdan neler elde edeceksiniz?"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={3}
                            value={deliverables.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  deliverables: { ...deliverables, description: e.target.value }
                                }
                              })
                            }
                          />
                        </div>
                      </div>

                      {/* Teslimatlar maddeleri */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Teslimatlar Maddeleri</h3>
                            <p className="text-xs text-gray-600">
                              SEO iş ortaklığımızdan elde edilecek teslimatları buradan düzenleyebilirsiniz.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const items = [...deliverableItems, ''];
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  deliverables: {
                                    ...deliverables,
                                    items
                                  }
                                }
                              });
                            }}
                          >
                            Madde Ekle
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {deliverableItems.map((item, index) => (
                            <div key={index} className="flex gap-2 items-start">
                              <Textarea
                                rows={2}
                                className="flex-1"
                                value={item}
                                onChange={(e) => {
                                  const items = [...deliverableItems];
                                  items[index] = e.target.value;
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      deliverables: {
                                        ...deliverables,
                                        items
                                      }
                                    }
                                  });
                                }}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => {
                                  const items = deliverableItems.filter((_, i) => i !== index);
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      deliverables: {
                                        ...deliverables,
                                        items
                                      }
                                    }
                                  });
                                }}
                              >
                                Sil
                              </Button>
                            </div>
                          ))}
                          {deliverableItems.length === 0 && (
                            <p className="text-xs text-gray-500">
                              Henüz teslimat maddesi eklenmedi.
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Case Study başlık ve görsel bölümü */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="text-lg font-bold text-gray-900">Vaka Çalışması Başlık Bölümü</h3>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Marka / Başlık</label>
                            <Input
                              value={caseStudy.brand || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    case_study: { ...caseStudy, brand: e.target.value }
                                  }
                                })
                              }
                              placeholder="B2B SaaS - Call Center Platformu"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Sorun</label>
                            <Textarea
                              rows={3}
                              value={caseStudy.challenge || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    case_study: { ...caseStudy, challenge: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Çözüm</label>
                            <Textarea
                              rows={3}
                              value={caseStudy.solution || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    case_study: { ...caseStudy, solution: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Görsel URL (CASE_IMAGE_URL)
                            </label>
                            <div className="flex gap-2">
                              <Input
                                value={caseStudy.image_url || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      case_study: { ...caseStudy, image_url: e.target.value }
                                    }
                                  })
                                }
                                placeholder="https://..."
                                className="flex-1"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => openMediaLibrary('additional_sections.case_study.image_url')}
                                className="flex items-center gap-2"
                              >
                                <Image className="w-4 h-4" />
                                Medya Seç
                              </Button>
                            </div>
                            {caseStudy.image_url && (
                              <div className="mt-2">
                                <img
                                  src={caseStudy.image_url}
                                  alt="Case Study Preview"
                                  className="w-32 h-32 object-cover rounded border border-gray-300"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>


                      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPage(null);
                            setPageContent(null);
                          }}
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handleSavePageContent}
                          disabled={saving}
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </Button>
                      </div>
                    </div>
                  );
                })()
              ) : editingPage.path === '/reklam-hizmetleri' && pageContent ? (
                (() => {
                  const additional = pageContent.additional_sections || {};
                  const channelsSection = additional.channels_section || {};
                  const channelHighlights = Array.isArray(additional.channel_highlights) ? additional.channel_highlights : [];
                  const processSection = additional.process_section || {};
                  const processSteps = Array.isArray(additional.process_steps) ? additional.process_steps : [];
                  const deliverables = additional.deliverables || {};
                  const deliverableItems = Array.isArray(deliverables.items) ? deliverables.items : [];
                  const caseStudy = additional.case_study || {};
                  const heroStats = Array.isArray(additional.hero_stats) ? additional.hero_stats : [];

                  return (
                    <div className="space-y-4 sm:space-y-6 overflow-x-hidden">
                      {/* Hero */}
                      <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Reklam Hizmetleri Hero Bölümü</h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                            <Input
                              value={pageContent.hero_badge_text || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })}
                              placeholder="Örn: Reklam Yönetimi"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Hero Başlığı</label>
                            <Input
                              value={pageContent.hero_title || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })}
                              placeholder="Ana başlık"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Açıklaması</label>
                          <Textarea
                            rows={3}
                            value={pageContent.hero_description || ''}
                            onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })}
                            placeholder="Kısa açıklama metni"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Hero Görseli</label>
                          <div className="flex gap-2">
                            <Input
                              value={pageContent.hero_image_url || ''}
                              onChange={(e) => setPageContent({ ...pageContent, hero_image_url: e.target.value })}
                              placeholder="https://..."
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => openMediaLibrary('hero_image_url')}
                              className="flex items-center gap-2"
                            >
                              <Image className="w-4 h-4" />
                              Medya Seç
                            </Button>
                          </div>
                          {pageContent.hero_image_url && (
                            <div className="mt-2">
                              <img
                                src={pageContent.hero_image_url}
                                alt="Hero Preview"
                                className="w-32 h-32 object-cover rounded border border-gray-300"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                          )}
                        </div>
                        
                        {/* Hero Stats */}
                        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-gray-900">Hero İstatistikleri</h4>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const stats = [...heroStats, { label: '', value: '' }];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    hero_stats: stats
                                  }
                                });
                              }}
                            >
                              İstatistik Ekle
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {heroStats.map((stat, idx) => (
                              <div key={idx} className="flex gap-2 items-center">
                                <Input
                                  value={stat.label || ''}
                                  onChange={(e) => {
                                    const stats = [...heroStats];
                                    stats[idx] = { ...stat, label: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, hero_stats: stats }
                                    });
                                  }}
                                  placeholder="Etiket (örn: Aylık Yönetilen Bütçe)"
                                  className="flex-1"
                                />
                                <Input
                                  value={stat.value || ''}
                                  onChange={(e) => {
                                    const stats = [...heroStats];
                                    stats[idx] = { ...stat, value: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, hero_stats: stats }
                                    });
                                  }}
                                  placeholder="Değer (örn: ₺5M+)"
                                  className="flex-1"
                                />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const stats = heroStats.filter((_, i) => i !== idx);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, hero_stats: stats }
                                    });
                                  }}
                                >
                                  Sil
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Channels Section */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="text-lg font-bold text-gray-900">Kanallar Bölümü</h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                            <Input
                              value={channelsSection.badge_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    channels_section: { ...channelsSection, badge_text: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={channelsSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    channels_section: { ...channelsSection, title: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={3}
                            value={channelsSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  channels_section: { ...channelsSection, description: e.target.value }
                                }
                              })
                            }
                          />
                        </div>
                      </div>

                      {/* Channel Highlights */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-bold text-gray-900">Kanal Vurguları (4 Kart)</h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const highlights = [...channelHighlights, { title: '', description: '', icon: 'BarChart3' }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, channel_highlights: highlights }
                              });
                            }}
                          >
                            Kart Ekle
                          </Button>
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          {channelHighlights.map((highlight, idx) => (
                            <div key={idx} className="border border-gray-200 rounded-lg p-4 space-y-2 bg-gray-50">
                              <label className="block text-xs font-semibold text-gray-700">Kart Başlığı</label>
                              <Input
                                value={highlight.title || ''}
                                onChange={(e) => {
                                  const highlights = [...channelHighlights];
                                  highlights[idx] = { ...highlight, title: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: { ...additional, channel_highlights: highlights }
                                  });
                                }}
                              />
                              <label className="block text-xs font-semibold text-gray-700 mt-2">Açıklama</label>
                              <Textarea
                                rows={3}
                                value={highlight.description || ''}
                                onChange={(e) => {
                                  const highlights = [...channelHighlights];
                                  highlights[idx] = { ...highlight, description: e.target.value };
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: { ...additional, channel_highlights: highlights }
                                  });
                                }}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-red-200 text-red-600 hover:bg-red-50 mt-2"
                                onClick={() => {
                                  const highlights = channelHighlights.filter((_, i) => i !== idx);
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: { ...additional, channel_highlights: highlights }
                                  });
                                }}
                              >
                                Kartı Sil
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Process Section */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="text-lg font-bold text-gray-900">Çalışma Süreci Başlık Bölümü</h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                            <Input
                              value={processSection.badge_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    process_section: { ...processSection, badge_text: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={processSection.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    process_section: { ...processSection, title: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={3}
                            value={processSection.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  process_section: { ...processSection, description: e.target.value }
                                }
                              })
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Süreç Görseli</label>
                          <div className="flex gap-2">
                            <Input
                              value={processSection.image_url || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    process_section: { ...processSection, image_url: e.target.value }
                                  }
                                })
                              }
                              placeholder="https://..."
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => openMediaLibrary('additional_sections.process_section.image_url')}
                              className="flex items-center gap-2"
                            >
                              <Image className="w-4 h-4" />
                              Medya Seç
                            </Button>
                          </div>
                          {processSection.image_url && (
                            <div className="mt-2">
                              <img
                                src={processSection.image_url}
                                alt="Process Preview"
                                className="w-32 h-32 object-cover rounded border border-gray-300"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Process Steps */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Çalışma Süreci Adımları</h3>
                            <p className="text-xs text-gray-600">Her adım için faz ve detayları düzenleyebilirsiniz.</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const steps = [...processSteps, { phase: '', details: [] }];
                              setPageContent({
                                ...pageContent,
                                additional_sections: { ...additional, process_steps: steps }
                              });
                            }}
                          >
                            Adım Ekle
                          </Button>
                        </div>
                        <div className="space-y-3">
                          {processSteps.map((step, idx) => (
                            <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-2">
                              <div>
                                <label className="block text-xs font-semibold text-gray-700">Faz (örn: 1. Keşif & KPI Tanımı)</label>
                                <Input
                                  value={step.phase || ''}
                                  onChange={(e) => {
                                    const steps = [...processSteps];
                                    steps[idx] = { ...step, phase: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, process_steps: steps }
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-700">Detaylar (her satır bir madde)</label>
                                <Textarea
                                  rows={4}
                                  value={Array.isArray(step.details) ? step.details.join('\n') : ''}
                                  onChange={(e) => {
                                    const lines = e.target.value.split('\n').map(l => l.trim()).filter(l => l !== '');
                                    const steps = [...processSteps];
                                    steps[idx] = { ...step, details: lines };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, process_steps: steps }
                                    });
                                  }}
                                />
                              </div>
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const steps = processSteps.filter((_, i) => i !== idx);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: { ...additional, process_steps: steps }
                                    });
                                  }}
                                >
                                  Adımı Sil
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Deliverables */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="text-lg font-bold text-gray-900">Teslimatlar Başlık Bölümü</h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                            <Input
                              value={deliverables.badge_text || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    deliverables: { ...deliverables, badge_text: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Başlık</label>
                            <Input
                              value={deliverables.title || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    deliverables: { ...deliverables, title: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                          <Textarea
                            rows={3}
                            value={deliverables.description || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  deliverables: { ...deliverables, description: e.target.value }
                                }
                              })
                            }
                          />
                        </div>
                        
                        {/* Deliverables Items */}
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-gray-900">Teslimat Maddeleri</h4>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const items = [...deliverableItems, ''];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    deliverables: { ...deliverables, items }
                                  }
                                });
                              }}
                            >
                              Madde Ekle
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {deliverableItems.map((item, idx) => (
                              <div key={idx} className="flex gap-2 items-start">
                                <Textarea
                                  rows={2}
                                  className="flex-1"
                                  value={item}
                                  onChange={(e) => {
                                    const items = [...deliverableItems];
                                    items[idx] = e.target.value;
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        deliverables: { ...deliverables, items }
                                      }
                                    });
                                  }}
                                />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const items = deliverableItems.filter((_, i) => i !== idx);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        deliverables: { ...deliverables, items }
                                      }
                                    });
                                  }}
                                >
                                  Sil
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Case Study */}
                      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 overflow-x-hidden">
                        <h3 className="text-lg font-bold text-gray-900">Vaka Çalışması Başlık Bölümü</h3>
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Marka</label>
                            <Input
                              value={caseStudy.brand || ''}
                              onChange={(e) =>
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    case_study: { ...caseStudy, brand: e.target.value }
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Vaka Görseli</label>
                            <div className="flex gap-2">
                              <Input
                                value={caseStudy.image_url || ''}
                                onChange={(e) =>
                                  setPageContent({
                                    ...pageContent,
                                    additional_sections: {
                                      ...additional,
                                      case_study: { ...caseStudy, image_url: e.target.value }
                                    }
                                  })
                                }
                                placeholder="https://..."
                                className="flex-1"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => openMediaLibrary('additional_sections.case_study.image_url')}
                                className="flex items-center gap-2"
                              >
                                <Image className="w-4 h-4" />
                                Medya Seç
                              </Button>
                            </div>
                            {caseStudy.image_url && (
                              <div className="mt-2">
                                <img
                                  src={caseStudy.image_url}
                                  alt="Case Study Preview"
                                  className="w-32 h-32 object-cover rounded border border-gray-300"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Sorun</label>
                          <Textarea
                            rows={3}
                            value={caseStudy.challenge || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  case_study: { ...caseStudy, challenge: e.target.value }
                                }
                              })
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Yaklaşımımız</label>
                          <Textarea
                            rows={3}
                            value={caseStudy.solution || ''}
                            onChange={(e) =>
                              setPageContent({
                                ...pageContent,
                                additional_sections: {
                                  ...additional,
                                  case_study: { ...caseStudy, solution: e.target.value }
                                }
                              })
                            }
                          />
                        </div>
                        
                        {/* Case Study Results */}
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-gray-900">Sonuç Metrikleri</h4>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const results = Array.isArray(caseStudy.results) ? caseStudy.results : [];
                                const newResults = [...results, { metric: '', label: '' }];
                                setPageContent({
                                  ...pageContent,
                                  additional_sections: {
                                    ...additional,
                                    case_study: { ...caseStudy, results: newResults }
                                  }
                                });
                              }}
                            >
                              Metrik Ekle
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {(Array.isArray(caseStudy.results) ? caseStudy.results : []).map((result, idx) => (
                              <div key={idx} className="flex gap-2 items-center">
                                <Input
                                  value={result.metric || ''}
                                  onChange={(e) => {
                                    const results = [...(Array.isArray(caseStudy.results) ? caseStudy.results : [])];
                                    results[idx] = { ...result, metric: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        case_study: { ...caseStudy, results }
                                      }
                                    });
                                  }}
                                  placeholder="Metrik (örn: %185)"
                                  className="flex-1"
                                />
                                <Input
                                  value={result.label || ''}
                                  onChange={(e) => {
                                    const results = [...(Array.isArray(caseStudy.results) ? caseStudy.results : [])];
                                    results[idx] = { ...result, label: e.target.value };
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        case_study: { ...caseStudy, results }
                                      }
                                    });
                                  }}
                                  placeholder="Etiket (örn: ROAS Artışı)"
                                  className="flex-1"
                                />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    const results = (Array.isArray(caseStudy.results) ? caseStudy.results : []).filter((_, i) => i !== idx);
                                    setPageContent({
                                      ...pageContent,
                                      additional_sections: {
                                        ...additional,
                                        case_study: { ...caseStudy, results }
                                      }
                                    });
                                  }}
                                >
                                  Sil
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingPage(null);
                            setPageContent(null);
                          }}
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handleSavePageContent}
                          disabled={saving}
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </Button>
                      </div>
                    </div>
                  );
                })()
              ) : (['/marka-tasarim-hizmeti', '/dijital-tasarimlar', '/basili-materyaller'].includes(editingPage.path) && pageContent) ? (
                (() => {
                  const additional = pageContent.additional_sections || {};
                  const auditSection = additional.audit_section || {};
                  const roadmapSection = additional.roadmap_section || {};
                  const deliverablesSection = additional.deliverables || {};
                  const caseStudy = additional.case_study || {};
                  const auditChecklistItems = Array.isArray(additional.audit_checklist?.items) ? additional.audit_checklist.items : [];
                  const pillars = Array.isArray(additional.pillars) ? additional.pillars : [];
                  const roadmapItems = Array.isArray(additional.roadmap) ? additional.roadmap : [];
                  const deliverableItems = Array.isArray(deliverablesSection.items) ? deliverablesSection.items : [];
                  const impactItems = Array.isArray(caseStudy.impact) ? caseStudy.impact : [];
                  const articles = Array.isArray(additional.articles) ? additional.articles : [];
                  const faqs = Array.isArray(additional.faqs) ? additional.faqs : [];
                  return (
                  <div className="space-y-4 sm:space-y-6 overflow-x-hidden">
                    {/* Hero */}
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 space-y-4">
                      <h3 className="text-lg font-bold text-gray-900">Hero Bölümü</h3>
                      <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-2 w-full">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                          <Input value={pageContent.hero_badge_text || ''} onChange={(e) => setPageContent({ ...pageContent, hero_badge_text: e.target.value })} placeholder="Örn: Marka Tasarım Hizmeti" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Hero Başlığı</label>
                          <Input value={pageContent.hero_title || ''} onChange={(e) => setPageContent({ ...pageContent, hero_title: e.target.value })} placeholder="Ana başlık" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Hero Açıklaması</label>
                        <Textarea rows={3} value={pageContent.hero_description || ''} onChange={(e) => setPageContent({ ...pageContent, hero_description: e.target.value })} placeholder="Kısa açıklama metni" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hero Görseli</label>
                        <div className="flex gap-2">
                          <Input value={pageContent.hero_image_url || ''} onChange={(e) => setPageContent({ ...pageContent, hero_image_url: e.target.value })} placeholder="https://..." className="flex-1" />
                          <Button type="button" variant="outline" onClick={() => openMediaLibrary('hero_image_url')} className="flex items-center gap-2">
                            <Image className="w-4 h-4" /> Medya Seç
                          </Button>
                        </div>
                        {pageContent.hero_image_url && (
                          <div className="mt-2">
                            <img src={pageContent.hero_image_url} alt="Hero Preview" className="w-32 h-32 object-cover rounded border border-gray-300" onError={(e) => { e.target.style.display = 'none'; }} />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Audit / İlk Adım */}
                    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
                      <h3 className="text-lg font-bold text-gray-900">İlk Adım (Marka Analizi) Bölümü</h3>
                      <div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                          <Input value={auditSection.badge_text || ''} onChange={(e) => setPageContent({ ...pageContent, additional_sections: { ...additional, audit_section: { ...auditSection, badge_text: e.target.value } } })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Başlık</label>
                          <Input value={auditSection.title || ''} onChange={(e) => setPageContent({ ...pageContent, additional_sections: { ...additional, audit_section: { ...auditSection, title: e.target.value } } })} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                        <Textarea rows={2} value={auditSection.description || ''} onChange={(e) => setPageContent({ ...pageContent, additional_sections: { ...additional, audit_section: { ...auditSection, description: e.target.value } } })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Checklist Maddeleri</label>
                        <Button variant="outline" size="sm" onClick={() => setPageContent({ ...pageContent, additional_sections: { ...additional, audit_checklist: { items: [...auditChecklistItems, ''] } } })}>Madde Ekle</Button>
                        <div className="mt-2 space-y-2">
                          {auditChecklistItems.map((item, i) => (
                            <div key={i} className="flex gap-2">
                              <Input value={item} onChange={(e) => { const arr = [...auditChecklistItems]; arr[i] = e.target.value; setPageContent({ ...pageContent, additional_sections: { ...additional, audit_checklist: { items: arr } } }); }} className="flex-1" />
                              <Button variant="outline" size="sm" className="border-red-200 text-red-600" onClick={() => setPageContent({ ...pageContent, additional_sections: { ...additional, audit_checklist: { items: auditChecklistItems.filter((_, idx) => idx !== i) } } })}>Sil</Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Pillars (4 kart) */}
                    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
                      <h3 className="text-lg font-bold text-gray-900">Pillar Kartları (Strateji, Kimlik, Uygulama, Hedef)</h3>
                      <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
                        {[0, 1, 2, 3].map((i) => {
                          const p = pillars[i] || {};
                          return (
                            <div key={i} className="border rounded-lg p-3 space-y-2 bg-gray-50">
                              <Input placeholder="İkon (Gauge, FileText, Globe, Target)" value={p.icon || ''} onChange={(e) => { const arr = [...pillars]; while (arr.length <= i) arr.push({}); arr[i] = { ...arr[i], icon: e.target.value }; setPageContent({ ...pageContent, additional_sections: { ...additional, pillars: arr } }); }} />
                              <Input placeholder="Başlık" value={p.title || ''} onChange={(e) => { const arr = [...pillars]; while (arr.length <= i) arr.push({}); arr[i] = { ...arr[i], title: e.target.value }; setPageContent({ ...pageContent, additional_sections: { ...additional, pillars: arr } }); }} />
                              <Textarea rows={2} placeholder="Açıklama" value={p.description || ''} onChange={(e) => { const arr = [...pillars]; while (arr.length <= i) arr.push({}); arr[i] = { ...arr[i], description: e.target.value }; setPageContent({ ...pageContent, additional_sections: { ...additional, pillars: arr } }); }} />
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Roadmap Section + Görsel */}
                    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
                      <h3 className="text-lg font-bold text-gray-900">Çalışma Planı (Roadmap) Bölümü</h3>
                      <div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Badge Metni</label>
                          <Input value={roadmapSection.badge_text || ''} onChange={(e) => setPageContent({ ...pageContent, additional_sections: { ...additional, roadmap_section: { ...roadmapSection, badge_text: e.target.value } } })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Başlık</label>
                          <Input value={roadmapSection.title || ''} onChange={(e) => setPageContent({ ...pageContent, additional_sections: { ...additional, roadmap_section: { ...roadmapSection, title: e.target.value } } })} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                        <Textarea rows={2} value={roadmapSection.description || ''} onChange={(e) => setPageContent({ ...pageContent, additional_sections: { ...additional, roadmap_section: { ...roadmapSection, description: e.target.value } } })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Roadmap Görseli</label>
                        <div className="flex gap-2">
                          <Input value={roadmapSection.image_url || ''} onChange={(e) => setPageContent({ ...pageContent, additional_sections: { ...additional, roadmap_section: { ...roadmapSection, image_url: e.target.value } } })} placeholder="https://..." className="flex-1" />
                          <Button type="button" variant="outline" onClick={() => openMediaLibrary('additional_sections.roadmap_section.image_url')} className="flex items-center gap-2"><Image className="w-4 h-4" /> Medya Seç</Button>
                        </div>
                        {roadmapSection.image_url && <div className="mt-2"><img src={roadmapSection.image_url} alt="Roadmap" className="w-32 h-32 object-cover rounded border" onError={(e) => { e.target.style.display = 'none'; }} /></div>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Roadmap Adımları (Faz, Başlık, Maddeler)</label>
                        <Button variant="outline" size="sm" onClick={() => setPageContent({ ...pageContent, additional_sections: { ...additional, roadmap: [...roadmapItems, { phase: '', title: '', points: [] }] } })}>Adım Ekle</Button>
                        <div className="mt-2 space-y-2">
                          {roadmapItems.map((step, idx) => (
                            <div key={idx} className="border rounded p-3 bg-gray-50 space-y-2">
                              <div className="flex gap-2">
                                <Input placeholder="Faz (örn: 1-2. Hafta)" value={step.phase || ''} onChange={(e) => { const arr = [...roadmapItems]; arr[idx] = { ...step, phase: e.target.value }; setPageContent({ ...pageContent, additional_sections: { ...additional, roadmap: arr } }); }} />
                                <Input placeholder="Başlık" value={step.title || ''} onChange={(e) => { const arr = [...roadmapItems]; arr[idx] = { ...step, title: e.target.value }; setPageContent({ ...pageContent, additional_sections: { ...additional, roadmap: arr } }); }} />
                              </div>
                              <Textarea rows={2} placeholder="Maddeler (her satır bir madde)" value={Array.isArray(step.points) ? step.points.join('\n') : ''} onChange={(e) => { const arr = [...roadmapItems]; arr[idx] = { ...step, points: e.target.value.split('\n').map(l => l.trim()).filter(Boolean) }; setPageContent({ ...pageContent, additional_sections: { ...additional, roadmap: arr } }); }} />
                              <Button variant="outline" size="sm" className="border-red-200 text-red-600" onClick={() => setPageContent({ ...pageContent, additional_sections: { ...additional, roadmap: roadmapItems.filter((_, i) => i !== idx) } })}>Sil</Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Deliverables */}
                    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
                      <h3 className="text-lg font-bold text-gray-900">Teslimatlar Bölümü</h3>
                      <div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
                        <div><label className="block text-sm font-medium text-gray-700">Badge</label><Input value={deliverablesSection.badge_text || ''} onChange={(e) => setPageContent({ ...pageContent, additional_sections: { ...additional, deliverables: { ...deliverablesSection, badge_text: e.target.value } } })} /></div>
                        <div><label className="block text-sm font-medium text-gray-700">Başlık</label><Input value={deliverablesSection.title || ''} onChange={(e) => setPageContent({ ...pageContent, additional_sections: { ...additional, deliverables: { ...deliverablesSection, title: e.target.value } } })} /></div>
                      </div>
                      <div><label className="block text-sm font-medium text-gray-700">Açıklama</label><Textarea rows={2} value={deliverablesSection.description || ''} onChange={(e) => setPageContent({ ...pageContent, additional_sections: { ...additional, deliverables: { ...deliverablesSection, description: e.target.value } } })} /></div>
                      <div>
                        <Button variant="outline" size="sm" onClick={() => setPageContent({ ...pageContent, additional_sections: { ...additional, deliverables: { ...deliverablesSection, items: [...deliverableItems, ''] } } })}>Madde Ekle</Button>
                        <div className="mt-2 space-y-2">
                          {deliverableItems.map((it, i) => (
                            <div key={i} className="flex gap-2">
                              <Input value={it} onChange={(e) => { const arr = [...deliverableItems]; arr[i] = e.target.value; setPageContent({ ...pageContent, additional_sections: { ...additional, deliverables: { ...deliverablesSection, items: arr } } }); }} className="flex-1" />
                              <Button variant="outline" size="sm" className="border-red-200 text-red-600" onClick={() => setPageContent({ ...pageContent, additional_sections: { ...additional, deliverables: { ...deliverablesSection, items: deliverableItems.filter((_, idx) => idx !== i) } } })}>Sil</Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Case Study + Görsel */}
                    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
                      <h3 className="text-lg font-bold text-gray-900">Vaka Çalışması Bölümü</h3>
                      <div><label className="block text-sm font-medium text-gray-700">Marka / Başlık</label><Input value={caseStudy.brand || ''} onChange={(e) => setPageContent({ ...pageContent, additional_sections: { ...additional, case_study: { ...caseStudy, brand: e.target.value } } })} /></div>
                      <div><label className="block text-sm font-medium text-gray-700">Sorun</label><Textarea rows={2} value={caseStudy.challenge || ''} onChange={(e) => setPageContent({ ...pageContent, additional_sections: { ...additional, case_study: { ...caseStudy, challenge: e.target.value } } })} /></div>
                      <div><label className="block text-sm font-medium text-gray-700">Çözüm</label><Textarea rows={2} value={caseStudy.solution || ''} onChange={(e) => setPageContent({ ...pageContent, additional_sections: { ...additional, case_study: { ...caseStudy, solution: e.target.value } } })} /></div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Vaka Görseli</label>
                        <div className="flex gap-2">
                          <Input value={caseStudy.image_url || ''} onChange={(e) => setPageContent({ ...pageContent, additional_sections: { ...additional, case_study: { ...caseStudy, image_url: e.target.value } } })} placeholder="https://..." className="flex-1" />
                          <Button type="button" variant="outline" onClick={() => openMediaLibrary('additional_sections.case_study.image_url')} className="flex items-center gap-2"><Image className="w-4 h-4" /> Medya Seç</Button>
                        </div>
                        {caseStudy.image_url && <div className="mt-2"><img src={caseStudy.image_url} alt="Case Study" className="w-32 h-32 object-cover rounded border" onError={(e) => { e.target.style.display = 'none'; }} /></div>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sonuç Metrikleri (metric, label)</label>
                        <Button variant="outline" size="sm" onClick={() => setPageContent({ ...pageContent, additional_sections: { ...additional, case_study: { ...caseStudy, impact: [...impactItems, { metric: '', label: '' }] } } })}>Metrik Ekle</Button>
                        <div className="mt-2 space-y-2">
                          {impactItems.map((imp, idx) => (
                            <div key={idx} className="flex gap-2 items-center">
                              <Input placeholder="Metrik (%40, 3x)" value={imp.metric || ''} onChange={(e) => { const arr = [...impactItems]; arr[idx] = { ...imp, metric: e.target.value }; setPageContent({ ...pageContent, additional_sections: { ...additional, case_study: { ...caseStudy, impact: arr } } }); }} />
                              <Input placeholder="Etiket" value={imp.label || ''} onChange={(e) => { const arr = [...impactItems]; arr[idx] = { ...imp, label: e.target.value }; setPageContent({ ...pageContent, additional_sections: { ...additional, case_study: { ...caseStudy, impact: arr } } }); }} />
                              <Button variant="outline" size="sm" className="border-red-200 text-red-600" onClick={() => setPageContent({ ...pageContent, additional_sections: { ...additional, case_study: { ...caseStudy, impact: impactItems.filter((_, i) => i !== idx) } } })}>Sil</Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                      <Button variant="outline" onClick={() => { setEditingPage(null); setPageContent(null); }}>İptal</Button>
                      <Button onClick={handleSavePageContent} disabled={saving} className="bg-emerald-600 text-white hover:bg-emerald-700">{saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}</Button>
                    </div>
                  </div>
                  );
                })()
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800 text-sm">
                  <p className="font-medium mb-2">ℹ️ İçerik Düzenleme</p>
                  <p>
                    Bu sayfa için içerik düzenleme henüz eklenmedi. Anasayfa, Hakkımızda, İletişim, E-Ticaret, Marka Tasarım Hizmeti, Dijital Tasarımlar, Basılı Materyaller ve diğer birçok sayfa düzenlenebilir. SEO ayarlarını düzenlemek için sol menüden "Sayfa SEO Ayarları" ekranını kullanabilirsiniz.
                  </p>
                  <div className="mt-4 space-y-1 text-xs text-gray-600">
                    <p>Sayfa: <strong>{editingPage.name}</strong></p>
                    <p>Component: <strong>{editingPage.component}</strong></p>
                    <p>Kategori: <strong>{editingPage.category}</strong></p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SEO Edit Modal for Dynamic Pages */}
      {editingSeoPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between z-10">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                  SEO Ayarları - {editingSeoPage.name}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  <code className="bg-gray-100 px-2 py-1 rounded text-purple-600 text-xs break-all">
                    {editingSeoPage.path}
                  </code>
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingSeoPage(null);
                  setSeoData(null);
                }}
                className="flex-shrink-0"
              >
                Kapat
              </Button>
            </div>
            <div className="p-4 sm:p-6">
              {loadingSeo ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : seoData ? (
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-purple-50 via-white to-rose-50 border border-purple-200 rounded-lg p-4 sm:p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          <Globe className="w-5 h-5 text-purple-600" />
                          SEO Ayarları
                        </h3>
                        <p className="text-sm text-gray-500">
                          Sayfanın arama motoru sonuçlarındaki görünürlüğünü buradan yönetin.
                        </p>
                      </div>
                      <span className="text-xs uppercase tracking-wider bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
                        Meta • Open Graph
                      </span>
                    </div>

                    <div className="grid gap-5 lg:grid-cols-2">
                      <div className="space-y-3">
                        <label className="block text-sm font-semibold text-gray-700">Meta Başlık</label>
                        <Input
                          value={seoData.title || ''}
                          onChange={(e) => setSeoData({ ...seoData, title: e.target.value })}
                          placeholder="Sayfa başlığı (60 karakter önerilir)"
                          maxLength={160}
                        />
                        <p className="text-xs text-gray-400">
                          Google sonuçlarında başlık olarak görüntülenir. Şu an {seoData.title?.length || 0} karakter.
                        </p>
                      </div>

                      <div className="space-y-3">
                        <label className="block text-sm font-semibold text-gray-700">Meta Açıklama</label>
                        <Textarea
                          value={seoData.description || ''}
                          onChange={(e) => setSeoData({ ...seoData, description: e.target.value })}
                          placeholder="Arama sonuçlarında görünen özet metin (150-160 karakter ideal)"
                          rows={4}
                          maxLength={300}
                        />
                        <p className="text-xs text-gray-400">
                          Önerilen uzunluk 150-160 karakter. Şu an {seoData.description?.length || 0} karakter.
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-5 lg:grid-cols-2 mt-6">
                      <div className="space-y-3">
                        <label className="block text-sm font-semibold text-gray-700">Anahtar Kelimeler</label>
                        <Input
                          value={seoData.keywords || ''}
                          onChange={(e) => setSeoData({ ...seoData, keywords: e.target.value })}
                          placeholder="Her bir anahtar kelimeyi virgül ile ayırın"
                        />
                        <p className="text-xs text-gray-400">Örn: dijital ajans, seo hizmeti, web tasarım</p>
                      </div>

                      <div className="space-y-3">
                        <label className="block text-sm font-semibold text-gray-700">Open Graph Görseli</label>
                        <div className="flex gap-2">
                          <Input
                            value={seoData.og_image || ''}
                            onChange={(e) => setSeoData({ ...seoData, og_image: e.target.value })}
                            placeholder="1200x630 px önerilir"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setSelectedImageField('og_image_seo');
                              setShowMediaLibrary(true);
                            }}
                            className="flex items-center gap-2"
                          >
                            <Image className="w-4 h-4" />
                            Seç
                          </Button>
                        </div>
                        {seoData.og_image && (
                          <div className="mt-2">
                            <img
                              src={seoData.og_image}
                              alt="OG preview"
                              className="w-64 h-36 object-cover rounded border border-gray-200"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-6">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={seoData.noindex === 1}
                          onChange={(e) => setSeoData({ ...seoData, noindex: e.target.checked ? 1 : 0 })}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm font-semibold text-gray-700">
                          Noindex (Arama motorlarında indexlenmesin)
                        </span>
                      </label>
                      <p className="text-xs text-gray-400 mt-1">
                        Bu seçeneği işaretlerseniz, sayfa arama motorları tarafından indexlenmeyecektir.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingSeoPage(null);
                        setSeoData(null);
                      }}
                    >
                      İptal
                    </Button>
                    <Button
                      onClick={handleSaveSeo}
                      disabled={savingSeo}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {savingSeo ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Media Library Modal */}
      {showMediaLibrary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-2 sm:p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 z-10">
              <div className="flex-1 min-w-0">
                <h2 className="text-base sm:text-xl font-bold text-gray-900">Medya Kütüphanesi</h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">Bir resim seçin</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => { setShowMediaLibrary(false); setSelectedImageField(null); }} className="flex-shrink-0">
                Kapat
              </Button>
            </div>
            <div className="p-3 sm:p-6">
              {loadingMedia ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : mediaFiles.length === 0 ? (
                <div className="text-center py-12">
                  <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Henüz medya dosyası yok</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Medya sayfasından dosya yükleyebilirsiniz
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {mediaFiles.map((media, index) => {
                    // API'den gelen field'ları normalize et
                    const fileName = media.name || media.original_name || 'Unknown';
                    const fileType = media.type || media.file_type || '';
                    const fileUrl = media.url || media.file_path || '';
                    
                    return (
                      <div
                        key={media.id || fileUrl || index}
                        onClick={() => selectMedia(fileUrl)}
                        className="relative group cursor-pointer border-2 border-gray-200 rounded-lg overflow-hidden hover:border-purple-500 transition-all"
                      >
                        <div className="aspect-square bg-gray-100 flex items-center justify-center">
                          {fileType.startsWith('image/') ? (
                            <img
                              src={fileUrl}
                              alt={fileName}
                              className="w-full h-full object-cover"
                            />
                          ) : fileType.startsWith('video/') ? (
                            <div className="flex flex-col items-center justify-center text-gray-500">
                              <Eye className="w-12 h-12 mb-2" />
                              <p className="text-xs">Video</p>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center text-gray-500">
                              <FileText className="w-12 h-12 mb-2" />
                              <p className="text-xs">File</p>
                            </div>
                          )}
                        </div>
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center">
                          <Button
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 transition-opacity bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            Seç
                          </Button>
                        </div>
                        <div className="p-2 bg-white">
                          <p className="text-xs text-gray-600 truncate" title={fileName}>{fileName}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPages;


